import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from "@angular/core";
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { CommonService, CrudComponent } from 'wire-frame';
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { TagModule } from 'primeng/tag';
import { AutoComplete, AutoCompleteModule } from "primeng/autocomplete";
import { DropdownModule } from 'primeng/dropdown';
import { ToolbarModule } from 'primeng/toolbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService } from 'primeng/api';
import { EditorModule } from 'primeng/editor';
import { CalendarModule } from 'primeng/calendar';

import { PrescriptionsService } from '../../../service/admin/prescriptions.service';
import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { UserService } from '../../../service/admin/user.service';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';
import { ChiefComplaintService } from '../../../service/admin/chief-complaint.service';
import { DiagnosisService } from '../../../service/admin/diagnosis.service';
import { SuggestedReportService } from '../../../service/admin/suggested-report.service';
import { MedicineService } from '../../../service/admin/medicine.service';
import { TemplateService } from '../../../service/admin/template.service';
import { AiService } from '../../../service/admin/ai.service';
import { ReportAnalysisService } from '../../../service/admin/report-analysis.service';
import { ConsultationDraftService } from '../../../service/admin/consultation-draft.service';
import { DoseService } from '../../../service/admin/dose.service';
import { HomeopathyRowParserService, ParsedMedicineHint, ParsedMedicineRow } from '../../../service/admin/homeopathy-row-parser.service';
import { MedicineResolverService } from '../../../service/admin/medicine-resolver.service';
import { EchoService } from '../../../services/echo.service';
import { DiseaseSearchResult, DiseaseSearchService } from '../../../service/admin/disease-search.service';

import { ApiService } from '../../../service/admin/api.service';
import { ToasterService } from '../../../service/common/toaster.service';
import {
  finalizePatientAgeInput,
  formatPatientAgeDisplay,
  getPatientAgeError,
  isValidPatientAge,
  normalizePatientAgeForStorage,
  normalizePatientAgeInput,
  PATIENT_AGE_HINT,
} from '../../../shared/patient-age.util';

import { ButtonModule } from 'primeng/button';
import { TabViewModule } from 'primeng/tabview';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { AccordionModule } from 'primeng/accordion';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SidebarModule } from 'primeng/sidebar';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { TooltipModule } from 'primeng/tooltip';

import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { HotkeyHelpComponent } from '../hotkey-help/hotkey-help.component';
import { ChiefComplaintAssistantComponent } from '../chief-complaint-assistant/chief-complaint-assistant.component';
import { RepertoryPanelComponent } from '../repertory/repertory-panel.component';
import { PhotoProgressCompareDialogComponent } from './photo-progress-compare-dialog.component';
import { RepertoryRemedy } from '../../../models/repertory.model';
import { AutoCapitalizeDirective } from '../../../directives/auto-capitalize.directive';
import { capitalizeClinicalTextFields, capitalizeLineStarts } from '../../../shared/clinical-text.util';
import { bindPatientSearchOverlayScroll } from '../../../shared/patient-search-overlay-scroll';
import {
  canOpenTextReport,
  formatTextReportCounter,
  getTextReportBody,
  getTextReportCharCount,
  getTextReportExcerpt,
  getTextReportLineCount,
  isTextReport as isTextReportFile,
  isTextReportOverLimit,
  TEXT_REPORT_MAX_LENGTH,
} from '../shared/text-report.util';

import jsPDF from 'jspdf';
import { finalize, map, switchMap } from "rxjs";
import { Medicine } from "./medicine-row.model";

import { StatusBadgePipe } from '../../../status-badge.pipe';

type SelectedIcdCode = {
  code: string;
  title: string;
  isPrimary: boolean;
  sourceComplaints: string[];
};

type RepertoryTargetRow = {
  mIndex: number;
  summary: string;
  isEmpty: boolean;
};

type RepertoryTargetGroup = {
  pIndex: number;
  rxLabel: string;
  rows: RepertoryTargetRow[];
};

export interface PrescriptionSearchResult {
  id: number;
  matched_title: string;
  patient_name: string;
  patient_number: string;
  date: string;
  medicine_count: number;
  raw_prescription: any[];
  /**
   * Unique key for PrimeNG AutoComplete selection when multiple results share
   * the same matched_title (e.g. many "Regular" prescriptions).
   */
  _rxKey?: string;
  /** Unique label so PrimeNG does not collapse duplicate matched_title values. */
  _rxLabel?: string;
}

type ReportAnalysisDiffDisplay = {
  stats: { changed: number; added: number; removed: number; unchanged: number };
  changed: any[];
  added: any[];
  removed: any[];
  unchanged: any[];
};

type ReportInterpretationDisplay = {
  likelyResponse: string;
  likelyResponseClass: string;
  confidencePercent: number;
  summaryText: string;
  supportingChanges: string[];
  caveats: string[];
  diff: ReportAnalysisDiffDisplay | null;
  changedPreview: any[];
};

type BaselineReportOption = {
  value: number;
  label: string;
  name: string;
  date: string;
  dateSort: number;
  kindLabel: string;
  kindIcon: string;
  kindClass: string;
  status: string;
  statusLabel: string;
  reportTypeLabel: string;
  insight?: string;
  isAnalyzed: boolean;
};

@Component({
  selector: 'app-patients',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    TagModule,
    DropdownModule,
    ToolbarModule,
    ConfirmDialogModule,
    EditorModule,
    ButtonModule,
    TabViewModule,
    AccordionModule,
    ScrollPanelModule,
    DialogModule,
    SkeletonModule,
    InputTextModule,
    FloatLabelModule,
    SidebarModule,
    AutoCompleteModule,
    ConfirmPopupModule,
    TooltipModule,
    CalendarModule,
    CheckboxModule,
    RouterOutlet,
    HotkeyHelpComponent,
    ChiefComplaintAssistantComponent,
    RepertoryPanelComponent,
    PhotoProgressCompareDialogComponent,
    StatusBadgePipe,
    AutoCapitalizeDirective
  ],
  providers: [
    ConfirmationService,
    CommonService,
    PrescriptionsService,
    AppointmentService,
    ClinicService,
    UserService,
    PatientRegistrationService,
    ChiefComplaintService,
    SuggestedReportService,
    MedicineService,
    TemplateService,
    AiService,
    ApiService,
    DoseService,
    HomeopathyRowParserService,
    MedicineResolverService,
    ReportAnalysisService,
    ConsultationDraftService,
  ],
  templateUrl: './prescriptions.component.html',
  styleUrl: './prescriptions.component.scss'
})
export class PrescriptionsComponent implements OnInit, OnDestroy {
  @ViewChild('ccTextarea') ccTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ccAssistant') ccAssistant?: ChiefComplaintAssistantComponent;
  @ViewChild('historicalRxAc') historicalRxAc?: AutoComplete;

  private readonly distanceViewStorageKey = 'prescriptions_distance_view';
  public distanceView: boolean = false;
  private draftAutosaveTimer: ReturnType<typeof setInterval> | null = null;
  private metadataRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private validationHighlightTimer: ReturnType<typeof setTimeout> | null = null;
  private hotkeySidebarEl: Element | null = null;
  private openSearchListener?: () => void;
  private openQueueListener?: () => void;
  private openMedicineQueueListener?: () => void;
  private isDraftAutosavePausedUntilChange = false;
  private isDraftAutosaveSuspended = false; // Prevents race condition overwrites during patient load
  public templateApplyMode: 'replace' | 'append' = 'replace';
  public pendingTemplateConfirm: any = null;
  public pendingClearRxNumber = 1;
  public currentAppointmentStatus: string = '';
  public currentAppointmentDate: string = '';
  public recalledFromPharmacy: boolean = false;
  public checked: boolean = false;
  activeIndex: number = 0;
  sidebarTab: 'queue' | 'search' = 'queue';
  readonly reportsTabIndex = 4;
  historyTabIndex: number = 5;
  public isDirty: boolean = false;
  displayHistory: boolean = false;

  isReportDialogVisible: boolean = false;

  isPatientVisible: boolean = true;

  clinicId: number = 0; // Default clinic ID
  doctorId: number = 0; // Default clinic ID
  // selectedDate: string = new Date().toISOString().slice(0, 10);
  selectedDate = new Date(Date.now() + 19800000).toISOString().slice(0, 10);

  public patientsList: any[] = [];
  public prescriptionsList: any[] = [];
  selectedPatient: any;
  public clinicsList = [];
  public cardList = [];
  public doctorsList = [];
  public appointmentsList = [];
  public editRecord = null;
  public queue: any[] = [];
  public queueTypeFilter: 'all' | 'new' | 'follow_up' = 'all';
  public queueModeFilter: 'all' | 'OPD' | 'COURIER' | 'NRI' | 'MC' = 'OPD';
  public isQueueRefreshing: boolean = false;
  uploadsByDate: { date: Date, files: any[] }[] = [];
  reportsMeta = { total: 0, limit: 40, offset: 0, hasMore: false };
  reportsLoadingMore = false;
  private baselineReportPool: any[] = [];
  selectedIndex: number | string = 'today';
  filteredRecords: any[] = [];
  accordionIndex: number | null = null;
  historyPageSize: number = 8;
  historyCurrentPage: number = 0;
  historyTotalRecords: number = 0;
  historyHasMore: boolean = false;
  historyLoading: boolean = false;
  historyLoadingMore: boolean = false;
  historyError: string = '';
  historyInitializedForPatientId: number = 0;
  selectedHistoryIndex: number = 0;
  private activeHistoryRequestToken: number = 0;

  public allDoses: any[] = [];

  public complaintTags: any[] = [];
  public diagnosisTags: any[] = [];
  public diagnosisInput: string = '';
  public draggingRxIndex: number | null = null;
  public draggingMedRxIndex: number | null = null;
  public draggingMedIndex: number | null = null;
  public dragOverRxIndex: number | null = null;
  public dragOverMedRxIndex: number | null = null;
  public dragOverMedIndex: number | null = null;
  public allDiagnoses: any[] = [];
  public filteredDiagnoses: any[] = [];
  public diagnosisSuggestionIndex: number = -1;
  // public complaintTags: Array<{ id: number | null; chief_complaint: string }> = [];
  public complaintInput: string = '';
  public allComplaints: string[] = [];
  // public allComplaints: Array<{ id: number | null; chief_complaint: string }> = [];
  public filteredComplaints: string[] = [];
  public pendingFiles: File[] = [];
  public docDropzoneActive = false;
  private docDropzoneDragDepth = 0;
  public reportTextTitle: string = '';
  public reportTextContent: string = '';
  public isSavingTextReport: boolean = false;
  public isUploadingReports = false;
  public pendingReportType = 'lab';
  reportsUploadPanelOpen = true;
  reportsDocFilter: 'all' | 'image' | 'pdf' | 'text' | 'analyzed' | 'pending' = 'all';
  reportsDocSearch = '';
  filteredUploadsByDate: { date: Date; files: any[] }[] = [];
  filteredDocumentsCount = 0;
  patientReportsLoadedCount = 0;
  imageCompareMode = false;
  imageCompareDialogVisible = false;
  imageCompareTarget: 'before' | 'after' = 'before';
  imageCompareBefore: any[] = [];
  imageCompareAfter: any[] = [];
  photoProgressHistory: any[] = [];
  photoProgressHistoryLoading = false;
  /** Collapsed by default so documents stay in view. */
  photoProgressHistoryOpen = false;
  /** Analysis passed into the dialog when restoring a past comparison. */
  photoProgressInitialAnalysis: any = null;
  readonly imageCompareMaxPerSide = 4;
  readonly reportsDocFilters: Array<{
    value: 'all' | 'image' | 'pdf' | 'text' | 'analyzed' | 'pending';
    label: string;
    icon: string;
  }> = [
    { value: 'all', label: 'All', icon: 'pi-inbox' },
    { value: 'image', label: 'Images', icon: 'pi-image' },
    { value: 'pdf', label: 'PDF', icon: 'pi-file-pdf' },
    { value: 'text', label: 'Text', icon: 'pi-align-left' },
    { value: 'analyzed', label: 'Analyzed', icon: 'pi-check-circle' },
    { value: 'pending', label: 'Pending AI', icon: 'pi-clock' },
  ];

  get visibleReportsDocFilters() {
    if (this.aiEnabled) {
      return this.reportsDocFilters;
    }
    return this.reportsDocFilters.filter(
      (filter) => filter.value !== 'analyzed' && filter.value !== 'pending'
    );
  }
  readonly reportUploadMaxMb = PrescriptionsService.REPORT_UPLOAD_MAX_MB;
  public reportTypeOptions = [
    {
      label: 'Blood & Laboratory',
      items: [
        { label: 'General Lab Report', value: 'lab' },
        { label: 'CBC (Complete Blood Count)', value: 'cbc' },
        { label: 'Lipid Profile', value: 'lipid' },
        { label: 'Liver Function Test (LFT)', value: 'lft' },
        { label: 'Kidney Function Test (KFT/RFT)', value: 'kft' },
        { label: 'Thyroid Profile (TSH/T3/T4)', value: 'thyroid' },
        { label: 'HbA1c / Diabetes Panel', value: 'hba1c' },
        { label: 'Blood Sugar (Fasting/PP)', value: 'blood_sugar' },
        { label: 'Urine Routine & Microscopy', value: 'urine' },
        { label: 'Vitamin D', value: 'vitamin_d' },
        { label: 'Vitamin B12 / Folate', value: 'vitamin_b12' },
        { label: 'Iron Studies / Ferritin', value: 'iron' },
        { label: 'ESR / CRP', value: 'esr_crp' },
        { label: 'Electrolytes (Na/K/Cl)', value: 'electrolytes' },
        { label: 'PSA', value: 'psa' },
        { label: 'Hormone Panel', value: 'hormone' },
        { label: 'Allergy / IgE Panel', value: 'allergy' },
        { label: 'Coagulation (PT/INR)', value: 'coagulation' },
        { label: 'Stool Routine', value: 'stool' },
        { label: 'Semen Analysis', value: 'semen' },
        { label: 'Culture & Sensitivity', value: 'culture' },
        { label: 'Histopathology / Biopsy', value: 'histopathology' },
        { label: 'Pap Smear / Cervical Cytology', value: 'pap_smear' },
      ],
    },
    {
      label: 'Imaging & Cardiac',
      items: [
        { label: 'X-Ray', value: 'xray' },
        { label: 'Ultrasound (USG)', value: 'usg' },
        { label: 'CT Scan', value: 'ct' },
        { label: 'MRI', value: 'mri' },
        { label: 'Mammography', value: 'mammography' },
        { label: 'ECG', value: 'ecg' },
        { label: '2D Echo / Echocardiography', value: 'echo' },
        { label: 'PFT / Spirometry', value: 'pft' },
        { label: 'Bone Density (DEXA)', value: 'dexa' },
        { label: 'Endoscopy / Colonoscopy', value: 'endoscopy' },
      ],
    },
    {
      label: 'Clinical Photos',
      items: [
        { label: 'Skin / Dermatology Photo', value: 'skin_photo' },
        { label: 'Clinical Progress Photo', value: 'clinical_photo' },
      ],
    },
    {
      label: 'Other Documents',
      items: [
        { label: 'Discharge Summary', value: 'discharge_summary' },
        { label: 'Referral Letter', value: 'referral' },
        { label: 'Other', value: 'other' },
      ],
    },
  ];
  public reportAnalysisDialogVisible = false;
  public textReportViewerVisible = false;
  public textReportViewerLoading = false;
  public viewingTextReport: any = null;
  readonly textReportMaxLength = TEXT_REPORT_MAX_LENGTH;
  public reportAnalysisTabIndex = 0;
  public selectedReportFile: any = null;
  public reportAnalyses: any[] = [];
  public reportExtractionAnalysis: any = null;
  public reportComparisonAnalysis: any = null;
  public reportOutcomeAnalysis: any = null;
  public reportAnalysisLoading = false;
  public isLoading = false;
  public reportCompareLoading = false;
  public reportOutcomeLoading = false;
  public reportReviewSaving = false;
  public baselineReportId: number | null = null;
  public baselineSelectionMode: 'auto' | 'manual' = 'auto';
  public baselineReportCandidates: BaselineReportOption[] = [];
  public filteredBaselineCandidates: BaselineReportOption[] = [];
  public baselineSearchQuery = '';
  public showUnchangedDiffMarkers = false;
  public treatmentContextSummary = '';
  public outcomePrerequisites: Array<{ ok: boolean; label: string }> = [];
  public comparisonDisplay: ReportInterpretationDisplay | null = null;
  public outcomeDisplay: ReportInterpretationDisplay | null = null;
  public analysisTabRenderReady = true;
  public doctorReviewNotes = '';
  public analyzingReportIds = new Set<number>();
  private reportAnalysisPollTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private reportAnalysisResultNotifiedIds = new Set<number>();

  public reportTags: any[] = [];
  public reportInput: string = '';
  public allReports: string[] = [];
  public filteredReports: string[] = [];
  public draftSaveState: 'idle' | 'saving' | 'saved' | 'error' | 'cleared' = 'idle';
  public draftSaveLabel: string = 'Draft autosave is active.';
  public canClearDraftFlag = false;

  public medicineTags: any[] = [];
  public medicineInput: string = '';
  public allMedicines: string[] = [];
  public filteredMedicines: string[] = [];

  pdfSrc: SafeResourceUrl | null = null;
  public aiPlan: string = '';
  /** Backend AI kill switch; when false, hide AI UI and skip AI API calls. */
  public aiEnabled = true;

  public showSchedulePicker: boolean = false;
  public scheduledDate: Date | null = null;
  public minDate: Date = new Date();

  private medicineByCode = new Map<string, Medicine>();
  private medicinesByName = new Map<string, Medicine[]>();
  public selectedIcdCodes: SelectedIcdCode[] = [];
  private icdSuggestionCacheKey: string = '';
  private icdSuggestionCache: Array<{ code: string; title: string; score: number; sourceComplaints: string[] }> = [];
  private readonly staticIcdCatalog: Array<{ code: string; title: string; keywords: string[] }> = [
    { code: 'J00', title: 'Acute nasopharyngitis (common cold)', keywords: ['cold', 'runny nose', 'sneezing', 'coryza'] },
    { code: 'J02.9', title: 'Acute pharyngitis, unspecified', keywords: ['sore throat', 'throat pain', 'pharyngitis'] },
    { code: 'R05', title: 'Cough', keywords: ['cough', 'dry cough', 'productive cough'] },
    { code: 'J06.9', title: 'Acute upper respiratory infection, unspecified', keywords: ['uri', 'upper respiratory infection', 'flu', 'viral fever'] },
    { code: 'R50.9', title: 'Fever, unspecified', keywords: ['fever', 'pyrexia', 'high temperature'] },
    { code: 'G43.9', title: 'Migraine, unspecified', keywords: ['migraine', 'one sided headache', 'aura'] },
    { code: 'R51', title: 'Headache', keywords: ['headache', 'head pain'] },
    { code: 'K29.7', title: 'Gastritis, unspecified', keywords: ['gastritis', 'acidity', 'burning in stomach'] },
    { code: 'K21.9', title: 'Gastro-esophageal reflux disease without esophagitis', keywords: ['gerd', 'acid reflux', 'heartburn'] },
    { code: 'R10.9', title: 'Abdominal pain, unspecified', keywords: ['abdominal pain', 'stomach pain', 'colic'] },
    { code: 'K59.0', title: 'Constipation', keywords: ['constipation', 'hard stool'] },
    { code: 'R19.7', title: 'Diarrhoea, unspecified', keywords: ['diarrhea', 'loose motions', 'loose stool'] },
    { code: 'E03.9', title: 'Hypothyroidism, unspecified', keywords: ['hypothyroid', 'hypothyroidism', 'thyroid low'] },
    { code: 'E11.9', title: 'Type 2 diabetes mellitus without complications', keywords: ['diabetes', 'high sugar', 'dm'] },
    { code: 'I10', title: 'Essential (primary) hypertension', keywords: ['hypertension', 'high bp', 'high blood pressure'] },
    { code: 'M54.5', title: 'Low back pain', keywords: ['back pain', 'low back pain', 'lumbar pain'] },
    { code: 'M25.5', title: 'Pain in joint', keywords: ['joint pain', 'arthralgia', 'knee pain'] },
    { code: 'L30.9', title: 'Dermatitis, unspecified', keywords: ['dermatitis', 'skin rash', 'eczema'] },
    { code: 'L29.9', title: 'Pruritus, unspecified', keywords: ['itching', 'pruritus'] },
    { code: 'J30.9', title: 'Allergic rhinitis, unspecified', keywords: ['allergy', 'allergic rhinitis', 'allergic cold'] }
  ];


  public patient: any = {
    id: 0,  // number: Unique ID
    first_name: '',
    middle_name: '',
    last_name: '',
    primary_contact: '',  // string: Phone number
    secondary_contact: '',  // string: Phone number
    age: 0,
    blood_group: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    marital_status: '',
    is_free: false,     // number: Age in years  // boolean: true = free visit,     // string: Courier-related info
    scanned_card: null as File | null, // File upload (can be null or File)

    visit_date: new Date(),

    // Date of first visit
    first_visit: '', // Date of first visit
    patient_number: '',
    is_courier: 'OPD',
    referred_by: '',
  };

  public prescription: any = {
    appointment_id: 0,
    allergies: '',
    current_medications: '',
    personal_family_history: '',
    surgical_history: '',
    blood_pressure: '',
    pulse_rate: '',
    temperature: '',
    weight: '',
    height: '',
    fasting_blood_sugar: '',
    blood_oxygen: '',
    runny_nose: '',
    cough: '',
    chief_complaints: '',
    suggested_reports: '',

    prescriptions: [
      {
        id: 1,
        title: '',
        duration: '',
        medicines: [
          {
            tags: [
              // {
              //   id: null,       // optional — if medicine exists in DB
              //   name: '',       // medicine name
              //   code: '',       // unique code, e.g., “A0001”
              //   potency: ''     // optional field
              // }
            ],
            dose: '',
            drams: '',
            ballers: '',
            medicineInput: '',         // for the input field
            filteredMedicines: [],     // for the suggestions dropdown
            filteredDoses: [],         // for the dose suggestions dropdown
            medicineTags: []           // alias for tags, for backward compatibility
          }
        ]
      }
    ],

    diet: '',
    exercise: '',

    personal_dues: 0,
    visit_fees: 0,
    received_cash: 0,
    received_online: 0,

    special_instructions: '',
    is_courier: 'OPD',
    // collection_date: '',

    notes: '',
  }

  public patientSelectedFromSearch = false;
  public patientSelectionConfirmed = false;
  private activeSearchSelectionToken = 0;
  public searchVisitType: 'auto' | 'new' | 'follow_up' = 'auto';
  public searchVisitTypeOptions: { label: string; value: 'auto' | 'new' | 'follow_up' }[] = [
    { label: 'Auto', value: 'auto' },
    { label: 'New', value: 'new' },
    { label: 'Follow Up', value: 'follow_up' }
  ];
  private unbindPatientSearchOverlayScroll: (() => void) | null = null;
  public prescriptionModeOptions = [
    { label: 'OPD', value: 'OPD' },
    { label: 'Courier', value: 'COURIER' },
    { label: 'NRI', value: 'NRI' }
  ];

  public closeFormEvent: boolean = false;
  public openDeletePopup: any = null;
  public activeMenu: string = 'personal';

  public complaintsDropdownOpen: boolean = false;
  public reportsDropdownOpen: boolean = false;
  public tags: string[] = [];
  public allComplaintsSuggestions: { label: string; value: string }[] = [];
  public searchTerm: string = '';

  public patientFromSearch = false;
  reportHtml: string = "";

  private apiUrl: string | undefined;

  queueIndex = 0; // currently selected index
  nextPatientIndex = -1;

  sidebarOpen = false;
  quickText = '';
  doctorNotesDialogVisible = false;
  medicineSearchDialogVisible = false;
  repertoryDialogVisible = false;
  repertoryTargetPIndex: number | null = null;
  repertoryTargetMIndex: number | null = null;
  repertoryInitialSymptom = '';
  repertoryTargetLabel = '';
  repertorySessionCount = 0;
  isMedicineSidebarVisible = false;

  // Historical Rx Search Variables
  selectedHistoricalRx: any;
  historicalRxResults: PrescriptionSearchResult[] = [];
  historicalRxSearchLoading = false;
  displayHistoricalRxPreview: boolean = false;
  previewRx: PrescriptionSearchResult | null = null;
  selectedPreviewMedicines: boolean[] = [];

  // Add Patient dialog
  showAddPatientDialog = false;
  savingNewPatient = false;
  checkingDuplicate = false;
  duplicatePatient: any = null;
  confirmDuplicateOverride = false;
  bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(b => ({ label: b, value: b }));
  genderOptions = [
    { label: 'Male', value: 'Male', icon: 'pi-mars' },
    { label: 'Female', value: 'Female', icon: 'pi-venus' },
    { label: 'Other', value: 'Other', icon: 'pi-user' },
  ];
  newPatient: any = {
    first_name: '', middle_name: '', last_name: '',
    primary_contact: '', gender: '', age: null,
    blood_group: '', city: '', address: '',
    clinic_id: 1
  };
  readonly patientAgeHint = PATIENT_AGE_HINT;
  readonly isValidPatientAge = isValidPatientAge;
  readonly formatPatientAgeDisplay = formatPatientAgeDisplay;

  hasPreconsultValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== '';
  }
  newPatientAgeError: string | null = null;
  medicineSearchKeyword = '';
  medicineSearchResults: DiseaseSearchResult[] = [];
  medicineSearchLoading = false;
  medicineSearchError = '';
  medicineSearchPage = 1;
  readonly medicineSearchPageSize = 20;
  medicineSearchTotal: number | null = null;
  medicineSearchHasMore = false;
  doctorNotesTab: 'notes' | 'instructions' = 'notes';
  doctorNotesHistory: Array<{ date: string; notes: string }> = [];
  latestDoctorNote = '';
  latestDoctorNoteDate = '';
  specialInstructionsHistory: Array<{ date: string; notes: string }> = [];
  latestSpecialInstruction = '';
  latestSpecialInstructionDate = '';

  selectedReport: any; // Clear this after adding
  complaintSuggestionIndex = -1;
  reportSuggestionIndex = -1;

  // Language options for Pdf translation
  selectedRegionalLanguage: any = null;
  languageOptions = [
    { label: 'English', value: 'english' },
    { label: 'Hindi', value: 'hindi' },
    { label: 'Marathi', value: 'marathi' },
    { label: 'Gujarati', value: 'gujarati' },
    { label: 'Tamil', value: 'tamil' },
    { label: 'Telugu', value: 'telugu' },
    { label: 'Kannada', value: 'kannada' },
    { label: 'Malayalam', value: 'malayalam' },
    { label: 'Bengali', value: 'bengali' },
    { label: 'Punjabi', value: 'punjabi' },
    { label: 'Urdu', value: 'urdu' },
    { label: 'Odia', value: 'odia' }
  ];

  private loggedInUser = JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');

  constructor(
    public common: CommonService,
    private toast: ToasterService,
    private confirmationService: ConfirmationService,
    private prescriptionsService: PrescriptionsService,
    private appointmentService: AppointmentService,
    private clinicService: ClinicService,
    private userService: UserService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private patientRegistrationService: PatientRegistrationService,
    private sanitizer: DomSanitizer,
    private chiefComplaintService: ChiefComplaintService,
    private diagnosisService: DiagnosisService,
    private suggestedReportService: SuggestedReportService,
    private medicineService: MedicineService,
    private templateService: TemplateService,
    private aiService: AiService,
    private reportAnalysisService: ReportAnalysisService,
    private apiService: ApiService,
    private doseService: DoseService,
    private parser: HomeopathyRowParserService,
    private resolver: MedicineResolverService,
    private diseaseSearchService: DiseaseSearchService,
    private router: Router,
    private statusBadgePipe: StatusBadgePipe,
    private echoService: EchoService,
    private consultationDraftService: ConsultationDraftService
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
    this.common.breadCrum = {
      title: 'Visa Card Master',
      icon: 'user',
      list: ['Visa Card Master'],
    };
    this.common.setCrudTableHeaders([

      {
        title: 'Patient Name',
        val: 'patient_name',
        id: 'id',
        showColumn: true,
        filterColmn: true,
        order: 'asc',
        searchColumn: true,
        type: 'string',
        globalSearch: true,
      },
    ]);
    this.common.setCrudActions([
      {
        title: '',
        icon: 'pi-pencil',
        classArr: 'me-2',
        tooltip: 'Update Patient',
      },
      { title: '', icon: 'pi-trash', tooltip: 'Delete Patient' },
    ]);
  }


  // logo = '/logo.png'; // <-- for local
  logo = '/clinic/logo.png'; // <-- for server

  ngOnInit() {
    // this.echoService.echo.channel('pharmacy-queue')
    //   .listen('PrescriptionRecalledFromPharmacy', (event: any) => {
    //     this.queue = this.queue.filter((item: any) => item.id !== event.appointment_id);

    //     this.toast.add({
    //       severity: 'error',
    //       summary: 'Prescription Recalled',
    //       detail: 'Dr. Sancheti has recalled a prescription. Halting process.',
    //       life: 6000 
    //     });
    //     this.cdr.detectChanges();
    //   });

    this.loadDistanceViewPreference();
    this.loadTemplateBrowserPrefs();
    this.startDraftAutosave();
    // this.getPatients();
    this.loadQueue();
    this.loadLogo();
    this.aiService.loadStatus().subscribe((enabled) => {
      this.aiEnabled = enabled;
      if (!enabled && (this.reportsDocFilter === 'analyzed' || this.reportsDocFilter === 'pending')) {
        this.setReportsDocFilter('all');
      }
      if (!enabled && this.reportAnalysisDialogVisible) {
        this.reportAnalysisDialogVisible = false;
      }
    });

    this.getChiefComplaints();
    this.getDiagnoses();
    this.getSuggestedReports();
    this.getMedicines();
    this.getTemplates();
    this.getDoses();

    this.hotkeySidebarEl = document.querySelector('[data-hotkey="patient-search"]');
    this.openSearchListener = () => this.openPatientSearch();
    this.openQueueListener = () => this.openQueueSidebar();
    this.openMedicineQueueListener = () => this.toggleSidebar();
    this.hotkeySidebarEl?.addEventListener('open-search', this.openSearchListener);
    this.hotkeySidebarEl?.addEventListener('open-queue', this.openQueueListener);
    this.hotkeySidebarEl?.addEventListener('open-medicine-queue', this.openMedicineQueueListener);

    const badge = this.statusBadgePipe.transform('doctor');

    this.metadataRefreshTimer = setInterval(() => {
      if (this.common.getLoggedInUser().type === 'doctor') {
        // this.loadQueue();
        this.getChiefComplaints();
        this.getSuggestedReports();
      }
      // this.loadMedicineQueue();
    }, 30000);
  }

  ngOnDestroy() {
    if (this.draftAutosaveTimer) {
      clearInterval(this.draftAutosaveTimer);
      this.draftAutosaveTimer = null;
    }
    if (this.metadataRefreshTimer) {
      clearInterval(this.metadataRefreshTimer);
      this.metadataRefreshTimer = null;
    }
    if (this.validationHighlightTimer) {
      clearTimeout(this.validationHighlightTimer);
      this.validationHighlightTimer = null;
    }
    if (this.hotkeySidebarEl && this.openSearchListener) {
      this.hotkeySidebarEl.removeEventListener('open-search', this.openSearchListener);
    }
    if (this.hotkeySidebarEl && this.openQueueListener) {
      this.hotkeySidebarEl.removeEventListener('open-queue', this.openQueueListener);
    }
    if (this.hotkeySidebarEl && this.openMedicineQueueListener) {
      this.hotkeySidebarEl.removeEventListener('open-medicine-queue', this.openMedicineQueueListener);
    }
    this.openSearchListener = undefined;
    this.openQueueListener = undefined;
    this.openMedicineQueueListener = undefined;
    this.hotkeySidebarEl = null;
    this.onPatientSearchOverlayHide();
  }

  private startDraftAutosave() {
    if (this.draftAutosaveTimer) clearInterval(this.draftAutosaveTimer);
    let lastDraftStateHash = '';

    this.draftAutosaveTimer = setInterval(() => {
      if (!this.prescription || this.isDraftAutosaveSuspended) return;

      // Deep-hash all draftable state using safeStringify to prevent circular reference crashes
      const stateToHash = {
        p: this.prescription,
        d: this.diagnosisTags,
        r: this.reportTags,
        i: this.selectedIcdCodes,
        s: this.scheduledDate,
        sp: this.showSchedulePicker
      };
      const currentHash = this.safeStringify(stateToHash);

      if (lastDraftStateHash !== currentHash) {
        lastDraftStateHash = currentHash;

        // Auto-unpause draft saving if a genuine change is detected
        this.isDraftAutosavePausedUntilChange = false;

        // Save draft immediately after the change is detected
        this.saveDraftSnapshot();
      }
    }, 2000); // Check frequently to mimic "after every change"
  }

  private loadDistanceViewPreference() {
    try {
      this.distanceView = localStorage.getItem(this.distanceViewStorageKey) === '1';
    } catch {
      this.distanceView = false;
    }
  }

  toggleDistanceView() {
    this.distanceView = !this.distanceView;
    try {
      localStorage.setItem(this.distanceViewStorageKey, this.distanceView ? '1' : '0');
    } catch {
      // no-op if storage is unavailable
    }
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    }
  }

  normalizePrescriptionMode(mode: any): 'OPD' | 'COURIER' | 'NRI' {
    if (mode === 'NRI') {
      return 'NRI';
    }

    if (
      mode === true ||
      mode === 'true' ||
      mode === 1 ||
      mode === '1' ||
      mode === 'Courier' ||
      mode === 'COURIER'
    ) {
      return 'COURIER';
    }

    return 'OPD';
  }

  getModeInitial(mode: any): 'O' | 'C' | 'N' {
    const normalized = this.normalizePrescriptionMode(mode);

    if (normalized === 'COURIER') return 'C';
    if (normalized === 'NRI') return 'N';
    return 'O';
  }

  getModeTitle(mode: any): string {
    const normalized = this.normalizePrescriptionMode(mode);

    if (normalized === 'COURIER') return 'Courier';
    if (normalized === 'NRI') return 'NRI';
    return 'OPD';
  }

  getModeBadgeClass(mode: any): string {
    const normalized = this.normalizePrescriptionMode(mode);

    if (normalized === 'COURIER') return 'mode-courier';
    if (normalized === 'NRI') return 'mode-nri';
    return 'mode-opd';
  }

  isMedicineCollectionItem(item: any): boolean {
    return item?.collect_later === true || item?.collect_later === 1 || item?.collect_later === '1';
  }

  private resolvePrescriptionMode(...modes: any[]): 'OPD' | 'COURIER' | 'NRI' {
    for (const mode of modes) {
      if (mode === null || mode === undefined || mode === '') continue;
      return this.normalizePrescriptionMode(mode);
    }

    return 'OPD';
  }

  private getLatestModeFromRecords(records: any[]): any {
    if (!Array.isArray(records) || records.length === 0) {
      return null;
    }

    const latest = [...records].sort((a: any, b: any) => {
      const timeA = new Date(a?.created_at || 0).getTime();
      const timeB = new Date(b?.created_at || 0).getTime();

      if (timeA !== timeB) return timeB - timeA;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    })[0];

    return latest?.is_courier ?? null;
  }

  private sanitizeNamePart(value: any): string {
    if (value === null || value === undefined) return '';

    const part = String(value).trim();
    if (!part || part.toLowerCase() === 'null' || part.toLowerCase() === 'undefined') {
      return '';
    }

    return part;
  }

  buildPatientDisplayName(patient: any): string {
    const name = [
      this.sanitizeNamePart(patient?.first_name),
      this.sanitizeNamePart(patient?.middle_name),
      this.sanitizeNamePart(patient?.last_name)
    ].filter(Boolean).join(' ');

    if (name) return name;

    return this.sanitizeNamePart(patient?.full_name) || this.sanitizeNamePart(patient?.name);
  }

  getPatientSearchInitials(patient: any): string {
    const first = this.sanitizeNamePart(patient?.first_name);
    const last = this.sanitizeNamePart(patient?.last_name);
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    return initials || 'P';
  }

  onPatientSearchOverlayShow() {
    this.onPatientSearchOverlayHide();
    this.unbindPatientSearchOverlayScroll = bindPatientSearchOverlayScroll();
  }

  onPatientSearchOverlayHide() {
    this.unbindPatientSearchOverlayScroll?.();
    this.unbindPatientSearchOverlayScroll = null;
  }

  getPrescriptions() {
    this.prescriptionsService.getPrescriptions().subscribe((res: any) => {
      this.prescriptionsList = res.data;
    })
  }

  closeForm() {
    this.clearForm();
    this.closeFormEvent = true;
    setTimeout(() => {
      this.closeFormEvent = false;
    }, 1);
  }

  clearForm() {
    this.patientSelectionConfirmed = false;
    this.searchVisitType = 'auto';
    this.patient = {
      id: 0,  // number: Unique ID
      first_name: '',
      middle_name: '',
      last_name: '',
      primary_contact: '',  // string: Phone number
      secondary_contact: '',  // string: Phone number
      age: 0,
      blood_group: '',
      gender: '',
      address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      marital_status: '',
      is_free: false,     // number: Age in years  // boolean: true = free visit,     // string: Courier-related info
      scanned_card: null as File | null, // File upload (can be null or File)

      visit_date: new Date(),

      // Date of first visit
      first_visit: '', // Date of first visit
      is_courier: 'OPD',
    };

    this.prescription = {
      appointment_id: 0,
      allergies: '',
      current_medications: '',
      personal_family_history: '',
      surgical_history: '',
      blood_pressure: '',
      pulse_rate: '',
      temperature: '',
      weight: '',
      height: '',
      fasting_blood_sugar: '',
      blood_oxygen: '',
      runny_nose: '',
      cough: '',
      chief_complaints: '',
      suggested_reports: '',

      prescriptions: [
        {
          id: 1,
          title: '',
          duration: '',
          medicines: [
            {
              tags: [
                // {
                //   id: null,       // optional — if medicine exists in DB
                //   name: '',       // medicine name
                //   code: '',       // unique code, e.g., “A0001”
                //   potency: ''     // optional field
                // }
              ],
              dose: '',
              drams: '',
              ballers: '',
              medicineInput: '',         // for the input field
              filteredMedicines: [],     // for the suggestions dropdown
              filteredDoses: [],         // for the dose suggestions dropdown
              medicineTags: []           // alias for tags, for backward compatibility
            }
          ]
        }
      ],

      complaintTags: [],
      diagnosisTags: [],
      reportTags: [],

      personal_dues: 0,
      visit_fees: 0,
      received_cash: 0,
      received_online: 0,
      card_dues: 0,

      notes: '',

      special_instructions: '',
      is_courier: 'OPD',
    }

    // ✅ Reset history + reports
    this.resetHistoryStateForPatient(0);
    this.uploadsByDate = [];
    this.reportsMeta = { total: 0, limit: 40, offset: 0, hasMore: false };
    this.baselineReportPool = [];
    this.syncReportsViewModel();

    this.reportTags = [];
    this.complaintTags = [];
    this.diagnosisTags = [];
    this.selectedIcdCodes = [];

    this.scheduledDate = null;
    this.showSchedulePicker = false;
    this.doctorNotesDialogVisible = false;
    this.doctorNotesTab = 'notes';
    this.doctorNotesHistory = [];
    this.latestDoctorNote = '';
    this.latestDoctorNoteDate = '';
    this.specialInstructionsHistory = [];
    this.latestSpecialInstruction = '';
    this.latestSpecialInstructionDate = '';
    this.nextPatientIndex = -1;
    this.currentAppointmentStatus = '';
    this.currentAppointmentDate = '';
    this.recalledFromPharmacy = false;
  }

  // processPatients() {
  //   if (this.patient?.id) {
  //     this.updatePatients();
  //   } else {
  //     this.addPrescriptions();
  //   }
  // }

  //! old appointment and prescription addition Code
  // addPrescriptions() {
  //   if (this.patientSelectedFromSearch) {
  //     this.saveAppointmentAndPrescription();
  //     this.patientSelectedFromSearch = false; // reset flag
  //   } else {
  //     this.savePrescriptionOnly();
  //   }
  // }

  //! Combined Appointment and Prescription Code for all the three buttons in the footer
  addPrescriptions(mode: 'pharmacy' | 'case' | 'schedule' = 'pharmacy', event?: Event) {
    // Determine the visit_fees to save
    let resolvedVisitFees = 0;
    const hasSavedPrescriptionFees = Number(this.prescription.visit_fees) > 0;

    if (hasSavedPrescriptionFees) {
      resolvedVisitFees = Number(this.prescription.visit_fees);
    } else {
      resolvedVisitFees = Number(this.patient.visit_fees || 0);
    }

    // Assign `visit_fees` to `this.prescription` before proceeding
    this.prescription.visit_fees = resolvedVisitFees;

    // Check action logic 
    if (!this.ensureActionCanProceed(mode)) return;

    const proceed = () => {
      if (this.patientSelectedFromSearch) {
        this.handleAppointmentFlow(mode);
        this.patientSelectedFromSearch = false;
      } else {
        this.handlePrescriptionOnlyFlow(mode);
      }
    };

    if (mode === 'case' && this.isPrescriptionEmpty()) {
      this.confirmationService.confirm({
        target: event?.target as EventTarget,
        key: 'saveCaseConfirmPopup',
        header: 'Confirm Save Case',
        message: 'The prescription is empty. Are you sure you want to save the case without a prescription?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          proceed();
        }
      });
    } else {
      proceed();
    }
  }

  /* ---------------------------------------------------
    APPOINTMENT + PRESCRIPTION FLOW
  --------------------------------------------------- */
  private handleAppointmentFlow(mode: 'pharmacy' | 'case' | 'schedule') {
    const appointmentPayload = this.buildAppointmentPayload(mode);

    this.appointmentService.addAppointment(appointmentPayload).subscribe({
      next: (res: any) => {
        if (!res.success) {
          this.toast.add({ severity: 'error', summary: 'Error', detail: this.getAppointmentErrorMessage(mode) });
          return;
        }

        const appointmentId = res.data.id;
        const prescriptionPayload = this.buildPrescriptionPayload(mode, appointmentId);

        this.savePrescription(prescriptionPayload, mode, false);
      },

      error: () => {
        this.toast.add({ severity: 'error', summary: 'Error', detail: this.getAppointmentErrorMessage(mode) });
      }
    });
  }

  /* ---------------------------------------------------
    PRESCRIPTION ONLY FLOW
  --------------------------------------------------- */
  // private handlePrescriptionOnlyFlow(mode: 'pharmacy' | 'case' | 'schedule') {
  //   const appointmentId = this.prescription.appointment_id;

  //   if (!appointmentId) {
  //     this.toast.add({ severity: 'error', summary: 'Error', detail: 'Appointment not found.' });
  //     return;
  //   }

  //   const prescriptionPayload = this.buildPrescriptionPayload(mode, appointmentId);

  //   this.savePrescription(prescriptionPayload, mode, true);
  // }

  private handlePrescriptionOnlyFlow(mode: 'pharmacy' | 'case' | 'schedule') {
    const appointmentId = this.prescription.appointment_id;

    if (!appointmentId) {
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Appointment not found.'
      });
      return;
    }

    // CASE or SCHEDULE → must update appointment
    if (mode === 'case' || mode === 'schedule') {
      this.updateAppointmentForCaseOrSchedule(appointmentId, mode).subscribe({
        next: () => {
          const prescriptionPayload = this.buildPrescriptionPayload(mode, appointmentId);
          this.savePrescription(prescriptionPayload, mode, true);
        },
        error: () => {
          this.toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update appointment details.'
          });
        }
      });

      return;
    }

    // PHARMACY → prescription-only, no appointment update needed
    const prescriptionPayload = this.buildPrescriptionPayload(mode, appointmentId);
    this.savePrescription(prescriptionPayload, mode, true);
  }

  /* ---------------------------------------------------
    BUILD APPOINTMENT PAYLOAD
  --------------------------------------------------- */
  private buildAppointmentPayload(mode: 'pharmacy' | 'case' | 'schedule') {
    const now = new Date();
    // const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));

    const ist = new Date(Date.now() + 19800000);
    // const h = ist.getHours();
    const h = now.getHours();

    const appointment_date = ist.toISOString().slice(0, 10);

    const payload: any = {
      clinic_id: Number(sessionStorage.getItem('clinic_id')) || 1,
      doctor_id: Number(sessionStorage.getItem('doctor_id')) || 1,
      patient_id: this.patient.patient_id,
      appointment_date: appointment_date,
      // appointment_date: now.toISOString().slice(0, 10),
      // start_time: `${now.getHours().toString().padStart(2, '0')}:00`,
      // end_time: `${(now.getHours() + 1).toString().padStart(2, '0')}:00`,

      start_time: `${h.toString().padStart(2, '0')}:00`,
      end_time: `${((h + 1) % 24).toString().padStart(2, '0')}:00`,

      // start_time : now.toISOString().substring(11, 16), // HH:MM
      // end_time : new Date(now.getTime() + 60 * 60 * 1000).toISOString().substring(11, 16), // HH:MM

      //! time slot in 09:15 - 10:15 format
      // start_time: now.toTimeString().slice(0, 5),
      // end_time: new Date(now.getTime() + 3600000).toTimeString().slice(0, 5),

      type: 'on_flow'
    };

    // Mode-specific logic
    if (mode === 'pharmacy') {
      payload.appointment_type = this.resolveSearchAppointmentType();
      payload.status = 'medicine';
      payload.collect_later = false;
    } else if (mode === 'case') {
      payload.appointment_type = 'case';
      payload.status = 'completed';
      payload.collect_later = false;
    } else if (mode === 'schedule') {
      payload.appointment_type = this.resolveSearchAppointmentType();
      payload.status = 'medicine_scheduled';
      payload.collect_later = true;
    }

    return payload;
  }

  private resolveSearchAppointmentType(): 'new' | 'follow up' {
    if (this.searchVisitType === 'new') {
      return 'new';
    }

    if (this.searchVisitType === 'follow_up') {
      return 'follow up';
    }

    const hasHistory = Array.isArray(this.filteredRecords) && this.filteredRecords.length > 0;
    return hasHistory ? 'follow up' : 'new';
  }

  /* ---------------------------------------------------
    BUILD PRESCRIPTION PAYLOAD
  --------------------------------------------------- */
  private buildPrescriptionPayload(mode: string, appointmentId: number) {
    this.applyClinicalTextCapitalization();
    const transformedPrescriptions = this.transformPrescriptions();
    const totalReceived =
      Number(this.prescription.received_cash || 0) +
      Number(this.prescription.received_online || 0);
    const mainAction = mode === 'case' ? 'complete' : 'save_payment';

    const payload: any = {
      id: this.prescription.id,
      appointment_id: appointmentId,
      allergies: this.prescription.allergies || '',
      current_medications: this.prescription.current_medications || '',
      personal_family_history: this.prescription.personal_family_history || '',
      surgical_history: this.prescription.surgical_history || '',
      blood_pressure: this.prescription.blood_pressure || '',
      pulse_rate: this.prescription.pulse_rate || '',
      temperature: this.prescription.temperature || '',
      weight: this.prescription.weight || '',
      height: this.prescription.height || '',
      fasting_blood_sugar: this.prescription.fasting_blood_sugar || '',
      blood_oxygen: this.prescription.blood_oxygen || '',
      runny_nose: this.prescription.runny_nose || '',
      cough: this.prescription.cough || '',
      chief_complaints: this.prescription.chief_complaints || '',
      diagnosis: this.diagnosisTags.map((tag: any) => tag.diagnosis || tag),
      suggested_reports: this.reportTags.map((tag: any) => tag.report),
      prescriptions: transformedPrescriptions,
      personal_dues: this.prescription.personal_dues || 0,
      visit_fees: this.prescription.visit_fees || 0,
      received_cash: this.prescription.received_cash || 0,
      received_online: this.prescription.received_online || 0,
      notes: this.prescription.notes || '',
      is_courier: this.normalizePrescriptionMode(this.prescription.is_courier),
      special_instructions: this.prescription.special_instructions || '',
      main_action: mainAction,
      collect_payment: totalReceived > 0
    };

    // Mode-specific additions
    payload.collection_date = mode === 'schedule' ? this.scheduledDate : null;

    return payload;
  }

  /* ---------------------------------------------------
    UPDATE APPOINTMENT FOR CASE OR SCHEDULE
  --------------------------------------------------- */
  private updateAppointmentForCaseOrSchedule(appointmentId: number, mode: 'case' | 'schedule') {
    return this.appointmentService.getAppointmentDetails(appointmentId).pipe(
      map((res: any) => {
        if (!res.success || !res.data) {
          throw new Error("Appointment not found");
        }

        const appt = res.data;  // full payload the backend expects

        if (mode === 'case') {
          appt.appointment_type = 'case';
          appt.status = 'completed';
          appt.collect_later = false;
        }

        if (mode === 'schedule') {
          appt.appointment_type = 'new'; // or case → choose your workflow
          appt.status = 'medicine_scheduled';
          appt.collect_later = true;
        }

        return appt;
      }),

      switchMap((updatedAppt) => {
        return this.appointmentService.updateAppointment(updatedAppt);
      })
    );
  }



  /* ---------------------------------------------------
    SAVE PRESCRIPTION (ONE PLACE ONLY)
  --------------------------------------------------- */
  // private savePrescription(payload: any, mode: 'pharmacy' | 'case' | 'schedule', isPrescriptionOnly: boolean) {
  //   this.prescriptionsService.addPrescriptions(payload).subscribe({
  //     next: (res: any) => {
  //       if (!res.success) {
  //         this.toast.add({ 
  //           severity: 'error', 
  //           summary: 'Error', 
  //           // detail: res.message, 
  //           detail: this.getPrescriptionErrorMessage(mode) 
  //         });
  //         return;
  //       }

  //       const savedPrescription = res.data;
  //       const prescriptionId = savedPrescription.id;

  //       this.uploadPendingFiles(prescriptionId);

  //       // If this was prescription-only flow, update appointment status according to mode
  //       if (isPrescriptionOnly && payload.appointment_id) {
  //         let statusToSet = 'medicine'; // default

  //         if (mode === 'pharmacy') statusToSet = 'medicine';
  //         else if (mode === 'case') statusToSet = 'completed';
  //         else if (mode === 'schedule') statusToSet = 'medicine_scheduled';

  //         this.appointmentService.changeStatus(payload.appointment_id, { status: statusToSet }).subscribe({
  //           next: () => {
  //             // reload queue after status update
  //             if (typeof this.loadQueue === 'function') {
  //               this.loadQueue();
  //             }
  //           },
  //           error: (err: any) => {
  //             console.error('Error changing status:', err);
  //           }
  //         });
  //       }

  //       this.toast.add({
  //         severity: 'success',
  //         summary: 'Success',
  //         detail: this.getSuccessMessage(mode)
  //       });

  //       this.clearForm();
  //     },

  //     error: () => {
  //       this.toast.add({ severity: 'error', summary: 'Error', detail: this.getPrescriptionErrorMessage(mode) });
  //     }
  //   });
  // }

  private savePrescription(payload: any, mode: 'pharmacy' | 'case' | 'schedule', isPrescriptionOnly: boolean) {

    // CREATE or UPDATE logic restored
    const saveObs = this.prescription.id
      ? this.prescriptionsService.updatePrescriptions(payload)
      : this.prescriptionsService.addPrescriptions(payload);

    saveObs.subscribe({
      next: (res: any) => {
        if (!res.success) {
          this.toast.add({
            severity: 'error',
            summary: 'Error',
            detail: this.getPrescriptionErrorMessage(mode)
          });
          return;
        }

        const savedPrescription = res.data;

        // VERY IMPORTANT — restore this logic
        this.prescription.id = savedPrescription.id;

        // Upload files if needed
        this.uploadPendingFiles(savedPrescription.id);

        // If this was prescription-only, update appointment status
        if (isPrescriptionOnly && payload.appointment_id) {

          let statusToSet = 'medicine';
          if (mode === 'case') statusToSet = 'completed';
          if (mode === 'schedule') statusToSet = 'medicine_scheduled';

          this.appointmentService.changeStatus(payload.appointment_id, { status: statusToSet }).subscribe({
            next: () => this.loadQueue?.(),
            error: (err) => console.error('Error updating appointment status:', err)
          });
        }

        // Success message based on mode
        this.toast.add({
          severity: 'success',
          summary: 'Success',
          detail: this.getSuccessMessage(mode)
        });

        this.clearDraftForCurrentContext();

        // match original logic — clear after everything
        this.clearForm();
        this.closeForm?.();
      },

      error: (err) => {
        console.error('Error saving prescription:', err);
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: this.getPrescriptionErrorMessage(mode)
        });
      }
    });
  }

  /* ---------------------------------------------------
    FILE UPLOAD
  --------------------------------------------------- */
  private uploadPendingFiles(prescriptionId: number) {
    if (!this.pendingFiles || this.pendingFiles.length === 0) return;

    const formData = this.prescriptionsService.buildReportUploadFormData({
      patient_id: Number(this.patient?.patient_id ?? this.patient?.id),
      prescription_id: prescriptionId,
      report_type: this.pendingReportType,
      source_type: 'lab',
      files: this.pendingFiles,
    });

    this.prescriptionsService.uploadReports(formData).subscribe({
      next: (uploadRes: any) => {
        if (uploadRes.success) {
          this.pendingFiles = [];
          this.refreshPatientReports();
          this.autoAnalyzeUploadedReports(uploadRes.data || []);
        }
      },
      error: (err) => console.error('Report upload failed:', err)
    });
  }

  /* ---------------------------------------------------
    PRESCRIPTION TRANSFORMER
  --------------------------------------------------- */
  private transformPrescriptions() {
    return (this.prescription.prescriptions || [])
      .filter((p: any) => {
        const hasTitle = (p?.title || '').toString().trim() !== '';
        const hasDuration = (p?.duration || '').toString().trim() !== '';
        const medicines = Array.isArray(p?.medicines) ? p.medicines : [];
        const hasAnyMedicine = medicines.some((m: any) => {
          const hasTags = Array.isArray(m?.tags) ? m.tags.length > 0 : (Array.isArray(m?.medicineTags) && m.medicineTags.length > 0);
          const hasDose = (m?.drams ?? '').toString().trim() !== '';
          const hasSchedule = (m?.ballers ?? '').toString().trim() !== '';
          return hasTags || hasDose || hasSchedule;
        });
        return hasTitle || hasDuration || hasAnyMedicine;
      })
      .map((p: any) => ({
        id: p.id || null,
        title: p.title || '',
        duration: p.duration || '',
        price: Number.isFinite(Number(p?.price)) ? Math.max(0, Math.round(Number(p.price))) : undefined,
        medicines: (p.medicines || []).map((m: any) => ({
          drams: m.drams || '',
          ballers: m.ballers || '',
          tags: (m.tags || m.medicineTags || []).map((t: any) => ({
            id: t.id ?? null,
            name: t.name ?? '',
            code: t.code ?? '',
            potency: t.potency ?? ''
          }))
        }))
      }));
  }

  isPrescriptionEmpty(): boolean {
    const prescriptions = Array.isArray(this.prescription?.prescriptions) ? this.prescription.prescriptions : [];
    if (!prescriptions.length) return true;

    return prescriptions.every((p: any) => {
      const hasTitle = (p?.title || '').toString().trim() !== '';
      const hasDuration = (p?.duration || '').toString().trim() !== '';
      const medicines = Array.isArray(p?.medicines) ? p.medicines : [];
      const hasAnyMedicine = medicines.some((m: any) => {
        const hasTags = Array.isArray(m?.medicineTags) && m.medicineTags.length > 0;
        const hasDose = (m?.drams ?? '').toString().trim() !== '';
        const hasSchedule = (m?.ballers ?? '').toString().trim() !== '';
        return hasTags || hasDose || hasSchedule;
      });
      return !hasTitle && !hasDuration && !hasAnyMedicine;
    });
  }

  onChiefComplaintsBlur(): void {
    this.ccAssistant?.onSymptomsBlur();
  }

  onChiefComplaintKeydown(event: KeyboardEvent) {
    if (event.key === ' ') {
      const textarea = event.target as HTMLTextAreaElement;
      const val = textarea.value || '';
      const cursor = textarea.selectionStart;

      if (cursor > 0 && val[cursor - 1] === ' ') {
        event.preventDefault();

        const before = val.substring(0, cursor - 1);
        const after = val.substring(textarea.selectionEnd);

        this.prescription.chief_complaints = before + ' +' + after;
        textarea.value = this.prescription.chief_complaints;

        const newCursor = before.length + 2;
        textarea.setSelectionRange(newCursor, newCursor);
      }
    }
  }
  parseToArray(val: any): any[] | null {
    if (!val) return null;
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return null;
      }
    }
    return null;
  }


  isCaseValid(): boolean {
    const hasPatient = this.patientSelectionConfirmed && Number(this.patient?.patient_id || 0) > 0;
    const hasChiefComplaints = String(this.prescription?.chief_complaints || '').trim().length > 0;
    const hasDiagnosis = Array.isArray(this.diagnosisTags) && this.diagnosisTags.length > 0;
    return hasPatient && hasChiefComplaints && hasDiagnosis;
  }

  //! Message service messages
  private getSuccessMessage(mode: 'pharmacy' | 'case' | 'schedule'): string {
    if (mode === 'pharmacy') return 'Prescription saved and moved to Pharmacy.';
    if (mode === 'case') return 'Case saved and marked as Completed.';
    if (mode === 'schedule') return 'Prescription scheduled for later collection.';
    return 'Saved successfully.';
  }

  private getAppointmentErrorMessage(mode: 'pharmacy' | 'case' | 'schedule'): string {
    if (mode === 'pharmacy') return 'Failed to create appointment for Pharmacy movement.';
    if (mode === 'case') return 'Failed to save the Case appointment.';
    if (mode === 'schedule') return 'Failed to schedule appointment for later collection.';
    return 'Error saving appointment.';
  }

  private getPrescriptionErrorMessage(mode: 'pharmacy' | 'case' | 'schedule'): string {
    if (mode === 'pharmacy') return 'Unable to save prescription for Pharmacy.';
    if (mode === 'case') return 'Unable to save Case prescription.';
    if (mode === 'schedule') return 'Unable to schedule prescription. Check the date.';
    return 'Failed to save prescription.';
  }






  // savePrescriptionOnly() {
  //   this.prescriptionsService.addPrescriptions(this.prescription).subscribe((res: any) => {
  //     if (res.success) {
  //       const prescription = res.data;

  //       // ✅ Upload reports if there are any files waiting
  //       if (this.pendingFiles && this.pendingFiles.length > 0) {
  //         const formData = new FormData();
  //         formData.append('patient_id', this.patient.patient_id.toString());
  //         formData.append('prescription_id', prescription.id.toString());
  //         this.pendingFiles.forEach((file: File) => {
  //           formData.append('files[]', file, file.name);
  //         });

  //         this.prescriptionsService.uploadReports(formData).subscribe({
  //           next: (uploadRes: any) => {
  //             if (uploadRes.success) {
  //               this.pendingFiles = [];
  //             }
  //           },
  //           error: (err) => console.error('Report upload failed:', err)
  //         });
  //       }

  //       this.toast.add({ severity: 'success', summary: 'Success', detail: res.message });
  //       this.getPrescriptions();

  //       // Change appointment status to 'completed'
  //       if (this.prescription.appointment_id) {
  //         this.appointmentService.changeStatus(this.prescription.appointment_id, 'completed').subscribe({
  //           next: () => this.loadQueue(), // Optionally refresh the queue
  //           error: (err) => console.error('Error changing status:', err)
  //         });
  //       }

  //       this.clearForm();
  //       this.closeForm();

  //     } else {
  //       this.toast.add({ severity: 'error', summary: 'Error', detail: res.message });
  //     }
  //   });
  // }

  savePrescriptionOnly() {
    if (!this.patient.patient_id) {
      console.error('No patient selected!');
      this.toast.add({ severity: 'error', summary: 'Error', detail: 'Please select a patient first.' });
      return;
    }

    if (!this.ensureActionCanProceed('pharmacy')) return;

    this.applyClinicalTextCapitalization();
    this.prescription.chief_complaints = this.prescription.chief_complaints || '';
    this.prescription.diagnosis = this.diagnosisTags.map((tag: any) => tag.diagnosis || tag);
    this.prescription.suggested_reports = this.reportTags.map((tag: any) => tag.report);

    // 🔹 Transform prescriptions for API
    const transformedPrescriptions = this.transformPrescriptions();

    const payload = {
      ...this.prescription,
      prescriptions: transformedPrescriptions || [],
      main_action: 'save_payment',
      collect_payment:
        Number(this.prescription.received_cash || 0) +
        Number(this.prescription.received_online || 0) > 0
    };

    // Determine whether to create or update
    const saveObs = this.prescription.id
      ? this.prescriptionsService.updatePrescriptions(payload)
      : this.prescriptionsService.addPrescriptions(payload);

    saveObs.subscribe({
      next: (res: any) => {
        if (!res.success) {
          this.toast.add({ severity: 'error', summary: 'Error', detail: res.message });
          return;
        }

        const prescription = res.data;

        // ✅ Update the current prescription id after save
        this.prescription.id = prescription.id;

        // Upload reports if any
        if (this.pendingFiles && this.pendingFiles.length > 0) {
          const formData = new FormData();
          formData.append('patient_id', this.patient.patient_id.toString());
          formData.append('prescription_id', this.prescription.id.toString());

          this.pendingFiles.forEach((file: File) => {
            formData.append('files[]', file, file.name);
          });

          this.prescriptionsService.uploadReports(formData).subscribe({
            next: (uploadRes: any) => {
              if (uploadRes.success) {
                this.pendingFiles = []; // ✅ Clear pending files after successful upload
                this.refreshPatientReports();
                this.autoAnalyzeUploadedReports(uploadRes.data || []);
              } else {
                console.error('Report upload failed:', uploadRes.message);
              }
            },
            error: (err) => console.error('Report upload failed:', err)
          });
        }

        this.toast.add({ severity: 'success', summary: 'Success', detail: res.message });

        // Refresh prescriptions and queue
        this.getPrescriptions();
        if (this.prescription.appointment_id) {
          this.appointmentService.changeStatus(this.prescription.appointment_id, { status: 'medicine' }).subscribe({
            next: () => this.loadQueue(),
            error: (err) => console.error('Error changing status:', err)
          });
        }

        // ✅ Clear forms only after everything is handled
        this.clearDraftForCurrentContext();

        this.clearForm();
        this.closeForm();
      },
      error: (err) => {
        console.error('Error saving prescription:', err);
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save prescription.' });
      }
    });
  }

  // updatePatients() {
  //   this.prescriptionsService.updatePrescriptions(this.patient).subscribe((res: any) => {
  //     if (res.data) {
  //       this.toast.add({ severity: 'success', summary: 'Success', detail: res.message });
  //       this.getPrescriptions();
  //       this.closeForm();
  //     } else {
  //       this.toast.add({ severity: 'error', summary: 'Error', detail: res.message });
  //     }
  //   });
  // }

  handleEvent(event: any) {
    // console.log(event);
    if (event.action.tooltip == "Update Patient") {
      this.editRecord = event.data;
      this.patient = event.data;
      setTimeout(() => {
        this.editRecord = null;
      }, 1);
    } else if (event.action.tooltip == "Delete Patient") {
      this.editRecord = event.data;
      this.delRecPopupOpen({ title: this.editRecord?.['first_name  '], obj: this.editRecord });
    }
  }

  delRecPopupOpen(item: any) {
    this.openDeletePopup = item;
    setTimeout(() => {
      this.openDeletePopup = null;
    }, 1);
  }

  onCardUpload(event: any) {
    const file = event.target.files[0];
    this.patient.scanned_card = file ?? null;
  }

  onPrintReceipt() {
    // console.log('Printing receipt...');
  }

  onShowReceiptHistory() {
    // console.log('Showing receipt history...');
  }

  onShowOutstandingAll() {
    // console.log('Showing all outstanding balances...');
  }

  markDirty() {
    this.isDirty = true;
    this.isDraftAutosavePausedUntilChange = false;
    this.saveDraftSnapshot();
  }

  // Reset dirty state after saving/updating
  resetDirty() {
    this.isDirty = false;
  }

  patientHistory: any[] = [
    { date: '2025-08-01', summary: 'Follow-up checkup, BP stable' },
    { date: '2025-08-10', summary: 'Blood test results normal' },
    { date: '2025-08-15', summary: 'Prescription updated: Vitamin D' }
  ];

  openPoPopup() {
    this.displayHistory = true;
  }

  closePoPopup() {
    this.displayHistory = false;
  }

  // Method to close Patient section
  closePatient() {
    this.isPatientVisible = false;
  }

  // Method to reopen Patient section
  openPatient() {
    this.isPatientVisible = true;
  }

  openReports(date: string) {
    this.selectedDate = date;
    this.isReportDialogVisible = true;
  }

  searchPatients(event: any) {
    const query = event.query;
    this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders() })
      .subscribe(res => {
        if (!res.data || res.data.length === 0) {
          this.patientsList = [];
          this.toast.add({
            severity: 'info',
            summary: 'Info',
            detail: `No patients found for "${query}".`
          });
        } else {
          this.patientsList = res.data.map((p: any) => ({
            ...p,
            full_name: `${p.first_name || ''} ${p.middle_name || ''} ${p.last_name || ''} (${p.patient_number})`
          }));
        }
        // Stop spinner and update view
        this.cdr.detectChanges();
      }, error => {
        this.patientsList = [];
        this.toast.add({
          severity: 'info',
          summary: 'Info',
          detail: 'No patients found.'
        });
        this.cdr.detectChanges();
      });
  }

  onPatientSelect(event: any) {
    // Save outgoing patient's draft with frozen scope before mutating selection.
    const outgoingPatientId = Number(this.patient?.patient_id || this.patient?.id || 0);
    if (outgoingPatientId > 0) {
      this.persistDraftSnapshot(
        {
          context_type: 'prescription',
          appointment_id: this.patientSelectedFromSearch
            ? null
            : (Number(this.prescription?.appointment_id || 0) || null),
          patient_id: outgoingPatientId,
          patient_selected_from_search: this.patientSelectedFromSearch
        },
        true
      );
    }
    this.isDraftAutosaveSuspended = true;

    const selectionToken = ++this.activeSearchSelectionToken;

    // event.value is the selected patient object
    const nextPatientId = Number(event.value.id || event.value.patient_id || 0);
    this.patient.patient_id = nextPatientId;
    this.patient.id = nextPatientId;
    this.patientSelectionConfirmed = Number(this.patient.patient_id || 0) > 0;
    const selectedSearchMode = this.resolvePrescriptionMode(event?.value?.is_courier);
    this.patient.is_courier = selectedSearchMode;
    this.prescription.is_courier = selectedSearchMode;
    // Optionally, store the whole patient object if needed
    this.selectedPatient = null;
    this.patientsList = [];
    this.clearReportAnalysisPolling();
    this.reportsDocFilter = 'all';
    this.reportsDocSearch = '';
    this.resetImageCompareState();
    this.uploadsByDate = [];
    this.reportsMeta = { total: 0, limit: 40, offset: 0, hasMore: false };
    this.syncReportsViewModel();
    // console.log('Selected patient_id:', this.patient.patient_id);
    this.patientSelectedFromSearch = true;
    this.searchVisitType = 'auto';
    this.isPatientVisible = false;
    this.patientFromSearch = true;
    this.refreshDoctorNotes([]);
    this.currentAppointmentStatus = '';
    this.currentAppointmentDate = '';
    this.recalledFromPharmacy = false;

    //! Search selection starts a fresh consultation context.
    //! Reset current prescription state to avoid previous patient's draft bleed.
    this.prescription = {
      ...this.prescription,
      id: 0,
      appointment_id: 0,
      allergies: '',
      current_medications: '',
      personal_family_history: '',
      surgical_history: '',
      blood_pressure: '',
      pulse_rate: '',
      temperature: '',
      weight: '',
      height: '',
      fasting_blood_sugar: '',
      blood_oxygen: '',
      runny_nose: '',
      cough: '',
      chief_complaints: '',
      suggested_reports: '',
      diet: '',
      exercise: '',
      prescriptions: this.normalizePrescriptionRows([]),
      received_cash: 0,
      received_online: 0,
      personal_dues: 0,
      notes: '',
      special_instructions: ''
    };
    this.complaintTags = [];
    this.diagnosisTags = [];
    this.reportTags = [];
    this.selectedIcdCodes = [];
    this.showSchedulePicker = false;
    this.scheduledDate = null;
    this.isDraftAutosavePausedUntilChange = false;

    // 1. Fetch patient info
    this.patientRegistrationService.getSinglePatient(this.patient.patient_id).subscribe((res: any) => {
      if (selectionToken !== this.activeSearchSelectionToken) return;
      if (res.success) {
        const patient = res.data;
        const patientVisitFees = Number(patient.visit_fees ?? 0) || 0;
        this.patient.first_name = patient.first_name || '';
        this.patient.middle_name = patient.middle_name || '';
        this.patient.last_name = patient.last_name || '';
        this.patient.primary_contact = patient.primary_contact || '';
        this.patient.secondary_contact = patient.secondary_contact || '';
        this.patient.age = patient.age ?? 0;
        this.patient.gender = patient.gender || '';
        this.patient.patient_number = patient.patient_number || '';
        this.patient.blood_group = patient.blood_group || '';
        this.patient.address = patient.address || '';
        this.patient.city = patient.city || '';
        this.patient.state = patient.state || '';
        this.patient.country = patient.country || '';
        this.patient.pincode = patient.pincode || '';
        this.patient.is_free = patient.is_free || false;
        this.patient.referred_by = patient.referred_by || '';
        this.patient.first_visit = new Date(patient.first_visit).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
        this.patient.is_courier = this.normalizePrescriptionMode(patient.is_courier);

        // Default prescription mode from patient mode; user can still change it in the dropdown.
        this.prescription.is_courier = this.patient.is_courier;
        // Default from patient registration; remains editable in prescription form.
        this.prescription.visit_fees = patientVisitFees;
        this.prescription.personal_dues = this.calculatePersonalDues([], this.prescription);
      }
    });

    // 2. Fetch only latest page for immediate context; full history stays lazy in History tab.
    this.resetHistoryStateForPatient(this.patient.patient_id);
    this.prescriptionsService.getPrescriptionsByPatientId(this.patient.patient_id, {
      page: 1,
      per_page: this.historyPageSize
    })
      .subscribe({
        next: (res: any) => {
        if (selectionToken !== this.activeSearchSelectionToken) return;

        const records = this.extractHistoryRecords(res);
        this.filteredRecords = records;
        this.historyCurrentPage = this.extractHistoryPage(res) || (records.length ? 1 : 0);
        this.historyTotalRecords = this.extractHistoryTotal(res, records.length);
        this.historyHasMore = this.extractHistoryHasMore(res, records.length);
        this.historyInitializedForPatientId = Number(this.patient.patient_id) || 0;
        this.selectedHistoryIndex = 0;

        if (records.length > 0) {
          const latestRecordMode = this.getLatestModeFromRecords(records);
          const resolvedMode = this.resolvePrescriptionMode(
            this.patient.is_courier,
            latestRecordMode,
            selectedSearchMode
          );
          this.patient.is_courier = resolvedMode;
          this.prescription.is_courier = resolvedMode;

          const recallableAppointment = this.findTodaysRecallableAppointment(records);
          if (recallableAppointment) {
            this.prescription.appointment_id = Number(recallableAppointment.appointment_id) || 0;
            this.currentAppointmentStatus = (
              recallableAppointment?.status || recallableAppointment?.appointment_status || ''
            ).toString();
            this.currentAppointmentDate = (
              recallableAppointment?.appointment_date || recallableAppointment?.visit_date || ''
            ).toString();
          } else {
            this.prescription.appointment_id = 0;
            this.currentAppointmentStatus = '';
            this.currentAppointmentDate = '';
          }
          this.prescription.personal_dues = this.calculatePersonalDues(records, this.prescription);
          this.refreshDoctorNotes(records);
        } else {
          this.currentAppointmentStatus = '';
          this.currentAppointmentDate = '';
          this.prescription.personal_dues = this.calculatePersonalDues([], this.prescription);
          this.refreshDoctorNotes([]);
        }

        this.restoreDraftForCurrentContext(true, true, Number(this.patient.patient_id || 0));
        this.prescription.personal_dues = this.calculatePersonalDues(this.filteredRecords, this.prescription);
        },
        error: () => {
          if (selectionToken !== this.activeSearchSelectionToken) return;
          this.isDraftAutosaveSuspended = false;
        }
      });

    // ✅ Fetch reports
    this.refreshPatientReports(selectionToken);
  }

  // Utility
  safeParse(value: any) {
    try {
      return typeof value === 'string' ? JSON.parse(value) : value || [];
    } catch {
      return [];
    }
  }

  private getLatestPrescription(prescriptions: any): any {
    const list = Array.isArray(prescriptions) ? prescriptions : [];
    if (!list.length) return {};

    return [...list].sort((a: any, b: any) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      if (aTime !== bTime) return bTime - aTime;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    })[0] || {};
  }

  private normalizeComplaintTags(raw: any): any[] {
    if (typeof raw === 'string') {
      const plain = raw.trim();
      if (!plain) return [];

      try {
        const parsed = JSON.parse(plain);
        return this.normalizeComplaintTags(parsed);
      } catch {
        return plain
          .split(/[\n,]+/)
          .map((item: string, index: number) => {
            const text = item.trim();
            if (!text) return null;
            return {
              id: index + 1,
              chief_complaint: text,
              deleted_at: null
            };
          })
          .filter(Boolean);
      }
    }

    const items = this.safeParse(raw);
    if (!Array.isArray(items)) return [];

    return items
      .map((item: any, index: number) => {
        if (item && typeof item === 'object') {
          const text = String(item.chief_complaint ?? item.complaint ?? '').trim();
          if (!text) return null;
          return {
            id: item.id ?? index + 1,
            chief_complaint: text,
            deleted_at: item.deleted_at ?? null
          };
        }

        const text = String(item ?? '').trim();
        if (!text) return null;
        return {
          id: index + 1,
          chief_complaint: text,
          deleted_at: null
        };
      })
      .filter(Boolean);
  }

  normalizeComplaintTextarea(raw: any): string {
    if (!raw) return '';
    if (typeof raw === 'string') {
      const plain = raw.trim();
      if (!plain) return '';
      try {
        const parsed = JSON.parse(plain);
        return this.normalizeComplaintTextarea(parsed);
      } catch {
        return plain;
      }
    }
    if (Array.isArray(raw)) {
      return raw.map((item: any) => {
        if (item && typeof item === 'object') {
          return item.chief_complaint ?? item.complaint ?? '';
        }
        return String(item ?? '');
      }).filter(Boolean).join(', ');
    }
    return '';
  }

  private applyClinicalTextCapitalization(): void {
    capitalizeClinicalTextFields(this.prescription);
  }

  private applyTextReportCapitalization(): void {
    this.reportTextTitle = capitalizeLineStarts(this.reportTextTitle);
    this.reportTextContent = capitalizeLineStarts(this.reportTextContent);
  }

  normalizeDiagnosisTags(raw: any): any[] {
    if (typeof raw === 'string') {
      const plain = raw.trim();
      if (!plain) return [];

      try {
        const parsed = JSON.parse(plain);
        return this.normalizeDiagnosisTags(parsed);
      } catch {
        return plain
          .split(/[\n,]+/)
          .map((item: string, index: number) => {
            const text = item.trim();
            if (!text) return null;
            return {
              id: index + 1,
              diagnosis: text
            };
          })
          .filter(Boolean);
      }
    }

    const items = this.safeParse(raw);
    if (!Array.isArray(items)) return [];

    return items
      .map((item: any, index: number) => {
        if (item && typeof item === 'object') {
          const text = String(item.diagnosis ?? item.name ?? '').trim();
          if (!text) return null;
          return {
            id: item.id ?? index + 1,
            diagnosis: text
          };
        }
        const text = String(item ?? '').trim();
        if (!text) return null;
        return {
          id: index + 1,
          diagnosis: text
        };
      })
      .filter(Boolean);
  }

  private normalizeReportTags(raw: any): any[] {
    if (typeof raw === 'string') {
      const plain = raw.trim();
      if (!plain) return [];

      try {
        const parsed = JSON.parse(plain);
        return this.normalizeReportTags(parsed);
      } catch {
        return plain
          .split(/[\n,]+/)
          .map((item: string, index: number) => {
            const text = item.trim();
            if (!text) return null;
            return {
              id: index + 1,
              report: text
            };
          })
          .filter(Boolean);
      }
    }

    const items = this.safeParse(raw);
    if (!Array.isArray(items)) return [];

    return items
      .map((item: any, index: number) => {
        if (item && typeof item === 'object') {
          const text = String(item.report ?? '').trim();
          if (!text) return null;
          return {
            id: item.id ?? index + 1,
            report: text
          };
        }

        const text = String(item ?? '').trim();
        if (!text) return null;
        return {
          id: index + 1,
          report: text
        };
      })
      .filter(Boolean);
  }

  private hydrateFromLatestHistoryIfCurrentIsEmpty(records: any[]) {
    if (!Array.isArray(records) || records.length === 0) return;

    const hasNotes = !!String(this.prescription?.notes || '').trim();
    const hasInstructions = !!String(this.prescription?.special_instructions || '').trim();
    const hasComplaints = !!String(this.prescription?.chief_complaints || '').trim();
    const hasDiagnosis = Array.isArray(this.diagnosisTags) && this.diagnosisTags.length > 0;
    const hasReports = Array.isArray(this.reportTags) && this.reportTags.length > 0;
    const hasMedicines = (this.prescription?.prescriptions || []).some((rx: any) =>
      (rx?.medicines || []).some((m: any) => (m?.tags || []).length > 0)
    );

    if (hasNotes || hasInstructions || hasComplaints || hasDiagnosis || hasReports || hasMedicines) {
      return;
    }

    const latest = [...records].sort((a: any, b: any) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      if (aTime !== bTime) return bTime - aTime;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    })[0];

    if (!latest) return;

    const appointmentId = this.prescription?.appointment_id || 0;

    this.prescription = {
      ...this.prescription,
      id: latest?.id || this.prescription?.id || 0,
      appointment_id: appointmentId,
      allergies: latest?.allergies || '',
      current_medications: latest?.current_medications || '',
      personal_family_history: latest?.personal_family_history || '',
      surgical_history: latest?.surgical_history || '',
      blood_pressure: latest?.blood_pressure || '',
      pulse_rate: latest?.pulse_rate || '',
      temperature: latest?.temperature || '',
      weight: latest?.weight || '',
      height: latest?.height || '',
      fasting_blood_sugar: latest?.fasting_blood_sugar || '',
      blood_oxygen: latest?.blood_oxygen || '',
      runny_nose: latest?.runny_nose || '',
      cough: latest?.cough || '',
      notes: latest?.notes || this.prescription?.notes || '',
      special_instructions: latest?.special_instructions || this.prescription?.special_instructions || '',
      prescriptions: this.normalizePrescriptionRows(latest?.prescriptions),
      chief_complaints: this.normalizeComplaintTextarea(latest?.chief_complaints)
    };

    this.diagnosisTags = this.normalizeDiagnosisTags(latest?.diagnosis);
    this.reportTags = this.normalizeReportTags(latest?.suggested_reports);
  }

  private normalizeMedicineTag(tag: any): any | null {
    const name = String(tag?.name ?? tag?.medicine_name ?? '').trim();
    const code = String(tag?.code ?? '').trim();
    const potency = String(tag?.potency ?? '').trim();

    if (!name && !code && !potency) return null;

    return {
      id: tag?.id ?? null,
      name,
      code,
      potency
    };
  }

  private createEmptyMedicineRow(): any {
    return {
      tags: [],
      medicineTags: [],
      dose: '',
      drams: '',
      ballers: '',
      medicineInput: '',
      filteredMedicines: [],
      filteredDoses: [],
      unresolvedMedicines: [],
      resolvedMedicines: [],
      unresolvedHints: []
    };
  }

  private normalizePrescriptionRows(rows: any): any[] {
    const parsedRows = typeof rows === 'string' ? this.safeParse(rows) : rows;
    const source = Array.isArray(parsedRows) ? parsedRows : [];
    if (!source.length) {
      return [{
        id: 1,
        title: '',
        duration: '',
        medicines: [this.createEmptyMedicineRow()]
      }];
    }

    return source.map((rx: any, rxIndex: number) => {
      const medicinesSource = Array.isArray(rx?.medicines) ? rx.medicines : [];
      const medicines = medicinesSource.length
        ? medicinesSource.map((m: any) => {
          const sourceTags = Array.isArray(m?.tags) && m.tags.length
            ? m.tags
            : (Array.isArray(m?.medicineTags) ? m.medicineTags : []);
          const normalizedTags = sourceTags
            .map((t: any) => this.normalizeMedicineTag(t))
            .filter(Boolean);

          return {
            ...this.createEmptyMedicineRow(),
            ...m,
            tags: normalizedTags,
            medicineTags: [...normalizedTags],
            dose: m?.dose ?? '',
            drams: m?.drams ?? '',
            ballers: m?.ballers ?? '',
            medicineInput: m?.medicineInput ?? '',
            filteredMedicines: [],
            filteredDoses: [],
            unresolvedMedicines: Array.isArray(m?.unresolvedMedicines) ? m.unresolvedMedicines : [],
            resolvedMedicines: Array.isArray(m?.resolvedMedicines) ? m.resolvedMedicines : [],
            unresolvedHints: Array.isArray(m?.unresolvedHints) ? m.unresolvedHints : []
          };
        })
        : [this.createEmptyMedicineRow()];

      return {
        ...rx,
        id: rx?.id ?? rxIndex + 1,
        title: rx?.title ?? '',
        duration: rx?.duration ?? '',
        medicines
      };
    });
  }

  private sanitizeNote(value: any): string {
    if (value === null || value === undefined) return '';
    const note = String(value).trim();
    if (!note || note.toLowerCase() === 'null' || note.toLowerCase() === 'undefined') {
      return '';
    }
    return note;
  }

  private getRecordTimestamp(record: any): number {
    const rawDate = record?.visit_date || record?.created_at || record?.updated_at;
    const time = rawDate ? new Date(rawDate).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  }

  private formatRecordDate(record: any): string {
    const rawDate = record?.visit_date || record?.created_at || record?.updated_at;
    if (!rawDate) return '';

    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  private refreshDoctorNotes(records: any[] = []) {
    const currentPrescriptionId = Number(this.prescription?.id) || 0;

    const source = Array.isArray(records) ? records : [];

    const noteHistory = source
      .filter((record: any) => {
        const hasNote = !!this.sanitizeNote(record?.notes);
        const isCurrentRecord = currentPrescriptionId > 0 && Number(record?.id) === currentPrescriptionId;
        return hasNote && !isCurrentRecord;
      })
      .sort((a: any, b: any) => {
        const timeDiff = this.getRecordTimestamp(b) - this.getRecordTimestamp(a);
        if (timeDiff !== 0) return timeDiff;
        return (Number(b?.id) || 0) - (Number(a?.id) || 0);
      })
      .map((record: any) => ({
        date: this.formatRecordDate(record),
        notes: this.sanitizeNote(record?.notes)
      }));

    this.doctorNotesHistory = noteHistory;
    this.latestDoctorNote = noteHistory[0]?.notes || '';
    this.latestDoctorNoteDate = noteHistory[0]?.date || '';

    const instructionHistory = source
      .filter((record: any) => {
        const hasInstruction = !!this.sanitizeNote(record?.special_instructions);
        const isCurrentRecord = currentPrescriptionId > 0 && Number(record?.id) === currentPrescriptionId;
        return hasInstruction && !isCurrentRecord;
      })
      .sort((a: any, b: any) => {
        const timeDiff = this.getRecordTimestamp(b) - this.getRecordTimestamp(a);
        if (timeDiff !== 0) return timeDiff;
        return (Number(b?.id) || 0) - (Number(a?.id) || 0);
      })
      .map((record: any) => ({
        date: this.formatRecordDate(record),
        notes: this.sanitizeNote(record?.special_instructions)
      }));

    this.specialInstructionsHistory = instructionHistory;
    this.latestSpecialInstruction = instructionHistory[0]?.notes || '';
    this.latestSpecialInstructionDate = instructionHistory[0]?.date || '';
  }

  openDoctorNotesDialog(tab: 'notes' | 'instructions' = 'notes') {
    this.doctorNotesTab = tab;
    this.doctorNotesDialogVisible = true;
  }

  openAddPatientDialog() {
    this.newPatient = {
      first_name: '', middle_name: '', last_name: '',
      primary_contact: '', gender: '', age: null,
      blood_group: '', city: '', address: '',
      clinic_id: 1
    };
    this.duplicatePatient = null;
    this.checkingDuplicate = false;
    this.confirmDuplicateOverride = false;
    this.newPatientAgeError = null;
    this.showAddPatientDialog = true;
  }

  onNewPatientAgeChange(value: string | number | null): void {
    this.newPatient.age = normalizePatientAgeInput(value);
    this.newPatientAgeError = getPatientAgeError(this.newPatient.age);
  }

  onNewPatientAgeBlur(): void {
    this.newPatient.age = finalizePatientAgeInput(this.newPatient.age);
    this.newPatientAgeError = getPatientAgeError(this.newPatient.age);
  }

  clearDuplicateWarning() {
    this.duplicatePatient = null;
    this.confirmDuplicateOverride = false;
  }

  checkDuplicatePatient() {
    const mobile = this.newPatient.primary_contact?.trim();
    if (!mobile || mobile.length < 7) return;
    this.checkingDuplicate = true;
    this.http.get<any>(this.apiUrl + '/patient/search', {
      params: { q: mobile },
      ...this.getAuthHeaders()
    }).subscribe({
      next: (res) => {
        this.checkingDuplicate = false;
        const match = (res.data || []).find((p: any) =>
          p.primary_contact === mobile || p.secondary_contact === mobile
        );
        this.duplicatePatient = match || null;
      },
      error: () => { this.checkingDuplicate = false; }
    });
  }
  saveNewPatient() {
    const p = this.newPatient;
    if (!p.first_name?.trim() || !p.last_name?.trim() || !p.primary_contact?.trim() || !p.gender || !p.age) {
      this.toast.add({ severity: 'warn', summary: 'Required', detail: 'First name, last name, mobile, gender and age are required.' });
      return;
    }
    p.age = finalizePatientAgeInput(p.age);
    this.newPatientAgeError = getPatientAgeError(p.age);
    if (!isValidPatientAge(p.age)) {
      this.toast.add({
        severity: 'warn',
        summary: 'Invalid age',
        detail: getPatientAgeError(p.age) || 'Please enter a valid age.',
      });
      return;
    }
    this.savingNewPatient = true;
    this.patientRegistrationService.addPatient({
      ...p,
      age: normalizePatientAgeForStorage(p.age) ?? p.age,
    }).subscribe({
      next: (res: any) => {
        this.savingNewPatient = false;
        this.showAddPatientDialog = false;
        const newId = res?.data?.id || res?.id;
        if (newId) {
          // Load the newly created patient directly into the consultation
          this.patientRegistrationService.getSinglePatient(newId).subscribe((patientRes: any) => {
            const pt = patientRes?.data || patientRes;
            this.saveDraftSnapshot(true);
            this.patient.patient_id = pt.id;
            this.patient.first_name = pt.first_name || '';
            this.patient.middle_name = pt.middle_name || '';
            this.patient.last_name = pt.last_name || '';
            this.patient.primary_contact = pt.primary_contact || '';
            this.patient.age = pt.age || '';
            this.patient.gender = pt.gender || '';
            this.patient.blood_group = pt.blood_group || '';
            this.patient.patient_number = pt.patient_number || '';
            this.patient.is_free = pt.is_free || false;
            this.patientSelectionConfirmed = true;
            this.patientFromSearch = true;
            this.isPatientVisible = false;
            this.refreshDoctorNotes([]);
            this.cdr.detectChanges();
            this.toast.add({
              severity: 'success', summary: 'Patient Created',
              detail: `${pt.first_name} ${pt.last_name} has been created and loaded.`
            });
          });
        } else {
          this.toast.add({
            severity: 'success', summary: 'Patient Added',
            detail: `${p.first_name} ${p.last_name} created successfully.`
          });
        }
      },
      error: (err: any) => {
        this.savingNewPatient = false;
        const msg = err?.error?.message || 'Failed to create patient. Please try again.';
        this.toast.add({ severity: 'error', summary: 'Error', detail: msg });
      }
    });
  }

  openAnatomyLink() {
    const url = 'https://anatomy.drshaileshsancheti.com/';
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  openMedicineSearchDialog() {
    this.medicineSearchDialogVisible = true;
    this.medicineSearchError = '';

    if (!this.medicineSearchResults.length && this.medicineSearchKeyword.trim()) {
      this.searchMedicineFinder();
    }
  }

  getDefaultRepertoryPrescriptionIndex(): number {
    const count = this.prescription?.prescriptions?.length || 0;
    return count > 0 ? count - 1 : 0;
  }

  openRepertoryDialog(pIndex?: number) {
    const prescriptions = this.prescription?.prescriptions ?? [];
    if (!prescriptions.length) {
      this.toast.warn('Repertory', 'Add a prescription first.');
      return;
    }

    const defaultTarget = this.findDefaultRepertoryTarget(pIndex);
    this.setRepertoryTarget(defaultTarget.pIndex, defaultTarget.mIndex, false);
    this.repertoryInitialSymptom = this.extractRepertorySeedSymptom();
    this.repertorySessionCount = 0;
    this.repertoryDialogVisible = true;
  }

  closeRepertoryDialog() {
    this.repertoryDialogVisible = false;
    this.repertorySessionCount = 0;
  }

  setRepertoryTarget(pIndex: number, mIndex: number, focusToast = false): void {
    const medicines = this.prescription?.prescriptions?.[pIndex]?.medicines;
    if (!medicines?.length) {
      return;
    }

    this.repertoryTargetPIndex = pIndex;
    this.repertoryTargetMIndex = Math.min(Math.max(mIndex, 0), medicines.length - 1);
    this.refreshRepertoryTargetLabel();

    if (focusToast) {
      this.toast.info('Repertory', `Insert target: ${this.repertoryTargetLabel}`);
    }
  }

  isRepertoryTargetActive(pIndex: number, mIndex: number): boolean {
    return this.repertoryTargetPIndex === pIndex && this.repertoryTargetMIndex === mIndex;
  }

  getRepertoryTargetGroups(): RepertoryTargetGroup[] {
    const prescriptions = this.prescription?.prescriptions ?? [];

    return prescriptions.map((pres: any, pIndex: number) => {
      const rxTitle = String(pres?.title || '').trim();
      const rxLabel = rxTitle ? `Rx ${pIndex + 1} · ${rxTitle}` : `Rx ${pIndex + 1}`;
      const rows = (pres?.medicines ?? []).map((med: any, mIndex: number) => ({
        mIndex,
        summary: this.getMedicineRowSummary(med),
        isEmpty: this.isMedicineRowEmpty(med),
      }));

      return { pIndex, rxLabel, rows };
    });
  }

  addMedicineRowFromRepertory(pIndex: number): void {
    this.addMedicineRow(pIndex);
    const medicines = this.prescription?.prescriptions?.[pIndex]?.medicines ?? [];
    if (medicines.length) {
      this.setRepertoryTarget(pIndex, medicines.length - 1);
    }
  }

  jumpToNextEmptyRepertoryRow(): void {
    const next = this.findNextEmptyRepertoryTarget(
      this.repertoryTargetPIndex ?? 0,
      this.repertoryTargetMIndex ?? 0,
    );

    if (!next) {
      this.toast.info('Repertory', 'No more empty rows. Use + on an Rx to add one.');
      return;
    }

    this.setRepertoryTarget(next.pIndex, next.mIndex, true);
  }

  private findDefaultRepertoryTarget(preferredRxIndex?: number): { pIndex: number; mIndex: number } {
    const prescriptions = this.prescription?.prescriptions ?? [];
    if (!prescriptions.length) {
      return { pIndex: 0, mIndex: 0 };
    }

    const searchOrder = preferredRxIndex !== undefined
      ? [preferredRxIndex, ...prescriptions.map((_: any, idx: number) => idx).filter((idx: number) => idx !== preferredRxIndex)]
      : prescriptions.map((_: any, idx: number) => idx);

    for (const pIndex of searchOrder) {
      const medicines = prescriptions[pIndex]?.medicines ?? [];
      for (let mIndex = 0; mIndex < medicines.length; mIndex++) {
        if (this.isMedicineRowEmpty(medicines[mIndex])) {
          return { pIndex, mIndex };
        }
      }
    }

    const lastRxIndex = prescriptions.length - 1;
    const lastRows = prescriptions[lastRxIndex]?.medicines ?? [];
    return {
      pIndex: lastRxIndex,
      mIndex: Math.max(lastRows.length - 1, 0),
    };
  }

  private findNextEmptyRepertoryTarget(
    fromPIndex: number,
    fromMIndex: number,
  ): { pIndex: number; mIndex: number } | null {
    const prescriptions = this.prescription?.prescriptions ?? [];
    const flatTargets: Array<{ pIndex: number; mIndex: number; isEmpty: boolean }> = [];

    prescriptions.forEach((pres: any, pIndex: number) => {
      (pres?.medicines ?? []).forEach((med: any, mIndex: number) => {
        flatTargets.push({ pIndex, mIndex, isEmpty: this.isMedicineRowEmpty(med) });
      });
    });

    const currentIndex = flatTargets.findIndex(
      (target) => target.pIndex === fromPIndex && target.mIndex === fromMIndex,
    );
    const startIndex = currentIndex >= 0 ? currentIndex + 1 : 0;

    for (let i = startIndex; i < flatTargets.length; i++) {
      if (flatTargets[i].isEmpty) {
        return { pIndex: flatTargets[i].pIndex, mIndex: flatTargets[i].mIndex };
      }
    }

    for (let i = 0; i < startIndex; i++) {
      if (flatTargets[i].isEmpty) {
        return { pIndex: flatTargets[i].pIndex, mIndex: flatTargets[i].mIndex };
      }
    }

    return null;
  }

  private refreshRepertoryTargetLabel(): void {
    if (this.repertoryTargetPIndex === null || this.repertoryTargetMIndex === null) {
      this.repertoryTargetLabel = '';
      return;
    }

    const pres = this.prescription?.prescriptions?.[this.repertoryTargetPIndex];
    const rxTitle = String(pres?.title || '').trim();
    const rxLabel = rxTitle
      ? `Rx ${this.repertoryTargetPIndex + 1} · ${rxTitle}`
      : `Rx ${this.repertoryTargetPIndex + 1}`;
    this.repertoryTargetLabel = `${rxLabel} · Row ${this.repertoryTargetMIndex + 1}`;
  }

  private getMedicineRowTags(med: any): any[] {
    const raw = Array.isArray(med?.medicineTags) && med.medicineTags.length
      ? med.medicineTags
      : (Array.isArray(med?.tags) ? med.tags : []);

    const seen = new Set<string>();
    const unique: any[] = [];

    for (const tag of raw) {
      const name = String(tag?.name || '').trim();
      if (!name) {
        continue;
      }

      const key = [
        name.toLowerCase(),
        String(tag?.potency || '').trim().toLowerCase(),
        String(tag?.code || '').trim().toLowerCase(),
      ].join('|');

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      unique.push(tag);
    }

    return unique;
  }

  private isMedicineRowEmpty(med: any): boolean {
    return this.getMedicineRowTags(med).length === 0;
  }

  private getMedicineRowSummary(med: any): string {
    const names = this.getMedicineRowTags(med)
      .map((tag: any) => String(tag?.name || '').trim())
      .filter(Boolean);

    if (!names.length) {
      return 'Empty row';
    }

    const summary = names.join(', ');
    return summary.length > 28 ? `${summary.slice(0, 28)}…` : summary;
  }

  extractRepertorySeedSymptom(): string {
    const text = String(this.prescription?.chief_complaints || '').trim();
    if (!text) {
      return '';
    }

    const firstSegment = text.split(/[+,]/)[0]?.trim();
    if (firstSegment) {
      return firstSegment;
    }

    return text.split(/\s+/)[0] || '';
  }

  onRepertoryRemedySelected(remedy: RepertoryRemedy) {
    if (this.repertoryTargetPIndex === null || this.repertoryTargetMIndex === null) {
      return;
    }

    const tag = this.buildRepertoryMedicineTag(remedy);
    if (!tag.name) {
      return;
    }

    this.addMedicineTag(this.repertoryTargetPIndex, this.repertoryTargetMIndex, tag);
    this.repertorySessionCount += 1;

    if (this.repertorySessionCount === 1) {
      this.toast.success(
        'Repertory',
        `Adding to ${this.repertoryTargetLabel}. Use Insert into to switch rows.`,
      );
    }
  }

  getRepertoryPreferredPotency(): string {
    if (this.repertoryTargetPIndex === null || this.repertoryTargetMIndex === null) {
      return '';
    }

    const medicines = this.prescription?.prescriptions?.[this.repertoryTargetPIndex]?.medicines;
    const targetRow = medicines?.[this.repertoryTargetMIndex];
    const rowPotency = this.getMedicineRowPreferredPotency(targetRow);
    if (rowPotency) {
      return rowPotency;
    }

    for (const row of medicines || []) {
      const potency = this.getMedicineRowPreferredPotency(row);
      if (potency) {
        return potency;
      }
    }

    return '';
  }

  private getMedicineRowPreferredPotency(row: any): string {
    for (const tag of this.getMedicineRowTags(row)) {
      const potency = String(tag?.potency || '').trim();
      if (potency) {
        return potency;
      }
    }

    return '';
  }

  private buildRepertoryMedicineTag(remedy: RepertoryRemedy): Medicine {
    const fallbackName = (remedy.name || remedy.abbrev || '').trim();
    const selected = remedy.selected_clinic_match;

    if (selected) {
      return {
        id: selected.id,
        name: selected.name.trim(),
        code: selected.code,
        potency: selected.potency,
      };
    }

    return {
      id: null,
      name: fallbackName,
      code: '',
      potency: '',
    };
  }

  quickMedicineSearch(keyword: string) {
    this.medicineSearchKeyword = keyword;
    this.searchMedicineFinder(1);
  }

  clearMedicineSearch() {
    this.medicineSearchKeyword = '';
    this.medicineSearchResults = [];
    this.medicineSearchError = '';
    this.medicineSearchLoading = false;
    this.medicineSearchPage = 1;
    this.medicineSearchTotal = null;
    this.medicineSearchHasMore = false;
  }

  searchMedicineFinder(page: number = 1) {
    const keyword = this.medicineSearchKeyword.trim();
    this.medicineSearchError = '';

    if (!keyword) {
      this.medicineSearchResults = [];
      this.medicineSearchError = 'Enter a keyword to search.';
      this.medicineSearchPage = 1;
      this.medicineSearchTotal = null;
      this.medicineSearchHasMore = false;
      return;
    }

    this.medicineSearchLoading = true;

    this.diseaseSearchService
      .searchDiseases(keyword, page, this.medicineSearchPageSize)
      .pipe(finalize(() => (this.medicineSearchLoading = false)))
      .subscribe({
        next: (response) => {
          this.medicineSearchResults = response?.data ?? [];
          this.medicineSearchPage = response?.pagination?.page ?? page;
          this.medicineSearchTotal = response?.pagination?.total ?? null;
          this.medicineSearchHasMore = !!response?.pagination?.has_more;
        },
        error: () => {
          this.medicineSearchResults = [];
          this.medicineSearchError = 'Unable to load medicine search results.';
          this.medicineSearchTotal = null;
          this.medicineSearchHasMore = false;
        }
      });
  }

  goToPreviousMedicineSearchPage() {
    if (this.medicineSearchPage <= 1 || this.medicineSearchLoading) {
      return;
    }

    this.searchMedicineFinder(this.medicineSearchPage - 1);
  }

  goToNextMedicineSearchPage() {
    if (!this.medicineSearchHasMore || this.medicineSearchLoading) {
      return;
    }

    this.searchMedicineFinder(this.medicineSearchPage + 1);
  }

  get medicineSearchResultCount(): number {
    return this.medicineSearchResults.length;
  }

  get medicineSearchVisibleStart(): number {
    if (!this.medicineSearchResults.length) {
      return 0;
    }

    return ((this.medicineSearchPage - 1) * this.medicineSearchPageSize) + 1;
  }

  get medicineSearchVisibleEnd(): number {
    if (!this.medicineSearchResults.length) {
      return 0;
    }

    return this.medicineSearchVisibleStart + this.medicineSearchResults.length - 1;
  }

  get medicineSearchUniqueAuthors(): number {
    return new Set(
      this.medicineSearchResults
        .map((item) => item.author?.trim())
        .filter((value): value is string => !!value)
    ).size;
  }

  highlightMedicineSearchMatch(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const keyword = this.medicineSearchKeyword.trim();
    if (!keyword) {
      return value;
    }

    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(${escapedKeyword})`, 'gi');

    return value.replace(pattern, '<mark>$1</mark>');
  }


  refreshQueue() {
    if (this.isQueueRefreshing) return;
    this.loadQueue(true);
  }

  loadQueue(manualRefresh: boolean = false) {
    if (manualRefresh) {
      this.isQueueRefreshing = true;
    }

    const clinicId = sessionStorage.getItem('clinic_id') || '1';
    const doctorId = sessionStorage.getItem('doctor_id') || '1';

    // ✅ Check if clinic or doctor not selected
    if (!clinicId || !doctorId) {
      this.toast.add({
        severity: 'warn',
        summary: 'Missing Selection',
        detail: 'Select the clinic and doctor',
      });
      // return; // stop execution
    }

    const date = this.selectedDate || new Date(Date.now() + 19800000).toISOString().slice(0, 10);

    this.appointmentService.getQueue({
      clinic_id: clinicId,
      doctor_id: doctorId,
      date: date,
      // date: "2025-09-12",
      slot: 60
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          const appointments = res.data.slots.flatMap((slot: any) =>
            slot.appointments.map((appt: any) => ({
              appointment_id: appt.id ?? appt.appointment_id,
              patient_id: appt.patient_id,
              clinic_id: appt.clinic_id,
              doctor_id: appt.doctor_id,
              time: appt.start_time,
              status: appt.status,
              collect_later: Boolean(appt.collect_later ?? false),
              is_courier: this.normalizePrescriptionMode(appt.is_courier),
              appointment_type: this.normalizeAppointmentType(appt.appointment_type),
              created_at: appt.created_at,
              raw_time: appt.created_at || appt.start_time
            }))
          );

          // Collect all patient IDs
          const patientIds = appointments.map((a: any) => a.patient_id);

          if (!patientIds.length) {
            this.queue = [];
            this.updateNextPatientIndex();
            if (manualRefresh) {
              this.isQueueRefreshing = false;
            }
            return;
          }

          // Fetch all patient data in one request
          this.patientRegistrationService.getPatientsBatch(patientIds)
            .subscribe({
              next: (patientRes: any) => {
                if (patientRes.success) {
                  const patientsMap = new Map();
                  patientRes.data.forEach((p: any) => patientsMap.set(p.id, p));

                  // Map appointments to patients
                  this.queue = appointments.map((appt: any) => {
                    const patient = patientsMap.get(appt.patient_id);
                    const displayName = this.buildPatientDisplayName(patient);
                    return {
                      name: displayName,
                      full_name: displayName,
                      time: this.formatTime(appt.time),
                      raw_time: appt.raw_time,
                      status: appt.status,
                      collect_later: Boolean(appt.collect_later ?? false),
                      primary_contact: patient?.primary_contact || '',
                      secondary_contact: patient?.secondary_contact || '',
                      is_free: !!patient?.is_free,
                      blood_group: patient?.blood_group || '',
                      age: patient?.age ?? '',
                      gender: patient?.gender || '',
                      appointment_id: appt.appointment_id,
                      clinic_id: appt.clinic_id,
                      doctor_id: appt.doctor_id,
                      is_courier: this.normalizePrescriptionMode(patient?.is_courier ?? appt.is_courier),
                      appointment_type: appt.appointment_type
                    };
                  });

                  // Sort by time once
                  this.queue.sort((a, b) => a.time.localeCompare(b.time));
                  this.updateNextPatientIndex();
                  // console.log('Loaded queue:', this.queue);
                }
                if (manualRefresh) {
                  this.isQueueRefreshing = false;
                }
              },
              error: () => {
                if (manualRefresh) {
                  this.isQueueRefreshing = false;
                }
              }
            });
        }
        if (manualRefresh && !res?.success) {
          this.isQueueRefreshing = false;
        }
      },
      error: () => {
        if (manualRefresh) {
          this.isQueueRefreshing = false;
        }
      }
    });
  }

  private readonly nextQueuePriority: string[] = [
    'scheduled',
  ];

  getWaitTimeLabel(createdAt: string): string {
    if (!createdAt) return '';
    const now = new Date();

    // Check if it looks like a time-only string (fallback)
    if (createdAt.length <= 8 && createdAt.includes(':')) {
      const timeParts = createdAt.split(':');
      if (timeParts.length < 2) return '';
      const aptTime = new Date();
      aptTime.setHours(parseInt(timeParts[0], 10), parseInt(timeParts[1], 10), 0, 0);
      createdAt = aptTime.toISOString();
    }

    const aptTime = new Date(createdAt);
    if (isNaN(aptTime.getTime())) return '';

    const diffMs = now.getTime() - aptTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 0) {
      return `In ${Math.abs(diffMins)}m`;
    }
    if (diffMins === 0) {
      return 'Just now';
    }
    if (diffMins < 60) {
      return `Waiting ${diffMins}m`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `Waiting ${hours}h ${mins}m`;
  }

  private updateNextPatientIndex() {
    this.nextPatientIndex = -1;
    if (!Array.isArray(this.queue) || this.queue.length === 0) return;

    const waitingQueue = this.queue.filter(
      (item: any) =>
        this.isQueueItemVisible(item) &&
        item?.status !== 'doctor' &&
        item?.status !== 'assistant_doctor_done'
    );
    if (waitingQueue.length === 0) return;

    for (const status of this.nextQueuePriority) {
      const candidate = waitingQueue.find((item: any) => item?.status === status);
      if (candidate) {
        this.nextPatientIndex = this.queue.findIndex(
          (item: any) => item?.appointment_id === candidate?.appointment_id
        );
        return;
      }
    }

    this.nextPatientIndex = this.queue.findIndex(
      (item: any) => item?.appointment_id === waitingQueue[0]?.appointment_id
    );
  }

  isNextPatient(index: number): boolean {
    return index === this.nextPatientIndex;
  }

  get nowServingIndex(): number {
    return this.queue.findIndex(item => item.status === 'doctor');
  }

  get nowServingPatient(): any {
    const idx = this.nowServingIndex;
    if (idx !== -1 && this.isQueueItemVisible(this.queue[idx])) {
      return { ...this.queue[idx], index: idx };
    }
    return null;
  }

  getModernStatusColorClass(item: any): string {
    const s = String(item.status || '').toLowerCase();

    // Exact mapping for every status
    if (s === 'scheduled' || s === 'pending') return 'border-blue';
    if (s === 'assistant_doctor') return 'border-purple';
    if (s === 'assistant_doctor_done') return 'border-teal';
    if (s === 'doctor') return 'border-green';
    if (s === 'medicine' || s === 'medicine_scheduled') return 'border-orange';
    if (s === 'reception' || s === 'receptionist') return 'border-cyan';
    if (s === 'completed') return 'border-gray';

    return 'border-gray';
  }

  getWaitTimeColorClass(label: string): string {
    if (!label) return 'text-muted';
    if (label.includes('In ') || label.includes('Just now')) return 'text-muted';

    const matchMins = label.match(/Waiting (\d+)m/);
    const matchHours = label.match(/Waiting (\d+)h/);

    if (matchHours) return 'text-danger fw-bolder'; // 1h+
    if (matchMins) {
      const mins = parseInt(matchMins[1], 10);
      if (mins < 15) return 'text-muted'; // neutral
      if (mins < 30) return 'text-warning fw-semibold'; // amber
      return 'text-danger fw-semibold'; // red
    }
    return 'text-muted';
  }

  getQueueLifecycleLabel(item: any): string {
    const s = String(item?.status || '').toLowerCase();
    if (s === 'scheduled' || s === 'pending') return 'Scheduled';
    if (s === 'assistant_doctor') return 'Asst. Dr. Consulting';
    if (s === 'assistant_doctor_done') return 'Assistant Doctor Done';
    if (s === 'doctor') return 'In Consultation';
    if (s === 'medicine' || s === 'medicine_scheduled') return 'Pharmacy';
    if (s === 'reception' || s === 'receptionist') return 'Reception';
    if (s === 'completed') return 'Completed';
    return 'Waiting';
  }

  private normalizeAppointmentType(type: any): 'new' | 'follow_up' | 'case' | 'other' {
    const normalized = String(type || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (normalized === 'new') return 'new';
    if (normalized === 'follow_up') return 'follow_up';
    if (normalized === 'case') return 'case';
    return 'other';
  }

  setQueueTypeFilter(type: 'all' | 'new' | 'follow_up') {
    this.queueTypeFilter = type;
    this.updateNextPatientIndex();
  }

  setQueueModeFilter(mode: 'all' | 'OPD' | 'COURIER' | 'NRI' | 'MC') {
    this.queueModeFilter = mode;
    this.updateNextPatientIndex();
  }

  get queueNewCount(): number {
    return this.queue.filter((item: any) => this.normalizeAppointmentType(item?.appointment_type) === 'new').length;
  }

  get queueFollowUpCount(): number {
    return this.queue.filter((item: any) => this.normalizeAppointmentType(item?.appointment_type) === 'follow_up').length;
  }

  get queueOpdCount(): number {
    return this.queue.filter((item: any) => this.normalizePrescriptionMode(item?.is_courier) === 'OPD').length;
  }

  get queueCourierCount(): number {
    return this.queue.filter((item: any) => this.normalizePrescriptionMode(item?.is_courier) === 'COURIER').length;
  }

  get queueNriCount(): number {
    return this.queue.filter((item: any) => this.normalizePrescriptionMode(item?.is_courier) === 'NRI').length;
  }

  get queueMedicineCollectionCount(): number {
    return this.queue.filter((item: any) => this.isMedicineCollectionItem(item)).length;
  }

  isQueueItemVisible(item: any): boolean {
    const s = String(item?.status || '').toLowerCase();

    // if (s === 'scheduled' || s === 'pending') {
    //   return false;
    // }

    // Removing scheduled/pending block so these patients show up in the queue

    const type = this.normalizeAppointmentType(item?.appointment_type);
    const mode = this.normalizePrescriptionMode(item?.is_courier);

    const typeMatches =
      this.queueTypeFilter === 'new'
        ? type === 'new'
        : this.queueTypeFilter === 'follow_up'
          ? type === 'follow_up'
          : true;

    const modeMatches =
      this.queueModeFilter === 'all'
        ? true
        : this.queueModeFilter === 'MC'
          ? this.isMedicineCollectionItem(item)
          : mode === this.queueModeFilter;

    return typeMatches && modeMatches;
  }

  getAppointmentTypeLabel(type: any): string {
    const normalized = this.normalizeAppointmentType(type);
    if (normalized === 'new') return 'New';
    if (normalized === 'follow_up') return 'Follow Up';
    if (normalized === 'case') return 'Case';
    return 'Other';
  }

  getAppointmentTypeClass(type: any): string {
    const normalized = this.normalizeAppointmentType(type);
    if (normalized === 'new') return 'apt-new';
    if (normalized === 'follow_up') return 'apt-followup';
    if (normalized === 'case') return 'apt-case';
    return 'apt-default';
  }

  focusQueueItem() {
    setTimeout(() => {
      const items = document.querySelectorAll(
        '.patient-section .list-group-item'
      ) as NodeListOf<HTMLButtonElement>;

      items[this.queueIndex]?.focus();
    });
  }


  moveDown() {
    if (this.queueIndex < this.queue.length - 1) {
      this.queueIndex++;
      this.focusQueueItem();
    }
  }

  moveUp() {
    if (this.queueIndex > 0) {
      this.queueIndex--;
      this.focusQueueItem();
    }
  }


  onQueueKeydown(event: KeyboardEvent, index: number) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        // event.stopPropagation();
        this.moveDown();
        break;

      case 'ArrowUp':
        event.preventDefault();
        // event.stopPropagation();
        this.moveUp();
        break;

      case 'Enter':
        event.preventDefault();
        // event.stopPropagation();
        this.selectPatientByIndex(index);
        break;
    }
  }


  selectPatientByIndex(index: number) {
    this.queueIndex = index;

    const patient = this.queue[index];
    if (patient) {
      this.selectPatient(patient); // existing method
    }
  }


  selectPatient(item: any) {
    const appointmentId = Number(item?.appointment_id ?? item?.id ?? 0);
    if (!appointmentId) {
      this.toast.add({
        severity: 'error',
        summary: 'Queue Data Error',
        detail: 'Appointment id is missing for the selected queue item.'
      });
      return;
    }

    const skipDraftHydration = item?.fromRecall === true;

    // Save current context before switching to another patient from queue.
    if (!skipDraftHydration) {
      this.saveDraftSnapshot(true);
    }

    this.clearReportAnalysisPolling();
    this.reportsDocFilter = 'all';
    this.reportsDocSearch = '';
    this.resetImageCompareState();
    this.uploadsByDate = [];
    this.reportsMeta = { total: 0, limit: 40, offset: 0, hasMore: false };
    this.syncReportsViewModel();
    this.isDraftAutosaveSuspended = true;

    // console.log('Selected patient:', item);
    // 1. Auto-move to doctor only for normal queue flow.
    // For pharmacy/reception statuses, keep explicit "Recall from Pharmacy" action.
    const currentStatus = String(item?.status || '').toLowerCase();
    const requiresExplicitRecall = ['medicine', 'reception', 'medicine_scheduled'].includes(currentStatus);
    if (!requiresExplicitRecall && item?.fromRecall !== true) {
      this.appointmentService.changeStatus(appointmentId, { status: 'doctor' }).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.loadQueue(); // Refresh the queue to reflect status change
          }
        },
        error: (err) => {
          console.error('Error changing status:', err);
        }
      });
    }

    // 2. Load patient cards

    // 3. Hide patient selector (queue popup)
    this.patientSelectedFromSearch = false;
    this.isPatientVisible = false;
    this.refreshDoctorNotes([]);
    if (item?.fromRecall !== true) {
      this.recalledFromPharmacy = false;
    }

    // 4. Fetch full appointment details (patient + prescriptions)
    this.appointmentService.getAppointmentDetails(appointmentId).subscribe((res: any) => {
      // console.log('Full appointment details:', res);

      if (res.success) {
        const appt = res.data;
        this.patientSelectionConfirmed = true;
        this.currentAppointmentStatus = (appt?.status || '').toString();
        this.currentAppointmentDate = (appt?.appointment_date || '').toString();
        const patient = appt.patient;
        const prescription = this.getLatestPrescription(appt?.prescriptions);
        const rawPatientVisitFees = patient?.visit_fees;
        const patientVisitFees = Number(rawPatientVisitFees ?? 0) || 0;
        const rawPrescriptionFees = prescription?.visit_fees;
        const parsedPrescriptionFees = Number(rawPrescriptionFees);
        const hasSavedPrescriptionFees =
          rawPrescriptionFees !== null &&
          rawPrescriptionFees !== undefined &&
          !Number.isNaN(parsedPrescriptionFees) &&
          (parsedPrescriptionFees > 0 || !!patient?.is_free);
        const resolvedVisitFees = hasSavedPrescriptionFees
          ? parsedPrescriptionFees
          : patientVisitFees;

        // Patient form
        this.patient = {
          patient_id: patient.id,
          first_name: patient.first_name || '',
          middle_name: patient.middle_name || '',
          last_name: patient.last_name || '',
          primary_contact: patient.primary_contact || '',
          secondary_contact: patient.secondary_contact || '',
          age: patient.age || '',
          gender: patient.gender || '',
          blood_group: patient.blood_group || '',
          patient_number: patient.patient_number || '',
          address: patient.address || '',
          city: patient.city || '',
          state: patient.state || '',
          country: patient.country || '',
          pincode: patient.pincode || '',
          status: appt.status || 'Checkup',
          is_free: patient.is_free || false,
          referred_by: patient.referred_by || '',
          visit_fees: patientVisitFees,
          first_visit: new Date(patient.first_visit).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
          is_courier: this.normalizePrescriptionMode(patient.is_courier)
        };

        // Clinic/doctor ids
        this.clinicId = appt.clinic_id || 0;
        this.doctorId = appt.doctor_id || 0;

        // Active prescription form
        this.prescription = {
          id: prescription.id,
          appointment_id: appt.id,
          allergies: prescription.allergies || '',
          current_medications: prescription.current_medications || '',
          personal_family_history: prescription.personal_family_history || '',
          surgical_history: prescription.surgical_history || '',
          blood_pressure: prescription.blood_pressure || '',
          pulse_rate: prescription.pulse_rate || '',
          temperature: prescription.temperature || '',
          weight: prescription.weight || '',
          height: prescription.height || '',
          fasting_blood_sugar: prescription.fasting_blood_sugar || '',
          blood_oxygen: prescription.blood_oxygen || '',
          runny_nose: prescription.runny_nose || '',
          cough: prescription.cough || '',

          chief_complaints: this.normalizeComplaintTextarea(prescription.chief_complaints),

          prescriptions: this.normalizePrescriptionRows(prescription.prescriptions),

          personal_dues: prescription.personal_dues || 0,
          visit_fees: resolvedVisitFees,
          received_cash: prescription.received_cash || 0,
          received_online: prescription.received_online || 0,
          special_instructions: prescription.special_instructions || prescription.special_instruction || '',
          notes: prescription.notes || prescription.doctor_notes || '',
          is_courier: this.resolvePrescriptionMode(
            item?.is_courier,
            patient.is_courier,
            prescription.is_courier
          ),
        };
        this.applyClinicalTextCapitalization();
        this.patient.is_courier = this.prescription.is_courier;

        // Fallback: queue appointment details can miss patient.visit_fees
        // for assistant-doctor flow. Pull master patient data when missing.
        if (rawPatientVisitFees === null || rawPatientVisitFees === undefined) {
          this.patientRegistrationService.getSinglePatient(patient.id).subscribe((patientRes: any) => {
            if (!patientRes?.success || !patientRes?.data) return;

            const masterVisitFees = Number(patientRes.data.visit_fees ?? 0) || 0;
            this.patient.visit_fees = masterVisitFees;

            // Respect saved prescription fees if already present.
            if (!hasSavedPrescriptionFees) {
              this.prescription.visit_fees = masterVisitFees;
            }
          });
        }

        this.diagnosisTags = this.normalizeDiagnosisTags(prescription.diagnosis);
        this.reportTags = this.normalizeReportTags(prescription.suggested_reports);
        this.selectedIcdCodes = [];

        // 5. Load only latest history page for context; full history is lazy in History tab.
        this.resetHistoryStateForPatient(this.patient.patient_id);
        this.prescriptionsService.getPrescriptionsByPatientId(this.patient.patient_id, {
          page: 1,
          per_page: this.historyPageSize
        })
          .subscribe((res: any) => {
            const records = this.extractHistoryRecords(res);
            this.filteredRecords = records;
            this.historyCurrentPage = this.extractHistoryPage(res) || (records.length ? 1 : 0);
            this.historyTotalRecords = this.extractHistoryTotal(res, records.length);
            this.historyHasMore = this.extractHistoryHasMore(res, records.length);
            this.historyInitializedForPatientId = Number(this.patient.patient_id) || 0;
            this.selectedHistoryIndex = 0;

            if (records.length > 0) {
              // Fallback: if appointment-level prescription is sparse, hydrate from latest history.
              this.hydrateFromLatestHistoryIfCurrentIsEmpty(records);
              this.prescription.personal_dues = this.calculatePersonalDues(records, this.prescription);
              this.refreshDoctorNotes(records);
            } else {
              this.prescription.personal_dues = this.calculatePersonalDues([], this.prescription);
              this.refreshDoctorNotes([]);
            }

            // Queue flow should restore local drafts if the doctor had actively typed one!
            // The previous bug where empty states overwrote data is now fixed.
            this.restoreDraftForCurrentContext(true, true, Number(this.patient.patient_id || 0));
            this.prescription.personal_dues = this.calculatePersonalDues(this.filteredRecords, this.prescription);
          });

        //6. ✅ Fetch reports for this patient
        this.refreshPatientReports();
      }
    }, () => {
      this.isDraftAutosaveSuspended = false;
      this.toast.add({
        severity: 'error',
        summary: 'Load Failed',
        detail: 'Unable to load appointment details for selected patient.'
      });
    });
  }

  getColorClass(status: string): string {
    switch (status) {
      case 'scheduled':
        return 'bg-light-blue';
      case 'assistant_doctor':
        return 'bg-light-purple';
      case 'assistant_doctor_done':
        return 'bg-light-purple';
      case 'doctor':
        return 'bg-light-coral';
      case 'medicine':
        return 'bg-light-teal';
      case 'completed':
        return 'bg-light-green';
      case 'cancelled':
        return 'bg-light-red';
      default:
        return 'bg-light-gray'; // fallback for unknown statuses
    }
  }

  getClinic() {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicsList = Array.isArray(res.data)
          ? res.data.map((clinic: any) => ({
            label: clinic.name,
            value: clinic.id
          }))
          : [];
      },
      error: (err) => {
        console.error('Clinic API error:', err);
        this.clinicsList = [];
      }
    });
  }

  getDoctors() {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor: any) => ({
            label: doctor.name,
            value: doctor.id
          }))
          : [];
      },
      error: (err) => {
        console.error('Doctor API error:', err);
        this.doctorsList = [];
      }
    });
  }

  // onFileUpload(event: any) {
  //   const files = event.target.files;
  //   const today = new Date();

  //   let group = this.uploadsByDate.find(
  //     g => g.date.toDateString() === today.toDateString()
  //   );

  //   if (!group) {
  //     group = { date: today, files: [] };
  //     this.uploadsByDate.unshift(group); // latest date always at top
  //   }

  //   for (let file of files) {
  //     const fileObj = {
  //       name: file.name,
  //       type: file.type,
  //       url: URL.createObjectURL(file)
  //     };
  //     group.files.push(fileObj);
  //   }
  // }

  // 2025-10-18
  // onFileUpload(event: any) {
  //   this.pendingFiles = Array.from(event.target.files);
  // }

  imagePreviews: any[] = [];
  onFileUpload(event: any) {
    this.pendingFiles = Array.from(event.target.files);

    // Clear previous previews
    this.imagePreviews = [];

    // Generate previews for the selected files
    this.pendingFiles.forEach((file: File) => {
      // Check if it's an image
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();

        reader.onload = (e: any) => {
          this.imagePreviews.push({
            type: 'image',
            url: e.target.result,
            name: file.name
          });
        };

        reader.readAsDataURL(file);
      } else {
        // For non-image files, just store the name
        this.imagePreviews.push({
          type: 'document',
          url: null,
          name: file.name
        });
      }
    });
  }

  // Merge new reports into gallery
  private mergeReports(newReports: any[]) {
    newReports.forEach(r => {
      const date = r.uploaded_at.split(' ')[0];
      let group = this.uploadsByDate.find(g => g.date === date);
      if (!group) {
        group = { date, files: [] };
        this.uploadsByDate.push(group);
      }
      group.files.push(r);
    });
  }

  private normalizePrescriptionForEdit(rx: any) {
    const medicines = (rx?.medicines || []).map((m: any) => {
      const tags = (m?.tags || m?.medicineTags || []).map((t: any) => ({
        id: t?.id ?? null,
        name: t?.name ?? '',
        code: t?.code ?? '',
        potency: t?.potency ?? ''
      })).filter((t: any) => t.name || t.code || t.potency);

      return {
        tags,
        medicineTags: tags,
        dose: m?.dose ?? '',
        drams: m?.drams ?? '',
        ballers: m?.ballers ?? '',
        medicineInput: '',
        filteredMedicines: [],
        filteredDoses: [],
        unresolvedMedicines: [],
        resolvedMedicines: [],
        unresolvedHints: []
      };
    });

    return {
      id: 1,
      title: rx?.title ?? '',
      duration: rx?.duration ?? '',
      medicines: medicines.length
        ? medicines
        : [{
          tags: [],
          medicineTags: [],
          dose: '',
          drams: '',
          ballers: '',
          medicineInput: '',
          filteredMedicines: [],
          filteredDoses: []
        }]
    };
  }

  get selectedHistoryRecord(): any | null {
    if (!Array.isArray(this.filteredRecords) || this.filteredRecords.length === 0) return null;
    const boundedIndex = Math.max(0, Math.min(this.selectedHistoryIndex, this.filteredRecords.length - 1));
    return this.filteredRecords[boundedIndex] || null;
  }

  onMainTabChange(event: any) {
    const index = Number(event?.index ?? this.activeIndex ?? 0);
    this.activeIndex = index;
    if (index === this.historyTabIndex) {
      this.ensureHistoryLoadedForCurrentPatient();
    }
    if (index === this.reportsTabIndex) {
      this.syncReportsViewModel();
    }
  }

  selectHistoryRecord(index: number) {
    if (!Array.isArray(this.filteredRecords) || this.filteredRecords.length === 0) {
      this.selectedHistoryIndex = 0;
      return;
    }

    this.selectedHistoryIndex = Math.max(0, Math.min(index, this.filteredRecords.length - 1));
  }

  refreshHistory() {
    this.loadHistoryPage(1, false);
  }

  loadMoreHistory() {
    if (this.historyLoading || this.historyLoadingMore || !this.historyHasMore) return;
    this.loadHistoryPage(Math.max(1, this.historyCurrentPage + 1), true);
  }

  private ensureHistoryLoadedForCurrentPatient() {
    const patientId = Number(this.patient?.patient_id) || 0;
    if (!patientId) return;

    if (this.historyInitializedForPatientId === patientId && this.filteredRecords.length > 0) {
      return;
    }

    this.loadHistoryPage(1, false);
  }

  private loadHistoryPage(page: number, append: boolean) {
    const patientId = Number(this.patient?.patient_id) || 0;
    if (!patientId) return;

    const requestToken = ++this.activeHistoryRequestToken;
    this.historyError = '';
    if (append) {
      this.historyLoadingMore = true;
    } else {
      this.historyLoading = true;
    }

    this.prescriptionsService.getPrescriptionsByPatientId(patientId, {
      page,
      per_page: this.historyPageSize
    }).subscribe({
      next: (res: any) => {
        if (requestToken !== this.activeHistoryRequestToken) return;

        const pageRecords = this.extractHistoryRecords(res);
        this.filteredRecords = append ? [...this.filteredRecords, ...pageRecords] : pageRecords;
        this.historyCurrentPage = this.extractHistoryPage(res) || page;
        this.historyTotalRecords = this.extractHistoryTotal(res, this.filteredRecords.length);
        this.historyHasMore = this.extractHistoryHasMore(res, pageRecords.length);
        this.historyInitializedForPatientId = patientId;

        if (!append || this.selectedHistoryIndex >= this.filteredRecords.length) {
          this.selectedHistoryIndex = 0;
        }
      },
      error: () => {
        if (requestToken !== this.activeHistoryRequestToken) return;
        this.historyError = 'Unable to load patient history right now.';
      },
      complete: () => {
        if (requestToken !== this.activeHistoryRequestToken) return;
        this.historyLoading = false;
        this.historyLoadingMore = false;
      }
    });
  }

  private resetHistoryStateForPatient(patientId: number) {
    this.activeHistoryRequestToken++;
    this.filteredRecords = [];
    this.selectedHistoryIndex = 0;
    this.historyCurrentPage = 0;
    this.historyTotalRecords = 0;
    this.historyHasMore = false;
    this.historyLoading = false;
    this.historyLoadingMore = false;
    this.historyError = '';
    this.historyInitializedForPatientId = Number(patientId) || 0;
  }

  private extractHistoryRecords(res: any): any[] {
    const rows = Array.isArray(res?.data) ? res.data : [];
    return rows.map((record: any) => ({
      ...record,
      chief_complaints: record?.chief_complaints,
      suggested_reports: this.safeParse(record?.suggested_reports)
    }));
  }

  private extractHistoryPage(res: any): number {
    return Number(res?.pagination?.page || 0) || 0;
  }

  private extractHistoryTotal(res: any, fallback: number): number {
    const total = Number(res?.pagination?.total ?? 0);
    if (Number.isFinite(total) && total > 0) return total;
    return fallback;
  }

  private extractHistoryHasMore(res: any, latestChunkLength: number): boolean {
    if (typeof res?.pagination?.has_more === 'boolean') {
      return res.pagination.has_more;
    }

    return latestChunkLength >= this.historyPageSize;
  }

  loadHistoryPrescription(record: any, historyRx: any) {
    const normalizedRx = this.normalizePrescriptionForEdit(historyRx);
    (normalizedRx as any).is_from_history = true;
    if (!Array.isArray(this.prescription.prescriptions)) {
      this.prescription.prescriptions = [];
    }
    this.prescription.prescriptions.push(normalizedRx);

    this.activeIndex = 0;
  }

  loadSelectedHistoryRxs() {
    let rxAdded = false;

    if (!Array.isArray(this.prescription.prescriptions)) {
      this.prescription.prescriptions = [];
    }

    if (this.filteredRecords && this.filteredRecords.length > 0) {
      this.filteredRecords.forEach(record => {
        if (record.prescriptions) {
          record.prescriptions.forEach((prescription: any) => {
            if (prescription.selected) {
              const normalizedRx = this.normalizePrescriptionForEdit(prescription);
              (normalizedRx as any).is_from_history = true;
              this.prescription.prescriptions.push(normalizedRx);

              rxAdded = true;
              prescription.selected = false;
            }
          });
        }
      });
    }

    if (rxAdded) {
      this.activeIndex = 0; // Go to Patient Tab
      this.toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Selected prescriptions loaded.'
      });
    }
  }

  get selectedRxCount(): number {
    let count = 0;
    if (this.filteredRecords && this.filteredRecords.length > 0) {
      this.filteredRecords.forEach(record => {
        if (record.prescriptions) {
          count += record.prescriptions.filter((p: any) => p.selected).length;
        }
      });
    }
    return count;
  }

  hasSelectedRx(record: any): boolean {
    if (!record || !record.prescriptions) return false;
    return record.prescriptions.some((p: any) => p.selected);
  }

  scrollToTop() {
    // Find the scrollable parent container
    const scrollContainer = document.querySelector('.history-container') ||
      document.querySelector('.tab-content') ||
      document.querySelector('p-tabPanel') ||
      document.querySelector('.p-tabview-panel');

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  scrollToRecord(index: number) {
    const el = document.getElementById('record-' + index);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  scrollToToday() {
    // Assuming latest record is at index 0 after reverse
    const el = document.getElementById('record-0');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  trackByHistoryIndex(index: number): number {
    return index;
  }

  trackByHistoryRecord(index: number, record: any): number {
    return Number(record?.id) || index;
  }

  saveAppointmentAndPrescription() {
    const now = new Date();
    const appointmentDate = now.toISOString().slice(0, 10);
    const hour = now.getHours().toString().padStart(2, '0');
    const startTime = `${hour}:00`;
    const endTime = `${(now.getHours() + 1).toString().padStart(2, '0')}:00`;

    const clinicId = sessionStorage.getItem('clinic_id');
    const doctorId = sessionStorage.getItem('doctor_id');

    // Appointment payload
    const appointmentPayload = {
      clinic_id: Number(clinicId) || 1,
      doctor_id: Number(doctorId) || 1,
      // clinic_id: this.clinicId,
      // doctor_id: this.doctorId,
      patient_id: this.patient.patient_id,
      appointment_date: appointmentDate,
      appointment_type: this.resolveSearchAppointmentType(),
      start_time: startTime,
      end_time: endTime,
      status: 'medicine',
      type: 'on_flow',
    };

    this.appointmentService.addAppointment(appointmentPayload).subscribe({
      next: (res: any) => {
        if (res.success) {
          const appt = res.data;
          const prescription = this.prescription;

          // 🔹 Transform prescriptions before sending
          const transformedPrescriptions = (prescription.prescriptions || []).map((p: any) => ({
            id: p.id || null,
            title: p.title || '',
            duration: p.duration || '',
            medicines: (p.medicines || []).map((m: any) => ({
              drams: m.drams || '',
              ballers: m.ballers || '',
              // 🔹 Flatten tags into structure the backend expects
              tags: (m.tags || []).map((t: any) => ({
                id: t.id ?? null,
                name: t.name ?? '',
                code: t.code ?? '',
                potency: t.potency ?? '',
              })),
            })),
          }));

          // Now create prescription linked to this appointment
          const prescriptionPayload = {
            appointment_id: appt.id,
            allergies: prescription.allergies || '',
            current_medications: prescription.current_medications || '',
            personal_family_history: prescription.personal_family_history || '',
            surgical_history: prescription.surgical_history || '',
            blood_pressure: prescription.blood_pressure || '',
            pulse_rate: prescription.pulse_rate || '',
            temperature: prescription.temperature || '',
            weight: prescription.weight || '',
            height: prescription.height || '',
            fasting_blood_sugar: prescription.fasting_blood_sugar || '',
            blood_oxygen: prescription.blood_oxygen || '',
            runny_nose: prescription.runny_nose || '',
            cough: prescription.cough || '',

            chief_complaints: this.prescription.chief_complaints || '',
            diagnosis: this.diagnosisTags.map((tag: any) => tag.diagnosis || tag),
            suggested_reports: this.reportTags.map((tag: any) => tag.report),

            // prescriptions: prescription.prescriptions || [],
            prescriptions: transformedPrescriptions || [],

            personal_dues: prescription.personal_dues || 0,
            visit_fees: prescription.visit_fees || 0,
            received_cash: prescription.received_cash || 0,
            received_online: prescription.received_online || 0,

            notes: prescription.notes || '',
            is_courier: this.normalizePrescriptionMode(prescription.is_courier),
            special_instructions: prescription.special_instructions || '',
          };

          this.prescriptionsService.addPrescriptions(prescriptionPayload).subscribe((presRes: any) => {
            if (presRes.success) {
              const prescription = presRes.data;

              // ✅ Upload reports if there are any files waiting
              if (this.pendingFiles && this.pendingFiles.length > 0) {
                const formData = new FormData();
                formData.append('patient_id', this.patient.patient_id.toString());
                formData.append('prescription_id', prescription.id.toString());
                this.pendingFiles.forEach((file: File) => {
                  formData.append('files[]', file, file.name);
                });

                this.prescriptionsService.uploadReports(formData).subscribe({
                  next: (uploadRes: any) => {
                    if (uploadRes.success) {
                      this.pendingFiles = [];
                      this.refreshPatientReports();
                      this.autoAnalyzeUploadedReports(uploadRes.data || []);
                    }
                  },
                  error: (err) => console.error('Report upload failed:', err)
                });
              }

              this.toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Appointment & Prescription saved successfully',
              });

              this.clearForm();
            }
          });

          // this.clearForm();
        }
      },
      error: (err) => {
        console.error('Save error:', err);
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save appointment and prescription',
        });
      }
    });
  }

  calculatePersonalDues(records: any[], current: any): number {
    const toNumber = (value: any): number => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };

    // Exclude the currently loaded prescription (if present in history)
    // to prevent double-counting while editing an existing appointment.
    const currentPrescriptionId = toNumber(current?.id);
    const previousRecords = Array.isArray(records)
      ? records.filter((r: any) => toNumber(r?.id) !== currentPrescriptionId)
      : [];

    let lastDue = 0;
    if (previousRecords.length > 0) {
      const latestRecord = [...previousRecords].sort((a: any, b: any) => {
        const timeA = new Date(a?.created_at ?? a?.visit_date ?? 0).getTime();
        const timeB = new Date(b?.created_at ?? b?.visit_date ?? 0).getTime();
        if (timeA !== timeB) return timeB - timeA;
        return toNumber(b?.id) - toNumber(a?.id);
      })[0];
      lastDue = Math.max(0, toNumber(latestRecord?.personal_dues));
    }

    const visitFees = Math.max(0, toNumber(current?.visit_fees));
    const receivedCash = Math.max(0, toNumber(current?.received_cash));
    const receivedOnline = Math.max(0, toNumber(current?.received_online));

    return Math.max(0, (visitFees - (receivedCash + receivedOnline)) + lastDue);
  }

  getDoses() {
    this.doseService.getDose().subscribe((res: any) => {
      // Convert all to lowercase for consistent search
      this.allDoses = res.data;
      console.log(this.allDoses);
    });
  }

  getChiefComplaints() {
    this.chiefComplaintService.getChiefComplaint().subscribe((res: any) => {
      // Convert all to lowercase for consistent search
      this.allComplaints = res.data;
      // console.log(this.allComplaints);
    });
  }

  getDiagnoses() {
    this.diagnosisService.getDiagnosis().subscribe((res: any) => {
      this.allDiagnoses = res.data || [];
    });
  }

  getSuggestedReports() {
    this.suggestedReportService.getSuggestedReport().subscribe((res: any) => {
      this.allReports = res.data;
      // console.log(this.allReports);
    });
  }

  getMedicines() {
    this.medicineService.getMedicine().subscribe((res: any) => {
      this.allMedicines = res.data || [];
      // @ts-ignore
      this.buildMedicineIndex(this.allMedicines);
      // console.log(this.allMedicines);
    });
  }

  private normalizeCode(code: string): string {
    const match = code.toUpperCase().match(/^([A-Z]+)0*(\d+)$/);
    if (!match) return code.toUpperCase();
    return `${match[1]}${match[2]}`;
  }

  buildMedicineIndex(medicines: Medicine[]) {
    this.medicineByCode.clear();
    this.medicinesByName.clear();

    for (const med of medicines) {
      // ✅ NORMALIZED CODE INDEX
      const normalizedCode = this.normalizeCode(med.code);
      this.medicineByCode.set(normalizedCode, med);

      // (Optional) name index — keep for later suggestions
      const nameKey = med.name.toLowerCase();

      if (!this.medicinesByName.has(nameKey)) {
        this.medicinesByName.set(nameKey, []);
      }
      this.medicinesByName.get(nameKey)!.push(med);
    }
  }


  // buildMedicineIndex(medicines: Medicine[]) {
  //   this.medicineByCode.clear();
  //   this.medicinesByName.clear();

  //   for (const med of medicines) {
  //     // Code index (fastest + safest)
  //     this.medicineByCode.set(med.code.toLowerCase(), med);

  //     // Name index (for fuzzy fallback)
  //     const key = med.name.toLowerCase();

  //     if (!this.medicinesByName.has(key)) {
  //       this.medicinesByName.set(key, []);
  //     }
  //     this.medicinesByName.get(key)!.push(med);
  //   }
  // }


  /**
   * Called when the AI copilot emits a follow-up question via (addNote).
   * Appends the question to the chief complaints textarea so the doctor
   * can use it as a clinical note/prompt.
   */
  appendToChiefComplaints(question: string): void {
    const current = (this.prescription.chief_complaints || '').trim();
    const separator = current.length > 0 ? '\n' : '';
    this.prescription.chief_complaints = `${current}${separator}• ${question}`;
    this.toast.add({
      severity: 'info',
      summary: 'Added to Notes',
      detail: 'Follow-up question added to chief complaints',
      life: 2500,
    });

    // Move cursor to the end of the text area
    setTimeout(() => {
      if (this.ccTextarea?.nativeElement) {
        const el = this.ccTextarea.nativeElement;
        el.focus();
        const length = el.value.length;
        el.setSelectionRange(length, length);
      }
    }, 0);
  }

  /**
   * Called when the AI copilot emits a suggested report via (addReport).
   * Adds the report name to the report tags (same flow as typing it manually).
   */
  addReportByName(name: string): void {
    if (!name?.trim()) return;
    const valueLower = name.trim().toLowerCase();
    const exists = this.reportTags.some(
      (r: any) => (typeof r === 'string' ? r : r.report || r.name || '').toLowerCase() === valueLower
    );
    if (!exists) {
      // Attempt to find in existing allReports list first
      const known = (this.allReports as any[]).find(
        (r: any) => (typeof r === 'string' ? r : r.report || r.name || '').toLowerCase() === valueLower
      );
      this.addReportTag(known ?? { report: name.trim() });
    }
    this.toast.add({
      severity: 'success',
      summary: 'Report Added',
      detail: `"${name}" added to suggested reports`,
      life: 2500,
    });
  }

  //! Chief Complaints Methods
  onComplaintInputChange() {
    const value = this.prescription.chief_complaints.trim().toLowerCase();
    this.complaintSuggestionIndex = -1;
    if (value.length < 1) {
      this.filteredComplaints = [];
      return;
    }

    this.filteredComplaints = this.allComplaints
      .filter((c: any) => {
        const text = (typeof c === 'string' ? c : c.chief_complaint)?.toLowerCase();
        const alreadySelected = this.complaintTags.some(
          (tag: any) => String(tag?.chief_complaint ?? tag ?? '').toLowerCase() === text
        );
        return text.startsWith(value) && !alreadySelected;
      })
      .slice(0, 5);

    // console.log(this.filteredComplaints);
  }

  // onComplaintEnter(event: any) {
  //   event.preventDefault();
  //   const value = this.prescription.chief_complaints.trim().toLowerCase();
  //   // if (value) {
  //   //   this.addComplaintTag(value);
  //   // }

  //   if (!value) return;

  //   // Check if already selected
  //   if (this.complaintTags.includes(value)) {
  //     this.prescription.chief_complaints = '';
  //     return;
  //   }

  //   if (!this.allComplaints.includes(value)) {
  //     // Call API to add new chief complaint
  //     this.chiefComplaintService.addChiefComplaint({ chief_complaint: value }).subscribe({
  //       next: () => {
  //         this.allComplaints.push(value); 
  //         this.addComplaintTag(value)
  //       },
  //       error: err => console.error('Error adding complaint', err)
  //     });
  //   }

  //   // this.addComplaintTag(value);
  // }

  onComplaintEnter(event: any) {
    event.preventDefault();
    const value = this.prescription.chief_complaints.trim().toLowerCase();
    if (!value) return;

    // Check if already selected (support both string/object)
    const exists = this.complaintTags.some(
      (c: any) => (typeof c === 'string' ? c : c.chief_complaint) === value
    );
    if (exists) {
      this.prescription.chief_complaints = '';
      return;
    }

    // Check if it exists in allComplaints
    const known = this.allComplaints.some(
      (c: any) => (typeof c === 'string' ? c : c.chief_complaint) === value
    );

    if (!known) {
      // Call API to add new chief complaint
      this.chiefComplaintService
        .addChiefComplaint({ chief_complaint: value })
        .subscribe({
          next: (res: any) => {
            const newComplaint = {
              id: res.data.id,
              chief_complaint: res.data.chief_complaint,
            };

            this.allComplaints.push(newComplaint.chief_complaint);
            this.addComplaintTag(newComplaint);
            this.getChiefComplaints();
          },
          error: err => console.error('Error adding complaint', err),
        });
    } else {
      this.addComplaintTag({ id: null, chief_complaint: value });
      this.getChiefComplaints();
    }
  }

  // addComplaintTag(tag: { id: number | null; chief_complaint: string }) {
  //   // if (!this.complaintTags.includes(tag)) {
  //   //   this.complaintTags.push(tag);
  //   //   if (!this.allComplaints.includes(tag)) this.allComplaints.push(tag);
  //   // }

  //   // Only add if not already in tags
  //   if (!this.complaintTags.includes(tag)) {
  //     this.complaintTags.push(tag);
  //   }
  //   this.prescription.chief_complaints = '';
  //   this.filteredComplaints = [];
  // }

  addComplaintTag(tag: any) {
    const exists = (this.complaintTags as any[]).some(
      (c: any) => (c.chief_complaint ?? c).toLowerCase() ===
        (tag.chief_complaint ?? tag).toLowerCase()
    );

    if (!exists) {
      (this.complaintTags as any[]).push(tag);
    }

    this.prescription.chief_complaints = '';
    this.filteredComplaints = [];
    this.complaintSuggestionIndex = -1;
  }

  removeComplaintTag(tag: any) {
    const target = String(tag?.chief_complaint ?? tag ?? '').toLowerCase();
    this.complaintTags = this.complaintTags.filter((t: any) =>
      String(t?.chief_complaint ?? t ?? '').toLowerCase() !== target
    );
  }

  //! Diagnosis Methods
  onDiagnosisInputChange() {
    const value = this.diagnosisInput.trim().toLowerCase();
    this.diagnosisSuggestionIndex = -1;
    if (value.length < 1) {
      this.filteredDiagnoses = [];
      return;
    }

    this.filteredDiagnoses = this.allDiagnoses
      .filter((d: any) => {
        const text = (typeof d === 'string' ? d : d.diagnosis || d.name)?.toLowerCase() || '';
        const alreadySelected = this.diagnosisTags.some(
          (tag: any) => String(tag?.diagnosis ?? tag?.name ?? tag ?? '').toLowerCase() === text
        );
        return text.includes(value) && !alreadySelected;
      })
      .slice(0, 5);
  }

  onDiagnosisEnter(event: any) {
    event.preventDefault();
    const value = this.diagnosisInput.trim();
    if (!value) return;
    const valueLower = value.toLowerCase();

    // Check if already selected
    const exists = this.diagnosisTags.some(
      (d: any) => (typeof d === 'string' ? d : d.diagnosis || d.name || '').toLowerCase() === valueLower
    );
    if (exists) {
      this.diagnosisInput = '';
      return;
    }

    // Check if it exists in allDiagnoses
    const known = this.allDiagnoses.find(
      (d: any) => (typeof d === 'string' ? d : d.diagnosis || d.name || '').toLowerCase() === valueLower
    );

    if (!known) {
      // Call API to add new diagnosis
      this.diagnosisService
        .addDiagnosis({ diagnosis: value })
        .subscribe({
          next: (res: any) => {
            const newDiagnosis = {
              id: res.data.id,
              diagnosis: res.data.diagnosis,
            };

            this.allDiagnoses.push(newDiagnosis);
            this.addDiagnosisTag(newDiagnosis);
            this.getDiagnoses();
          },
          error: err => console.error('Error adding diagnosis', err),
        });
    } else {
      this.addDiagnosisTag(known);
    }
  }

  addDiagnosisTag(tag: any) {
    const tagName = typeof tag === 'string' ? tag : tag.diagnosis || tag.name || '';
    const exists = (this.diagnosisTags as any[]).some(
      (d: any) => ((d.diagnosis ?? d.name ?? d ?? '') || '').toLowerCase() === tagName.toLowerCase()
    );

    if (!exists) {
      (this.diagnosisTags as any[]).push(tag);
    }

    this.diagnosisInput = '';
    this.filteredDiagnoses = [];
    this.diagnosisSuggestionIndex = -1;
  }

  removeDiagnosisTag(tag: any) {
    const target = String(tag?.diagnosis ?? tag?.name ?? tag ?? '').toLowerCase();
    this.diagnosisTags = this.diagnosisTags.filter((t: any) =>
      String(t?.diagnosis ?? t?.name ?? t ?? '').toLowerCase() !== target
    );
  }

  handleDiagnosisComposerKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.diagnosisSuggestionIndex >= 0 && this.diagnosisSuggestionIndex < this.filteredDiagnoses.length) {
        this.addDiagnosisTag(this.filteredDiagnoses[this.diagnosisSuggestionIndex]);
      } else {
        this.onDiagnosisEnter(event);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.filteredDiagnoses.length > 0) {
        this.diagnosisSuggestionIndex = (this.diagnosisSuggestionIndex + 1) % this.filteredDiagnoses.length;
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.filteredDiagnoses.length > 0) {
        this.diagnosisSuggestionIndex = (this.diagnosisSuggestionIndex - 1 + this.filteredDiagnoses.length) % this.filteredDiagnoses.length;
      }
    } else if (event.key === 'Escape') {
      this.filteredDiagnoses = [];
      this.diagnosisSuggestionIndex = -1;
    }
  }

  //! Suggested Reports Methods
  onReportInputChange() {
    const value = this.prescription.suggested_reports.trim().toLowerCase();
    this.reportSuggestionIndex = -1;
    if (value.length < 1) {
      this.filteredReports = [];
      return;
    }

    // this.filteredReports = this.allReports
    //   .filter(
    //     r =>
    //       r.includes(value) &&
    //       !this.reportTags.includes(r)
    //   )
    //   .slice(0, 5);

    this.filteredReports = this.allReports
      .filter((r: any) => {
        const text = (typeof r === 'string' ? r : r.report)?.toLowerCase();
        const alreadySelected = this.reportTags.some(
          (tag: any) => String(tag?.report ?? tag ?? '').toLowerCase() === text
        );
        return text.startsWith(value) && !alreadySelected;
      })
      .slice(0, 5);
  }

  onReportEnter(event: any) {
    event.preventDefault();
    const value = this.prescription.suggested_reports.trim().toLowerCase();
    if (!value) return;

    // Check if already selected
    const exists = this.reportTags.some(
      (r: any) => (typeof r === 'string' ? r : r.report) === value
    );
    if (exists) {
      this.prescription.suggested_reports = '';
      return;
    }

    // Check if it exists in allReports
    const known = this.allReports.some(
      (r: any) => (typeof r === 'string' ? r : r.report) === value
    );

    if (!known) {
      // Call API to add new chief complaint
      this.suggestedReportService
        .addSuggestedReport({ report: value })
        .subscribe({
          next: (res: any) => {
            const newReport = {
              id: res.data.id,
              report: res.data.report,
            };

            this.allReports.push(newReport.report);
            this.addReportTag(newReport);
          },
          error: err => console.error('Error adding report', err),
        });
    } else {
      this.addReportTag({ id: null, report: value });
    }

    // this.addReportTag(value);
  }

  // addReportTag(tag: string) {
  //   // if (!this.reportTags.includes(tag)) {
  //   //   this.reportTags.push(tag);
  //   //   if (!this.allReports.includes(tag)) this.allReports.push(tag);
  //   // }
  //   if (!this.reportTags.includes(tag)) {
  //     this.reportTags.push(tag);
  //   }

  //   this.prescription.suggested_reports = '';
  //   this.filteredReports = [];
  // }

  addReportTag(tag: any) {
    const exists = (this.reportTags as any[]).some(
      (r: any) => (r.report ?? r).toLowerCase() ===
        (tag.report ?? tag).toLowerCase()
    );

    if (!exists) {
      (this.reportTags as any[]).push(tag);
    }

    this.selectedReport = null; // Clear dropdown
    this.prescription.suggested_reports = '';
    this.filteredReports = [];
    this.reportSuggestionIndex = -1;
  }

  removeReportTag(tag: any) {
    const target = String(tag?.report ?? tag ?? '').toLowerCase();
    this.reportTags = this.reportTags.filter((t: any) =>
      String(t?.report ?? t ?? '').toLowerCase() !== target
    );
  }

  formatTagDisplay(tag: string | any): string {
    // if it's an object, get the text property
    const text = typeof tag === 'string' ? tag : tag.chief_complaint || tag.report || '';
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  }

  get icdSuggestionsFromComplaints(): Array<{ code: string; title: string; score: number; sourceComplaints: string[] }> {
    const complaintTexts = (this.complaintTags || [])
      .map((tag: any) => this.normalizeIcdSearchText(typeof tag === 'string' ? tag : tag?.chief_complaint))
      .filter((text: string) => !!text);

    const cacheKey = complaintTexts.join('|');
    if (cacheKey === this.icdSuggestionCacheKey) {
      return this.icdSuggestionCache;
    }

    if (!complaintTexts.length) {
      this.icdSuggestionCacheKey = '';
      this.icdSuggestionCache = [];
      return this.icdSuggestionCache;
    }

    const scored = new Map<string, { code: string; title: string; score: number; sourceComplaints: Set<string> }>();

    for (const complaint of complaintTexts) {
      for (const icd of this.staticIcdCatalog) {
        const keywordMatched = icd.keywords.some((keyword) => {
          const normalizedKeyword = this.normalizeIcdSearchText(keyword);
          return complaint.includes(normalizedKeyword) || normalizedKeyword.includes(complaint);
        });

        if (!keywordMatched) continue;

        const existing = scored.get(icd.code) ?? {
          code: icd.code,
          title: icd.title,
          score: 0,
          sourceComplaints: new Set<string>()
        };

        existing.score += 1;
        existing.sourceComplaints.add(complaint);
        scored.set(icd.code, existing);
      }
    }

    this.icdSuggestionCache = Array.from(scored.values())
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
      .map((row) => ({
        code: row.code,
        title: row.title,
        score: row.score,
        sourceComplaints: Array.from(row.sourceComplaints).map((text) => this.toTitleCase(text))
      }))
      .slice(0, 8);

    this.icdSuggestionCacheKey = cacheKey;
    return this.icdSuggestionCache;
  }

  isIcdSelected(code: string): boolean {
    const normalizedCode = String(code || '').trim().toUpperCase();
    return this.selectedIcdCodes.some((item) => item.code.toUpperCase() === normalizedCode);
  }

  get topPendingIcdSuggestion(): { code: string; title: string; score: number; sourceComplaints: string[] } | null {
    const next = this.icdSuggestionsFromComplaints.find((item) => !this.isIcdSelected(item.code));
    return next || null;
  }

  applyTopIcdSuggestion() {
    const next = this.topPendingIcdSuggestion;
    if (!next) return;
    this.addIcdFromSuggestion(next);
  }

  handleComplaintComposerKeydown(event: KeyboardEvent) {
    const list = this.filteredComplaints || [];
    if (!list.length && event.key !== 'Enter') return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.complaintSuggestionIndex = (this.complaintSuggestionIndex + 1 + list.length) % list.length;
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.complaintSuggestionIndex = (this.complaintSuggestionIndex - 1 + list.length) % list.length;
      return;
    }

    if (event.key === 'Escape') {
      this.complaintSuggestionIndex = -1;
      this.filteredComplaints = [];
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.complaintSuggestionIndex >= 0 && this.complaintSuggestionIndex < list.length) {
        this.addComplaintTag(list[this.complaintSuggestionIndex]);
        this.getChiefComplaints();
        return;
      }
      this.onComplaintEnter(event);
    }
  }

  handleReportComposerKeydown(event: KeyboardEvent) {
    const list = this.filteredReports || [];
    if (!list.length && event.key !== 'Enter') return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.reportSuggestionIndex = (this.reportSuggestionIndex + 1 + list.length) % list.length;
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.reportSuggestionIndex = (this.reportSuggestionIndex - 1 + list.length) % list.length;
      return;
    }

    if (event.key === 'Escape') {
      this.reportSuggestionIndex = -1;
      this.filteredReports = [];
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.reportSuggestionIndex >= 0 && this.reportSuggestionIndex < list.length) {
        this.addReportTag(list[this.reportSuggestionIndex]);
        return;
      }
      this.onReportEnter(event);
    }
  }


  addIcdFromSuggestion(item: { code: string; title: string; sourceComplaints: string[] }) {
    const code = String(item?.code || '').trim().toUpperCase();
    if (!code || this.isIcdSelected(code)) return;

    const next: SelectedIcdCode = {
      code,
      title: String(item?.title || '').trim(),
      isPrimary: this.selectedIcdCodes.length === 0,
      sourceComplaints: Array.isArray(item?.sourceComplaints) ? item.sourceComplaints : []
    };

    this.selectedIcdCodes = [...this.selectedIcdCodes, next];
  }

  removeSelectedIcd(code: string) {
    const normalizedCode = String(code || '').trim().toUpperCase();
    const removedPrimary = this.selectedIcdCodes.some((item) => item.code.toUpperCase() === normalizedCode && item.isPrimary);
    this.selectedIcdCodes = this.selectedIcdCodes.filter((item) => item.code.toUpperCase() !== normalizedCode);

    if (removedPrimary && this.selectedIcdCodes.length > 0) {
      this.selectedIcdCodes = this.selectedIcdCodes.map((item, index) => ({
        ...item,
        isPrimary: index === 0
      }));
    }
  }

  setPrimaryIcd(code: string) {
    const normalizedCode = String(code || '').trim().toUpperCase();
    this.selectedIcdCodes = this.selectedIcdCodes.map((item) => ({
      ...item,
      isPrimary: item.code.toUpperCase() === normalizedCode
    }));
  }

  private normalizeSelectedIcdCodes(codes: any): SelectedIcdCode[] {
    if (!Array.isArray(codes)) return [];

    const normalized: SelectedIcdCode[] = [];
    const seen = new Set<string>();

    for (const row of codes) {
      const code = String(row?.code || '').trim().toUpperCase();
      const title = String(row?.title || '').trim();
      if (!code || !title || seen.has(code)) continue;

      seen.add(code);
      normalized.push({
        code,
        title,
        isPrimary: Boolean(row?.isPrimary),
        sourceComplaints: Array.isArray(row?.sourceComplaints)
          ? row.sourceComplaints.map((v: any) => String(v || '').trim()).filter((v: string) => !!v)
          : []
      });
    }

    if (!normalized.length) return [];

    const hasPrimary = normalized.some((item) => item.isPrimary);
    if (!hasPrimary) normalized[0].isPrimary = true;

    return normalized.map((item, index) => ({
      ...item,
      isPrimary: hasPrimary ? item.isPrimary : index === 0
    }));
  }

  private normalizeIcdSearchText(value: any): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private toTitleCase(text: string): string {
    return String(text || '')
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  getPotencyParts(potency: any): { hasHighlight: boolean; prefix: string; suffix: string } {
    const raw = String(potency ?? '').trim();
    if (!raw) return { hasHighlight: false, prefix: '', suffix: '' };

    const upper = raw.toUpperCase();
    const markers = ['MT', 'BC', 'LM', 'Q'];
    const marker = markers.find((m) => upper.startsWith(m));

    if (!marker) {
      return { hasHighlight: false, prefix: raw, suffix: '' };
    }

    return {
      hasHighlight: true,
      prefix: marker,
      suffix: raw.slice(marker.length)
    };
  }

  getCodeParts(code: any): { hasHighlight: boolean; prefix: string; suffix: string } {
    const raw = String(code ?? '').trim();
    if (!raw) return { hasHighlight: false, prefix: '', suffix: '' };

    const upper = raw.toUpperCase();
    const markers = ['MT', 'BC', 'LM', 'Q'];
    const marker = markers.find((m) => upper.startsWith(m));

    if (!marker) {
      return { hasHighlight: false, prefix: raw, suffix: '' };
    }

    return {
      hasHighlight: true,
      prefix: marker,
      suffix: raw.slice(marker.length)
    };
  }

  //! ------------------- MEDICINE METHODS -------------------

  // Version 1
  // onMedicineInputChange(pIndex: number, mIndex: number) {
  //   const med = this.prescription.prescriptions[pIndex].medicines[mIndex];
  //   med.medicineTags = med.medicineTags || [];
  //   med.filteredMedicines = med.filteredMedicines || [];

  //   const value = (med.medicineInput || '').trim().toLowerCase();
  //   if (value.length < 1) {
  //     med.filteredMedicines = [];
  //     return;
  //   }

  //   const tagNames = med.medicineTags.map((t: any) =>
  //     typeof t === 'string' ? t.toLowerCase() : t.name?.toLowerCase()
  //   );

  //   med.filteredMedicines = this.allMedicines
  //     .filter((m: any) => {
  //       // const text = typeof m === 'string' ? m : m.name;
  //       // return text.toLowerCase().includes(value) && !tagNames.includes(text.toLowerCase());

  //       const text = (typeof m === 'string' ? m : m.name)?.toLowerCase();
  //       return text.startsWith(value) && !tagNames.includes(text);
  //     })
  //     .slice(0, 25);
  // }

  // Version 2
  onMedicineInputChange(pIndex: number, mIndex: number, event?: any) {
    const med = this.prescription.prescriptions[pIndex].medicines[mIndex];

    med.medicineTags ||= [];
    med.filteredMedicines ||= [];

    const value = (med.medicineInput || '').trim().toLowerCase();
    if (!value) {
      med.filteredMedicines = [];
      return;
    }

    const selectedKeys = new Set(
      med.medicineTags.map((t: any) =>
        `${t.name}|${t.potency || ''}|${t.code || ''}`.toLowerCase()
      )
    );

    med.filteredMedicines = this.allMedicines
      .filter((m: any) => {
        const key = `${m.name}|${m.potency || ''}|${m.code || ''}`.toLowerCase();
        const searchable = `${m.name} ${m.potency || ''} ${m.code || ''}`.toLowerCase();

        return (
          searchable.includes(value) &&
          !selectedKeys.has(key)
        );
      })
      .slice(0, 25);
  }


  onMedicineEnter(event: any, pIndex: number, mIndex: number) {
    event.preventDefault();
    const med = this.prescription.prescriptions[pIndex].medicines[mIndex];
    med.medicineTags = med.medicineTags || [];

    const value = (med.medicineInput || '').trim();
    if (!value) return;

    const tagNames = med.medicineTags.map((t: any) =>
      typeof t === 'string' ? t.toLowerCase() : t.name?.toLowerCase()
    );

    if (tagNames.includes(value.toLowerCase())) {
      med.medicineInput = '';
      return;
    }

    this.addMedicineTag(pIndex, mIndex, value);
  }

  addMedicineTag(pIndex: number, mIndex: number, tag: any) {
    const med = this.prescription.prescriptions[pIndex].medicines[mIndex];
    med.medicineTags ||= [];
    med.tags ||= [];
    med.unresolvedMedicines ||= [];
    med.resolvedMedicines ||= [];
    med.unresolvedHints ||= [];

    const parsedTextInput = this.resolveMedicineFromTextInput(tag);
    const sourceTag = parsedTextInput || tag;

    // Normalize tag into a consistent structure
    const tagObj = typeof sourceTag === 'string'
      ? { name: sourceTag.trim(), code: null, potency: null }
      : {
        id: sourceTag.id ?? null,
        name: sourceTag.name?.trim() ?? '',
        code: sourceTag.code ?? null,
        potency: sourceTag.potency ?? null
      };

    // Prevent duplicates with same name + potency combo
    const isDuplicate = med.medicineTags.some(
      (t: any) =>
        t.name.toLowerCase() === tagObj.name.toLowerCase() &&
        (t.potency || '') === (tagObj.potency || '')
    );

    if (!isDuplicate) {
      this.prescription.prescriptions[pIndex].medicines[mIndex].medicineTags.push(tagObj);
      this.prescription.prescriptions[pIndex].medicines[mIndex].tags.push(tagObj);

      const resolvedFromUnresolved = this.resolveUnresolvedForTag(med, tagObj);
      if (resolvedFromUnresolved.length) {
        const existing = new Set(
          (med.resolvedMedicines || []).map((x: any) => String(x).toLowerCase())
        );

        for (const item of resolvedFromUnresolved) {
          if (!existing.has(String(item).toLowerCase())) {
            med.resolvedMedicines.push(item);
          }
        }
      }
    }

    med.medicineInput = '';
    med.filteredMedicines = [];
  }

  removeMedicineTag(pIndex: number, mIndex: number, tag: any) {
    const med = this.prescription.prescriptions[pIndex].medicines[mIndex];
    med.tags = med.tags.filter(
      (t: any) => t.name.toLowerCase() !== tag.name.toLowerCase()
    );
    med.medicineTags = med.medicineTags.filter((t: any) => {
      return !(
        t.name?.toLowerCase() === tag.name?.toLowerCase() &&
        (t.potency || '').toLowerCase() === (tag.potency || '').toLowerCase() &&
        (t.code || '').toLowerCase() === (tag.code || '').toLowerCase()
      );
    });
  }

  addPrescription() {
    if (!this.prescription?.prescriptions || this.prescription?.prescriptions?.length == 0) {
      this.prescription.prescriptions = [];
    }

    const nextId = this.prescription.prescriptions.length + 1;

    this.prescription.prescriptions.push({
      id: nextId,
      title: '',
      duration: '',
      medicines: [
        {
          tags: [],                // array of selected medicine objects
          dose: '',
          drams: '',
          ballers: '',
          medicineInput: '',       // used by the input box
          filteredMedicines: [],   // used by the dropdown
          filteredDoses: [],       // for the dose suggestions dropdown
          medicineTags: []         // temporary alias until you fully migrate
        }
      ]
    });
  }

  // --- Drag and Drop for prescriptions (Rx cards) ---
  onRxDragStart(index: number, event: DragEvent) {
    this.draggingRxIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onRxDragOver(index: number, event: DragEvent) {
    event.preventDefault();
    if (this.draggingRxIndex !== null && this.draggingRxIndex !== index) {
      this.dragOverRxIndex = index;
    }
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onRxDragLeave() {
    this.dragOverRxIndex = null;
  }

  onRxDrop(targetIndex: number, event: DragEvent) {
    event.preventDefault();
    this.dragOverRxIndex = null;
    if (this.draggingRxIndex === null || this.draggingRxIndex === targetIndex) {
      return;
    }
    const list = this.prescription.prescriptions;
    const draggedItem = list[this.draggingRxIndex];

    // Remove the dragged item
    list.splice(this.draggingRxIndex, 1);
    // Insert at target index
    list.splice(targetIndex, 0, draggedItem);

    // Re-index prescription ids
    list.forEach((p: any, idx: number) => {
      p.id = idx + 1;
    });

    this.draggingRxIndex = null;
    this.isDirty = true;
    this.saveDraftSnapshot();
  }

  onRxDragEnd() {
    this.draggingRxIndex = null;
    this.dragOverRxIndex = null;
  }

  // --- Drag and Drop for medicine rows ---
  onMedDragStart(rxIndex: number, medIndex: number, event: DragEvent) {
    this.draggingMedRxIndex = rxIndex;
    this.draggingMedIndex = medIndex;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', `${rxIndex}:${medIndex}`);
    }
  }

  onMedDragOver(rxIndex: number, medIndex: number, event: DragEvent) {
    // Only allow drag over rows in the SAME prescription card
    if (this.draggingMedRxIndex === rxIndex) {
      event.preventDefault();
      if (this.draggingMedIndex !== medIndex) {
        this.dragOverMedRxIndex = rxIndex;
        this.dragOverMedIndex = medIndex;
      }
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    }
  }

  onMedDragLeave() {
    this.dragOverMedRxIndex = null;
    this.dragOverMedIndex = null;
  }

  onMedDrop(rxIndex: number, targetMedIndex: number, event: DragEvent) {
    event.preventDefault();
    this.dragOverMedRxIndex = null;
    this.dragOverMedIndex = null;
    if (
      this.draggingMedRxIndex !== rxIndex ||
      this.draggingMedIndex === null ||
      this.draggingMedIndex === targetMedIndex
    ) {
      return;
    }

    const list = this.prescription.prescriptions[rxIndex].medicines;
    const draggedItem = list[this.draggingMedIndex];

    // Remove from source index
    list.splice(this.draggingMedIndex, 1);
    // Insert at target index
    list.splice(targetMedIndex, 0, draggedItem);

    this.draggingMedRxIndex = null;
    this.draggingMedIndex = null;
    this.isDirty = true;
    this.saveDraftSnapshot();
  }

  onMedDragEnd() {
    this.draggingMedRxIndex = null;
    this.draggingMedIndex = null;
    this.dragOverMedRxIndex = null;
    this.dragOverMedIndex = null;
  }

  // Remove a prescription
  removePrescription(index: number) {
    if (this.prescription.prescriptions.length > 1) {
      this.prescription.prescriptions.splice(index, 1);
    }
  }

  // Add a new medicine row to a specific prescription
  // addMedicineRow(index: number) {
  //   if (!Array.isArray(this.prescription.prescriptions[index].medicines)) {
  //     this.prescription.prescriptions[index].medicines = [];
  //   }

  //   this.prescription.prescriptions[index].medicines.push({ name: '', drams: '', ballers: '' });
  // }

  addMedicineRow(index: number) {
    const pres = this.prescription.prescriptions[index];

    if (!Array.isArray(pres.medicines)) {
      pres.medicines = [];
    }

    pres.medicines.push({
      dose: '',
      drams: '',
      ballers: '',
      medicineInput: '',
      tags: [
        // {
        //   id: null,
        //   name: '',
        //   code: '',
        //   potency: ''
        // }
      ],
      filteredMedicines: [],   // keep only if dropdown suggestions depend on this
      filteredDoses: [],       // for the dose suggestions dropdown
      unresolvedMedicines: [],
      resolvedMedicines: [],
      unresolvedHints: []
    });
  }

  private normalizeMedicineSearchText(value: any): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private levenshteinDistance(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    if (!m) return n;
    if (!n) return m;

    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }

    return dp[m][n];
  }

  private nameSimilarity(a: string, b: string): number {
    const x = this.normalizeMedicineSearchText(a).replace(/\s+/g, '');
    const y = this.normalizeMedicineSearchText(b).replace(/\s+/g, '');
    if (!x || !y) return 0;
    if (x === y) return 1;
    const dist = this.levenshteinDistance(x, y);
    return 1 - dist / Math.max(x.length, y.length);
  }

  private normalizeHintCodeForMatch(code: any): string {
    const value = String(code || '').trim().toUpperCase();
    if (!value) return '';
    const match = value.match(/^([A-Z]+)0*(\d+)$/);
    return match ? `${match[1]}${match[2]}` : value;
  }

  private resolveMedicineFromTextInput(input: any): any | null {
    if (input && typeof input === 'object' && (input.name || input.code)) {
      return null;
    }

    const text = String(input || '').trim();
    if (!text) return null;

    const normalizedText = this.normalizeMedicineSearchText(text);
    const normalizedCode = this.normalizeHintCodeForMatch(text);

    const byCode = (this.allMedicines || []).find((m: any) => {
      const medCode = this.normalizeHintCodeForMatch(m?.code);
      return !!normalizedCode && !!medCode && medCode === normalizedCode;
    });
    if (byCode) return byCode;

    const exactByName = (this.allMedicines || []).find((m: any) => {
      return this.normalizeMedicineSearchText(m?.name) === normalizedText;
    });
    if (exactByName) return exactByName;

    return null;
  }

  private buildFallbackHintFromRaw(raw: string): ParsedMedicineHint {
    const codeMatch = String(raw || '').toUpperCase().match(/([A-Z]+0*\d+)/);
    const potencyMatch = String(raw || '').toUpperCase().match(/\b(\d+(C|X|M)|Q)\b/);

    return {
      raw,
      nameHint: String(raw || '').trim(),
      codeHint: codeMatch ? codeMatch[1] : '',
      potencyHint: potencyMatch ? potencyMatch[1] : ''
    };
  }

  private resolveUnresolvedForTag(med: any, tag: any): string[] {
    const unresolvedList: string[] = Array.isArray(med?.unresolvedMedicines)
      ? med.unresolvedMedicines.map((x: any) => String(x))
      : [];
    med.unresolvedHints = Array.isArray(med?.unresolvedHints) ? med.unresolvedHints : [];

    if (med.unresolvedHints.length !== unresolvedList.length) {
      med.unresolvedHints = unresolvedList.map((raw: string) => this.buildFallbackHintFromRaw(raw));
    }

    if (!unresolvedList.length) return [];

    const nameText = this.normalizeMedicineSearchText(tag?.name);
    const codeText = this.normalizeHintCodeForMatch(tag?.code);

    const matched: string[] = [];
    const kept: string[] = [];
    const keptHints: ParsedMedicineHint[] = [];

    for (let idx = 0; idx < unresolvedList.length; idx++) {
      const raw = unresolvedList[idx];
      const hint = med.unresolvedHints[idx] || this.buildFallbackHintFromRaw(raw);
      const unresolvedText = this.normalizeMedicineSearchText(raw);
      const hintNameText = this.normalizeMedicineSearchText(hint?.nameHint || raw);
      const hintCodeText = this.normalizeHintCodeForMatch(hint?.codeHint);
      let isMatch = true;

      // If unresolved hint had code, selected medicine MUST match code.
      if (hintCodeText) {
        isMatch = isMatch && !!codeText && codeText === hintCodeText;
      }

      // Potency is informative but not strict for manual resolution.
      // Do not block resolution on potency mismatch/missing potency.

      // If unresolved hint had name, selected medicine should match name.
      // Allow typo-tolerant matching when doctor explicitly selected a medicine.
      if (hintNameText) {
        if (!nameText) {
          isMatch = false;
        } else if (!(hintNameText.includes(nameText) || nameText.includes(hintNameText))) {
          const hintWords = hintNameText.split(' ').filter((w: string) => w.length >= 3);
          const selectedWords = nameText.split(' ').filter((w: string) => w.length >= 3);
          const commonWords = hintWords.filter((w: string) => selectedWords.includes(w));
          const minRequired = Math.min(2, hintWords.length || 1);
          const similarity = this.nameSimilarity(hintNameText, nameText);
          isMatch = commonWords.length >= minRequired || similarity >= 0.72;
        }
      }

      // Fallback for legacy unresolved strings with no parsed hints.
      if (!hintCodeText && !hintNameText) {
        isMatch = !!nameText && (
          unresolvedText.includes(nameText) ||
          nameText.includes(unresolvedText)
        );
      }

      if (isMatch) {
        matched.push(raw);
      } else {
        kept.push(raw);
        keptHints.push(hint);
      }
    }

    med.unresolvedMedicines = kept;
    med.unresolvedHints = keptHints;
    return matched;
  }

  removeMedicineRow(prescriptionIndex: number, medicineIndex: number) {
    if (
      this.prescription.prescriptions[prescriptionIndex] &&
      this.prescription.prescriptions[prescriptionIndex].medicines.length > 1
    ) {
      this.prescription.prescriptions[prescriptionIndex].medicines.splice(medicineIndex, 1);
    }
  }

  async generateAIPlan() {
    if (!this.aiEnabled) {
      this.toast.add({
        severity: 'warn',
        summary: 'AI Disabled',
        detail: 'AI features are currently turned off.',
      });
      return;
    }

    this.isLoading = true;

    const prescriptionPayload = {
      ...this.prescription,
      chief_complaints: this.prescription.chief_complaints || '',
      diagnosis: this.diagnosisTags.map((tag: any) => tag.diagnosis || tag),
      suggested_reports: this.reportTags.map((tag: any) => tag.report),
    };

    try {
      const response: any = await this.aiService
        .generatePlan(this.patient, prescriptionPayload)
        .toPromise();

      if (response.success) {
        this.reportHtml = response.data.html;
        this.aiPlan = response.data.html;
        this.prescription.diet = response.data.diet;
        this.prescription.exercise = response.data.exercise;

        // Add AI diagnosis to the diagnosis section only
        if (response.data.diagnosis && Array.isArray(response.data.diagnosis)) {
          response.data.diagnosis.forEach((diag: string) => {
            if (diag && typeof diag === 'string') {
              const valueLower = diag.trim().toLowerCase();
              const exists = this.diagnosisTags.some(
                (d: any) => (typeof d === 'string' ? d : d.diagnosis || d.name || '').toLowerCase() === valueLower
              );
              if (!exists) {
                this.diagnosisTags.push({ diagnosis: diag.trim() });
              }
            }
          });
        }
      } else {
        console.error("AI generation failed:", response.message);
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate AI consultation report',
        });
      }

    } catch (err) {
      console.error("AI error:", err);
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred while generating AI consultation report',
      });
    } finally {
      this.isLoading = false;
    }
  }

  // Your editor content  
  downloadPDFF() {
    const content = document.getElementById('pdfPreview')?.innerHTML;

    let prescriptionHTML = '';

    if (this.prescription?.prescriptions?.length) {
      this.prescription.prescriptions.forEach((p: any) => {
        let meds = '';
        p.medicines.forEach((m: any, i: number) => {
          meds += `${i + 1}. <br>`;
        });

        prescriptionHTML += `
          <tr>
            <td>${p.title}</td>
            <td>${p.duration}</td>
            <td>${meds}</td>
          </tr>
        `;
      });
    }

    const printWindow = window.open('', '_blank', 'width=900,height=900');
    printWindow?.document.write(`
      <html>
        <head>
          <title>Health Care Plan</title>
          <style>
            body {
              font-family: 'Inter', sans-serif;
              line-height: 1.6;
              margin: 40px;
              color: #000;
            }
            h1, h2, h3 {
              text-align: center;
              color: #2e3a59;
            }
            p { margin-bottom: 10px; }
            .clinic-header {
              text-align: center;
              font-weight: bold;
              font-size: 18px;
              margin-bottom: 20px;
            }
            .pdf-container {
              white-space: normal;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            table, th, td {
              border: 1px solid #000;
            }
            th, td {
              padding: 8px;
              vertical-align: top;
            }
          </style>
        </head>
        <body>
          <div class="clinic-header">
            Dr. Shailesh Sancheti's<br>
            Advance Homeopathic Clinic, Pune<br>
            Contact: 8070708001
          </div>

          <h2>Prescription Summary</h2>
          <table>
            <thead>
              <tr>
                <th>Prescription</th>
                <th>Duration</th>
                <th>Medicines</th>
              </tr>
            </thead>
            <tbody>
              ${prescriptionHTML}
            </tbody>
          </table>

          ${content}
        </body>
      </html>
    `);
    printWindow?.document.close();
    printWindow?.print();
  }

  logoBase64 = '';

  loadLogo() {
    // fetch('/drsancheti/logo.png') // for local
    fetch('/clinic/logo.png') // For prod
      .then(res => res.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => this.logoBase64 = reader.result as string;
        reader.readAsDataURL(blob);
      });
  }

  async downloadPDFFF() {
    const content = document.getElementById('pdfPreview')?.innerHTML;

    // Build prescription table    
    let prescriptionHTML = '';
    if (this.prescription?.prescriptions?.length) {
      this.prescription.prescriptions.forEach((p: any) => {
        let meds = '';
        p.medicines.forEach((m: any, i: number) => {
          meds += `${i + 1}.<br>`;
        });

        prescriptionHTML += `
          <tr>
            <td>${p.title}</td>
            <td>${p.duration}</td>
            <td>${meds}</td>
          </tr>
        `;
      });
    }


    const printWindow = window.open('', '_blank', 'width=900,height=900');


    printWindow?.document.write(`
    <html>
    <head>
      <title>Consultation Report</title>

      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">

      <style>

        html, body {
          height: 100%;
          padding: 0;
          margin: 0;
        }

        body {
          font-family: 'Inter', sans-serif;
          color: #2E3A59;
          line-height: 1.65;
          font-size: 14px;
          -webkit-print-color-adjust: exact;
        }

        /* WATERMARK FIX — now stays inside page */
        .page {
          position: relative;
          min-height: 100%;
          padding: 40px 48px;
          box-sizing: border-box;
        }

        .watermark {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0.06;
          background-image: url('${this.logoBase64}');
          background-repeat: repeat;
          background-size: 360px;
          transform: rotate(-35deg);
          transform-origin: center;
          pointer-events: none;
          z-index: 0;
        }

        .content-wrapper {
          position: relative;
          background: rgba(255,255,255,0.94);
          border-radius: 8px;
          padding: 28px 32px;
          z-index: 2;
          box-shadow: 0 0 12px rgba(0,0,0,0.08);
        }

        .header {
          display: flex;
          align-items: center;
          margin-bottom: 28px;
          padding-bottom: 16px;
          border-bottom: 2px solid #F68920;
        }

        .header img {
          height: 78px;
        }

        .header-info {
          margin-left: 18px;
        }

        .header-info .title {
          font-size: 20px;
          font-weight: 600;
          color: #E31C79;
          margin-bottom: 4px;
        }

        h2 {
          font-size: 18px;
          font-weight: 600;
          margin-top: 36px;
          margin-bottom: 14px;
          padding-left: 12px;
          border-left: 4px solid #F68920;
          color: #2E3A59;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          margin-top: 10px;
        }

        th {
          background: #F68920;
          color: white;
          padding: 10px;
          font-size: 14px;
          text-align: left;
        }

        td {
          border-bottom: 1px solid #e8e8e8;
          padding: 10px;
          font-size: 13px;
        }

        .signature-block {
          margin-top: 40px;
          padding-top: 18px;
          border-top: 2px solid #F68920;
          text-align: right;
          font-size: 14px;
        }

        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #777;
        }

      </style>

    </head>

    <body>

      <div class="page">
        <div class="watermark"></div>

        <div class="content-wrapper">

          <!-- HEADER -->
          <div class="header">
            <img src="${this.logoBase64}">
            <div class="header-info">
              <div class="title">Dr. Shailesh Sancheti’s Advance Homeopathic Clinic</div>
              <div>Sr. No. 2/11, Opp. Purandar (MHADA) Building, Pune – 411037</div>
              <div>Ph: 020-2426 0074 | Email: drshaileshsancheti@gmail.com</div>
              <div>Website: drsancheti.in</div>
            </div>
          </div>

          <!-- PRESCRIPTION SUMMARY -->
          <h2>Prescription Summary</h2>
          <table>
            <thead>
              <tr>
                <th>Prescription</th>
                <th>Duration</th>
                <th>Medicines</th>
              </tr>
            </thead>
            <tbody>
              ${prescriptionHTML}
            </tbody>
          </table>

          <!-- AI CONTENT -->
          ${content}

          <div class="signature-block">
            <b>Dr. Shailesh Sancheti</b><br>
            BHMS, MD (Homeopathy)
          </div>

          <div class="footer">
            Digitally generated report — no signature required.
          </div>

        </div>
      </div>

    </body>
    </html>
    `);

    printWindow?.document.close();
    // @ts-ignore
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow?.focus();
        printWindow?.print();
        printWindow?.close();
      }, 400);
    };
  }


  //   async downloadPDFOriginal() {
  //     const content = this.reportHtml;
  //     const patientName = `${this.patient.first_name} ${this.patient.last_name}`;
  //     const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

  //     let prescriptionHTML = '';
  //     if (this.prescription?.prescriptions?.length) {
  //       this.prescription.prescriptions.forEach((p: any) => {
  //         let meds = '';
  //         p.medicines.forEach((_: any, i: number) => {
  //           meds += `${i + 1}.<br>`;
  //         });

  //         prescriptionHTML += `
  //           <tr>
  //             <td>${p.title}</td>
  //             <td>${p.duration}</td>
  //             <td>${meds}</td>
  //           </tr>
  //         `;
  //       });
  //     }

  //     const printWindow = window.open('', '_blank', 'width=900,height=900');

  //     printWindow!.document.write(`
  //     <html>
  //       <head>
  //         <title>Consultation Report - ${patientName} - ${today}</title>

  //         <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">

  //         <style>

  //           @page {
  //             size: A4;
  //             margin: 0;
  //           }

  //           html, body {
  //             margin: 0;
  //             padding: 0;
  //             -webkit-print-color-adjust: exact;
  //             font-family: 'Inter', sans-serif;
  //           }

  //           /* Each print page */
  //           .print-page {
  //             height: 297mm;
  //             box-sizing: border-box;
  //             position: relative;
  //             page-break-after: always;
  //           }

  //           /* WATERMARK on EVERY PAGE */
  //           .watermark {
  //             position: absolute;
  //             inset: 0;
  //             background-image: url('${this.logoBase64}');
  //             background-repeat: repeat;
  //             background-size: 350px;
  //             opacity: 0.07;
  //             z-index: 0;
  //             pointer-events: none;
  //           }

  //           /* REPEATING HEADER */
  //           .header {
  //             display: flex;
  //             align-items: center;
  //             padding: 10px 18px;
  //             padding-bottom: 8px;
  //             border-bottom: 2px solid #F68920;
  //             background: white;
  //             z-index: 5;
  //           }

  //           .header img {
  //             height: 90px;
  //           }

  //           .header-info {
  //             margin-left: 16px;
  //           }

  //           .header-info .title {
  //             font-size: 18px;
  //             font-weight: 600;
  //             color: #E31C79;
  //             margin-bottom: 2px;
  //           }

  //           .content-wrapper {
  //             position: absolute;
  //             top: 40mm;
  //             left: 18mm;
  //             right: 18mm;
  //             bottom: 20mm;
  //             overflow: visible;
  //             z-index: 10;
  //           }

  //           h2 {
  //             font-size: 18px;
  //             font-weight: 600;
  //             margin-top: 26px;
  //             margin-bottom: 12px;
  //             padding-left: 10px;
  //             border-left: 4px solid #F68920;
  //           }

  //           .summary-table,
  //           table {
  //             width: 100%;
  //             border-collapse: collapse;
  //           }

  //           .summary-table td {
  //             border: 1px solid #dcdcdc;
  //             padding: 8px;
  //             font-size: 13px;
  //           }

  //           .summary-table tr:nth-child(even) {
  //             background: #fafafa;
  //           }

  //           th {
  //             background: #F68920;
  //             color: white;
  //             padding: 8px;
  //             font-size: 14px;
  //           }

  //           td {
  //             border-bottom: 1px solid #e8e8e8;
  //             padding: 8px;
  //             font-size: 13px;
  //           }

  //           .signature-block {
  //             margin-top: 40px;
  //             padding-top: 20px;
  //             text-align: right;
  //             font-size: 14px;
  //             border-top: 2px solid #F68920;
  //           }

  //           .footer {
  //             position: absolute;
  //             bottom: 12mm;
  //             left: 0;
  //             right: 0;
  //             text-align: center;
  //             font-size: 12px;
  //             color: #666;
  //           }

  //         </style>
  //       </head>

  //       <body>

  //         <div class="print-page">

  //           <div class="header">
  //             <img src="${this.logoBase64}">
  //             <div class="header-info">
  //               <div class="title">Dr. Shailesh Sancheti’s Advance Homeopathic Clinic</div>
  //               <div>Sr. No. 2/11, Opp. Purandar (MHADA) Building, Pune – 411037</div>
  //               <div>Ph: 020-2426 0074 | Email: drshaileshsancheti@gmail.com</div>
  //               <div>Website: drsancheti.in</div>
  //             </div>
  //           </div>

  //           <div class="content-wrapper">

  //             <h2>Prescription Summary</h2>
  //             <table>
  //               <thead>
  //                 <tr>
  //                   <th>Prescription</th>
  //                   <th>Duration</th>
  //                   <th>Medicines</th>
  //                 </tr>
  //               </thead>
  //               <tbody>${prescriptionHTML}</tbody>
  //             </table>

  //             <div class="report-content">
  //               ${content}
  //             </div>

  //             <div class="signature-block">
  //               <b>Dr. Shailesh Sancheti</b><br>
  //               BHMS, MD (Homeopathy)
  //             </div>

  //           </div>
  //         </div>

  //       </body>
  //     </html>
  //   `);

  //   printWindow!.document.close();

  //   printWindow!.onload = () => {
  //     setTimeout(() => {
  //       printWindow!.document.title = `Consultation Report - ${patientName} - ${today}`;
  //       printWindow!.focus();
  //       printWindow!.print();
  //       printWindow!.close();
  //     }, 300);
  //   };
  // }

  // async downloadPDF(language: string = 'english') {
  //     // language: 'english' or regional code like 'hindi', 'marathi', 'gujarati'
  //     const patientName = `${this.patient.first_name} ${this.patient.last_name}`;
  //     const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

  //     let prescriptionHTML = '';
  //     if (this.prescription?.prescriptions?.length) {
  //       this.prescription.prescriptions.forEach((p: any) => {
  //         let meds = '';
  //         p.medicines.forEach((_: any, i: number) => {
  //           meds += `${i + 1}.<br>`;
  //         });

  //         prescriptionHTML += `
  //           <tr>
  //             <td>${p.title}</td>
  //             <td>${p.duration}</td>
  //             <td>${meds}</td>
  //           </tr>
  //         `;
  //       });
  //     }

  //     const doPrint = (htmlContent: string) => {
  //       const printWindow = window.open('', '_blank', 'width=900,height=900');

  //       printWindow!.document.write(`
  //       <html>
  //         <head>
  //           <title>Consultation Report - ${patientName} - ${today}</title>

  //           <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">

  //           <style>
  //             @page { size: A4; margin: 0; }
  //             html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; font-family: 'Inter', sans-serif; }
  //             .print-page { height: 297mm; box-sizing: border-box; position: relative; page-break-after: always; }
  //             .watermark { position: absolute; inset: 0; background-image: url('${this.logoBase64}'); background-repeat: repeat; background-size: 350px; opacity: 0.07; z-index: 0; pointer-events: none; }
  //             .header { display: flex; align-items: center; padding: 10px 18px; padding-bottom: 8px; border-bottom: 2px solid #F68920; background: white; z-index: 5; }
  //             .header img { height: 90px; }
  //             .header-info { margin-left: 16px; }
  //             .header-info .title { font-size: 18px; font-weight: 600; color: #E31C79; margin-bottom: 2px; }
  //             .content-wrapper { position: absolute; top: 40mm; left: 18mm; right: 18mm; bottom: 20mm; overflow: visible; z-index: 10; }
  //             h2 { font-size: 18px; font-weight: 600; margin-top: 26px; margin-bottom: 12px; padding-left: 10px; border-left: 4px solid #F68920; }
  //             .summary-table, table { width: 100%; border-collapse: collapse; }
  //             .summary-table td { border: 1px solid #dcdcdc; padding: 8px; font-size: 13px; }
  //             .summary-table tr:nth-child(even) { background: #fafafa; }
  //             th { background: #F68920; color: white; padding: 8px; font-size: 14px; }
  //             td { border-bottom: 1px solid #e8e8e8; padding: 8px; font-size: 13px; }
  //             .signature-block { margin-top: 40px; padding-top: 20px; text-align: right; font-size: 14px; border-top: 2px solid #F68920; }
  //             .footer { position: absolute; bottom: 12mm; left: 0; right: 0; text-align: center; font-size: 12px; color: #666; }
  //           </style>
  //         </head>

  //         <body>
  //           <div class="print-page">
  //             <div class="header">
  //               <img src="${this.logoBase64}">
  //               <div class="header-info">
  //                 <div class="title">Dr. Shailesh Sancheti’s Advance Homeopathic Clinic</div>
  //                 <div>Sr. No. 2/11, Opp. Purandar (MHADA) Building, Pune – 411037</div>
  //                 <div>Ph: 020-2426 0074 | Email: drshaileshsancheti@gmail.com</div>
  //                 <div>Website: drsancheti.in</div>
  //               </div>
  //             </div>

  //             <div class="content-wrapper">

  //               <h2>Prescription Summary</h2>
  //               <table>
  //                 <thead>
  //                   <tr>
  //                     <th>Prescription</th>
  //                     <th>Duration</th>
  //                     <th>Medicines</th>
  //                   </tr>
  //                 </thead>
  //                 <tbody>${prescriptionHTML}</tbody>
  //               </table>

  //               <div class="report-content">
  //                 ${htmlContent}
  //               </div>

  //               <div class="signature-block">
  //                 <b>Dr. Shailesh Sancheti</b><br>
  //                 BHMS, MD (Homeopathy)
  //               </div>

  //             </div>
  //           </div>
  //         </body>
  //       </html>
  //       `);

  //       printWindow!.document.close();

  //       printWindow!.onload = () => {
  //         setTimeout(() => {
  //           printWindow!.document.title = `Consultation Report - ${patientName} - ${today}`;
  //           printWindow!.focus();
  //           printWindow!.print();
  //           printWindow!.close();
  //         }, 300);
  //       };
  //     };

  //     // If English requested, print current reportHtml directly
  //     const contentToTranslate = this.reportHtml || this.aiPlan || document.getElementById('pdfPreview')?.innerHTML || '';

  //     if (!contentToTranslate) {
  //       this.toast.add({ severity: 'warn', summary: 'No Content', detail: 'No AI report available to export.' });
  //       return;
  //     }

  //     if (!language || language === 'english') {
  //       doPrint(contentToTranslate);
  //       return;
  //     }

  //     // Otherwise request backend translation and then print
  //     this.isLoading = true;
  //     this.aiService.translateReport(contentToTranslate, language).subscribe({
  //       next: (res: any) => {
  //         const translated = res?.data?.html ?? res?.html ?? res;
  //         doPrint(translated);
  //       },
  //       error: (err: any) => {
  //         console.error('Translation failed:', err);
  //         this.toast.add({ severity: 'error', summary: 'Translation Failed', detail: 'Could not translate report. Try again.' });
  //       },
  //       complete: () => {
  //         this.isLoading = false;
  //       }
  //     });
  //   }







  //   async generatePlan() {
  //     const { first_name, middle_name, last_name } = this.patient;

  //     // Trim spaces and join only if values exist
  //     this.patient.full_name = [first_name, middle_name, last_name]
  //       .filter(name => name && name.trim() !== '')
  //       .join(' ');
  //     const prompt = `You are a medical assistant AI.

  // Generate a **patient consultation report** in the exact format below.
  // Follow the structure of the PDF report, and add **Diet Plan** and **Exercise Plan** at the end.

  // Dr. Shailesh Sancheti's Advance Homeopathic Clinic, Pune
  // Contact: 8070708001

  // Patient Summary
  // Name: ${this.patient.full_name || "N/A"}
  // Age/Gender: ${this.patient.age || "N/A"} / ${this.patient.gender || "N/A"}
  // Date of Report: ${new Date().toLocaleDateString()}

  // Diagnosis (Based on Pre-Consultation):
  // - Blood Pressure: ${this.prescription.blood_pressure || "N/A"}
  // - Pulse Rate: ${this.prescription.pulse_rate || "N/A"} bpm
  // - Temperature: ${this.prescription.temperature || "N/A"} °C
  // - Weight: ${this.prescription.weight || "N/A"} kg
  // - Height: ${this.prescription.height || "N/A"} cm
  // - Fasting Blood Sugar: ${this.prescription.fasting_blood_sugar || "N/A"} mg/dL
  // - Oxygen Saturation: ${this.prescription.blood_oxygen || "N/A"} %
  // - Cold/Runny Nose: ${this.prescription.runny_nose}
  // - Cough: ${this.prescription.cough}
  // - Current Complaints: ${this.prescription.chief_complaints}
  // - Allergies: ${this.prescription.allergies || "N/A"}
  // - Current Medication: ${this.prescription.current_medications || "N/A"}
  // - History: ${this.prescription.personal_family_history || "N/A"}
  // - Surgical History: ${this.prescription.surgical_history || "N/A"}

  // Understanding the Findings
  // Provide a short explanation of what these values may indicate clinically.

  // Scope of Homeopathy
  // List 3–5 personalized ways homeopathy could support this patient (bullet points).

  // Our Homeopathic Approach
  // List stepwise approach for treatment and follow-up.

  // Final Thoughts
  // Provide a short summary of overall health outlook and positivity.

  // Diet Plan
  // Provide a personalized diet plan with 4–6 bullet points.

  // Exercise Plan
  // Provide a personalized exercise plan with 3–5 bullet points.
  // `;
  //     try {
  //       const response = await this.client.chat.completions.create({
  //         model: "gpt-4o-mini",
  //         messages: [{ role: "user", content: prompt }]
  //       });
  //       const content = response.choices?.[0]?.message?.content ?? '';
  //       this.aiPlan = content;

  //       // Extract diet & exercise
  //       const dietMatch = content.match(/Diet Plan\s*([\s\S]*?)Exercise Plan/);
  //       const exerciseMatch = content.match(/Exercise Plan\s*([\s\S]*)/);
  //       this.prescription.diet = dietMatch?.[1]?.trim() ?? '';
  //       this.prescription.exercise = exerciseMatch?.[1]?.trim() ?? '';
  //     } catch (err) {
  //       console.error(err);
  //       this.aiPlan = '';
  //       this.prescription.diet = '';
  //       this.prescription.exercise = '';
  //     }
  //   }

  //   async generatePDF() {
  //       // Show "generating PDF" message
  //       const generatingMsg = { severity: 'info', summary: 'Info', detail: 'Generating PDF...' };
  //       this.toast.add(generatingMsg);

  //       try {
  //         // Ensure AI plan exists
  //         if (!this.aiPlan) await this.generatePlan();

  //         const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  //         const margin = 15;
  //         let yPos = 20;

  //         const content = this.aiPlan || `
  //         Patient Summary
  //         Name: ${this.patient.first_name || "N/A"} ${this.patient.mid_name || ""} ${this.patient.last_name || "N/A"}
  //         Age: ${this.patient.age || "N/A"}
  //         Gender: ${this.patient.gender || "N/A"}
  //         Blood Pressure: ${this.prescription.blood_pressure || "N/A"}
  //         Pulse: ${this.prescription.pulse_rate || 0}
  //         Temperature: ${this.prescription.temperature || 0}
  //         Weight: ${this.prescription.weight || 0}
  //         Height: ${this.prescription.height || 0}
  //         Fasting Blood Sugar: ${this.prescription.fasting_blood_sugar || ''}
  //         Blood Oxygen: ${this.prescription.blood_oxygen || ''}
  //         `;

  //         const lines = content.split('\n');

  //         doc.setFont('helvetica');
  //         lines.forEach((line) => {
  //           line = line.trim();
  //           if (!line) { yPos += 5; return; }

  //           if (/Patient Summary|Diagnosis|Understanding|Scope|Approach|Final Thoughts|Diet Plan|Exercise Plan/i.test(line)) {
  //             doc.setFont('helvetica', 'bold');
  //             doc.setFontSize(12);
  //             yPos += 5;
  //           } else {
  //             doc.setFont('helvetica', 'normal');
  //             doc.setFontSize(10);
  //           }

  //           if (/^\- /.test(line)) line = '• ' + line.slice(2);

  //           const splitText = doc.splitTextToSize(line, 180);
  //           doc.text(splitText, margin, yPos);
  //           yPos += splitText.length * 6;

  //           if (yPos > 280) { doc.addPage(); yPos = 20; }
  //         });

  //         const pdfBlob = doc.output('blob');
  //         const blobUrl = URL.createObjectURL(pdfBlob);
  //         this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);  // ✅ safe URL

  //         // Show success message
  //         this.toast.add({ severity: 'success', summary: 'Success', detail: 'PDF generated successfully.' });

  //       } catch (error) {
  //         console.error('Error generating PDF:', error);
  //         this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to generate PDF.' });
  //       }
  //     }

  //! Download English PDF
  async downloadPDFOriginal() {
    // Download in English (for doctor)
    await this.downloadPDF('english');
  }

  //! Download Regional Language PDF
  async downloadPDFRegional() {
    // Download in selected regional language (for patient)
    if (!this.aiEnabled) {
      this.toast.add({
        severity: 'warn',
        summary: 'AI Disabled',
        detail: 'Regional translation requires AI, which is currently turned off.',
      });
      return;
    }

    if (!this.selectedRegionalLanguage || this.selectedRegionalLanguage.value === 'english') {
      this.toast.add({
        severity: 'warn',
        summary: 'Language Required',
        detail: 'Please select a regional language.'
      });
      return;
    }
    await this.downloadPDF(this.selectedRegionalLanguage.value);
  }

  //! Download PDF with optional language translation
  // async downloadPDF(language: string = 'english') {
  //   const patientName = `${this.patient.first_name} ${this.patient.last_name}`;
  //   const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

  //   // Build prescription HTML
  //   let prescriptionHTML = '';
  //   if (this.prescription?.prescriptions?.length) {
  //     this.prescription.prescriptions.forEach((p: any) => {
  //       let meds = '';
  //       p.medicines.forEach((med: any, i: number) => {
  //         meds += `${i + 1}. ${med.name || ''}<br>`;
  //       });

  //       prescriptionHTML += `
  //         <tr>
  //           <td>${p.title}</td>
  //           <td>${p.duration}</td>
  //           <td>${meds}</td>
  //         </tr>
  //       `;
  //     });
  //   }

  //   const doPrint = (htmlContent: string, lang: string) => {
  //     const printWindow = window.open('', '_blank', 'width=900,height=900');
  //     if (!printWindow) {
  //       this.toast.add({ 
  //         severity: 'error', 
  //         summary: 'Error', 
  //         detail: 'Unable to open print window. Please check your browser settings.' 
  //       });
  //       return;
  //     }

  //     const languageLabel = lang === 'english' ? '' : ` (${this.languageOptions.find(l => l.value === lang)?.label})`;

  //     printWindow.document.write(`
  //     <html>
  //       <head>
  //         <title>Consultation Report - ${patientName} - ${today}${languageLabel}</title>
  //         <meta charset="UTF-8">
  //         <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  //         <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@300;400;500;600&display=swap" rel="stylesheet">

  //         <style>
  //           @page { size: A4; margin: 0; }
  //           html, body { 
  //             margin: 0; 
  //             padding: 0; 
  //             -webkit-print-color-adjust: exact; 
  //             font-family: 'Inter', 'Noto Sans Devanagari', sans-serif;
  //           }
  //           .print-page { 
  //             height: 297mm; 
  //             box-sizing: border-box; 
  //             position: relative; 
  //             page-break-after: always; 
  //           }
  //           .watermark { 
  //             position: absolute; 
  //             inset: 0; 
  //             background-image: url('${this.logoBase64}'); 
  //             background-repeat: repeat; 
  //             background-size: 350px; 
  //             opacity: 0.07; 
  //             z-index: 0; 
  //             pointer-events: none; 
  //           }
  //           .header { 
  //             display: flex; 
  //             align-items: center; 
  //             padding: 10px 18px; 
  //             padding-bottom: 8px; 
  //             border-bottom: 2px solid #F68920; 
  //             background: white; 
  //             z-index: 5; 
  //           }
  //           .header img { height: 90px; }
  //           .header-info { margin-left: 16px; }
  //           .header-info .title { 
  //             font-size: 18px; 
  //             font-weight: 600; 
  //             color: #E31C79; 
  //             margin-bottom: 2px; 
  //           }
  //           .language-badge {
  //             position: absolute;
  //             top: 10px;
  //             right: 18px;
  //             background: #E31C79;
  //             color: white;
  //             padding: 4px 12px;
  //             border-radius: 4px;
  //             font-size: 12px;
  //             font-weight: 600;
  //           }
  //           .content-wrapper { 
  //             position: absolute; 
  //             top: 40mm; 
  //             left: 18mm; 
  //             right: 18mm; 
  //             bottom: 20mm; 
  //             overflow: visible; 
  //             z-index: 10; 
  //           }
  //           h2 { 
  //             font-size: 18px; 
  //             font-weight: 600; 
  //             margin-top: 26px; 
  //             margin-bottom: 12px; 
  //             padding-left: 10px; 
  //             border-left: 4px solid #F68920; 
  //           }
  //           .summary-table, table { 
  //             width: 100%; 
  //             border-collapse: collapse; 
  //           }
  //           .summary-table td { 
  //             border: 1px solid #dcdcdc; 
  //             padding: 8px; 
  //             font-size: 13px; 
  //           }
  //           .summary-table tr:nth-child(even) { background: #fafafa; }
  //           th { 
  //             background: #F68920; 
  //             color: white; 
  //             padding: 8px; 
  //             font-size: 14px; 
  //           }
  //           td { 
  //             border-bottom: 1px solid #e8e8e8; 
  //             padding: 8px; 
  //             font-size: 13px; 
  //           }
  //           .signature-block { 
  //             margin-top: 40px; 
  //             padding-top: 20px; 
  //             text-align: right; 
  //             font-size: 14px; 
  //             border-top: 2px solid #F68920; 
  //           }
  //           .footer { 
  //             position: absolute; 
  //             bottom: 12mm; 
  //             left: 0; 
  //             right: 0; 
  //             text-align: center; 
  //             font-size: 12px; 
  //             color: #666; 
  //           }
  //         </style>
  //       </head>

  //       <body>
  //         <div class="print-page">
  //           <div class="watermark"></div>
  //           ${lang !== 'english' ? `<div class="language-badge">${languageLabel}</div>` : ''}

  //           <div class="header">
  //             <img src="${this.logoBase64}">
  //             <div class="header-info">
  //               <div class="title">Dr. Shailesh Sancheti's Advance Homeopathic Clinic</div>
  //               <div>Sr. No. 2/11, Opp. Purandar (MHADA) Building, Pune – 411037</div>
  //               <div>Ph: 020-2426 0074 | Email: drshaileshsancheti@gmail.com</div>
  //               <div>Website: drsancheti.in</div>
  //             </div>
  //           </div>

  //           <div class="content-wrapper">
  //             <h2>Prescription Summary</h2>
  //             <table>
  //               <thead>
  //                 <tr>
  //                   <th>Prescription</th>
  //                   <th>Duration</th>
  //                   <th>Medicines</th>
  //                 </tr>
  //               </thead>
  //               <tbody>${prescriptionHTML}</tbody>
  //             </table>

  //             <div class="report-content">
  //               ${htmlContent}
  //             </div>

  //             <div class="signature-block">
  //               <b>Dr. Shailesh Sancheti</b><br>
  //               BHMS, MD (Homeopathy)
  //             </div>
  //           </div>
  //         </div>
  //       </body>
  //     </html>
  //     `);

  //     printWindow.document.close();

  //     printWindow.onload = () => {
  //       setTimeout(() => {
  //         printWindow.document.title = `Consultation Report - ${patientName} - ${today}${languageLabel}`;
  //         printWindow.focus();
  //         printWindow.print();
  //         printWindow.close();
  //       }, 300);
  //     };
  //   };

  //   // Get content to translate
  //   const contentToTranslate = this.reportHtml || this.aiPlan || document.getElementById('pdfPreview')?.innerHTML || '';

  //   if (!contentToTranslate) {
  //     this.toast.add({ 
  //       severity: 'warn', 
  //       summary: 'No Content', 
  //       detail: 'No AI report available to export.' 
  //     });
  //     return;
  //   }

  //   // If English, print directly
  //   if (!language || language === 'english') {
  //     doPrint(contentToTranslate, 'english');
  //     return;
  //   }

  //   // Otherwise, translate first then print
  //   this.isLoading = true;
  //   this.aiService.translateReport(contentToTranslate, language).subscribe({
  //     next: (res: any) => {
  //       const translated = res?.data?.html ?? res?.html ?? res;
  //       doPrint(translated, language);
  //     },
  //     error: (err: any) => {
  //       console.error('Translation failed:', err);
  //       this.toast.add({ 
  //         severity: 'error', 
  //         summary: 'Translation Failed', 
  //         detail: 'Could not translate report. Please try again.' 
  //       });
  //     },
  //     complete: () => {
  //       this.isLoading = false;
  //     }
  //   });
  // }

  async downloadPDF(language: string = 'english') {
    const patientName = `${this.patient.first_name} ${this.patient.last_name}`;
    const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

    // Build prescription HTML
    let prescriptionHTML = '';
    if (this.prescription?.prescriptions?.length) {
      this.prescription.prescriptions.forEach((p: any) => {
        let meds = '';
        p.medicines.forEach((med: any, i: number) => {
          meds += `${i + 1}. ${med.name || ''}<br>`;
        });

        prescriptionHTML += `
        <tr>
          <td>${p.title}</td>
          <td>${p.duration}</td>
          <td>${meds}</td>
        </tr>
      `;
      });
    }

    // Get content to translate
    const contentToTranslate = this.reportHtml || this.aiPlan || document.getElementById('pdfPreview')?.innerHTML || '';

    if (!contentToTranslate) {
      this.toast.add({
        severity: 'warn',
        summary: 'No Content',
        detail: 'No AI report available to export.'
      });
      return;
    }

    // ✅ OPEN WINDOW IMMEDIATELY (within user gesture context)
    const printWindow = window.open('', '_blank', 'width=900,height=900');

    if (!printWindow) {
      this.toast.add({
        severity: 'error',
        summary: 'Popup Blocked',
        detail: 'Please allow popups for this site and try again.'
      });
      return;
    }

    // Show loading message in the popup
    printWindow.document.write(`
    <html>
      <head>
        <title>Loading...</title>
        <style>
          body {
            font-family: 'Inter', sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            margin: 0;
            background: #f5f5f5;
          }
          .loader {
            text-align: center;
          }
          .spinner {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #F68920;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </head>
      <body>
        <div class="loader">
          <div class="spinner"></div>
          <p>Preparing your document...</p>
        </div>
      </body>
    </html>
  `);

    const generatePDF = (htmlContent: string, lang: string) => {
      const languageLabel = lang === 'english' ? '' : ` (${this.languageOptions.find(l => l.value === lang)?.label})`;

      const fullHTML = `
    <html>
      <head>
        <title>Consultation Report - ${patientName} - ${today}${languageLabel}</title>
        <meta charset="UTF-8">
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@300;400;500;600&display=swap" rel="stylesheet">

        <style>
          @page { size: A4; margin: 0; }
          html, body { 
            margin: 0; 
            padding: 0; 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact;
            font-family: 'Inter', 'Noto Sans Devanagari', sans-serif;
          }
          .print-page { 
            min-height: 297mm; 
            box-sizing: border-box; 
            position: relative; 
            page-break-after: always; 
          }
          .watermark { 
            position: absolute; 
            inset: 0; 
            background-image: url('${this.logoBase64}'); 
            background-repeat: repeat; 
            background-size: 350px; 
            opacity: 0.07; 
            z-index: 0; 
            pointer-events: none; 
          }
          .header { 
            display: flex; 
            align-items: center; 
            padding: 10px 18px; 
            padding-bottom: 8px; 
            border-bottom: 2px solid #F68920; 
            background: white; 
            z-index: 5; 
            position: relative;
          }
          .header img { height: 90px; }
          .header-info { margin-left: 16px; }
          .header-info .title { 
            font-size: 18px; 
            font-weight: 600; 
            color: #E31C79; 
            margin-bottom: 2px; 
          }
          .language-badge {
            position: absolute;
            top: 10px;
            right: 18px;
            background: #E31C79;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
          }
          .content-wrapper { 
            padding: 20mm 18mm;
            position: relative;
            z-index: 10; 
          }
          h2 { 
            font-size: 18px; 
            font-weight: 600; 
            margin-top: 26px; 
            margin-bottom: 12px; 
            padding-left: 10px; 
            border-left: 4px solid #F68920; 
          }
          .summary-table, table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px;
          }
          .summary-table td { 
            border: 1px solid #dcdcdc; 
            padding: 8px; 
            font-size: 13px; 
          }
          .summary-table tr:nth-child(even) { background: #fafafa; }
          th { 
            background: #F68920; 
            color: white; 
            padding: 8px; 
            font-size: 14px; 
          }
          td { 
            border-bottom: 1px solid #e8e8e8; 
            padding: 8px; 
            font-size: 13px; 
          }
          ul {
            margin: 10px 0;
            padding-left: 20px;
          }
          li {
            margin-bottom: 8px;
            font-size: 13px;
          }
          p {
            font-size: 13px;
            line-height: 1.6;
            margin: 10px 0;
          }
          .signature-block { 
            margin-top: 40px; 
            padding-top: 20px; 
            text-align: right; 
            font-size: 14px; 
            border-top: 2px solid #F68920; 
          }
          @media print {
            .print-page { page-break-after: always; }
          }
        </style>
      </head>

      <body>
        <div class="print-page">
          ${lang !== 'english' ? `<div class="language-badge">${languageLabel}</div>` : ''}
          
          <div class="header">
            <img src="${this.logoBase64}" alt="Clinic Logo">
            <div class="header-info">
              <div class="title">Dr. Shailesh Sancheti's Advance Homeopathic Clinic</div>
              <div>Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala</div>
              <div>Maharshi Nagar, Pune, Maharashtra – 411037</div>
              <div>Ph: 8070708001 / 02 / 03 | Email: drshaileshsancheti@gmail.com</div>
            </div>
          </div>

          <div class="content-wrapper">
            <h2>Prescription Summary</h2>
            <table>
              <thead>
                <tr>
                  <th>Prescription</th>
                  <th>Duration</th>
                  <th>Medicines</th>
                </tr>
              </thead>
              <tbody>${prescriptionHTML}</tbody>
            </table>

            <div class="report-content">
              ${htmlContent}
            </div>

            <div class="signature-block">
              <b>Dr. Shailesh Sancheti</b><br>
              BHMS, MD (Homeopathy)
            </div>
          </div>
        </div>
      </body>
    </html>
    `;

      // Clear loading message and write actual content
      printWindow.document.open();
      printWindow.document.write(fullHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 500);
      };
    };
    // printWindow.close();

    // If English, generate directly
    if (!language || language === 'english') {
      generatePDF(contentToTranslate, 'english');
      return;
    }

    // Otherwise, translate first then generate
    this.isLoading = true;
    this.aiService.translateReport(contentToTranslate, language).subscribe({
      next: (res: any) => {
        const translated = res?.data?.html ?? res?.html ?? res;
        generatePDF(translated, language);
        // printWindow.close();
      },
      error: (err: any) => {
        console.error('Translation failed:', err);
        printWindow.close(); // Close the loading window
        this.toast.add({
          severity: 'error',
          summary: 'Translation Failed',
          detail: 'Could not translate report. Please try again.'
        });
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  selectedFiles: File[] = [];

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) {
      return;
    }

    this.processSelectedReportFiles(files);
    input.value = '';
  }

  onDocDropzoneDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.docDropzoneDragDepth++;
    this.docDropzoneActive = true;
  }

  onDocDropzoneDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.docDropzoneActive = true;
  }

  onDocDropzoneDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.docDropzoneDragDepth = Math.max(0, this.docDropzoneDragDepth - 1);
    if (this.docDropzoneDragDepth === 0) {
      this.docDropzoneActive = false;
    }
  }

  onDocDropzoneDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.docDropzoneDragDepth = 0;
    this.docDropzoneActive = false;

    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) {
      return;
    }

    this.processSelectedReportFiles(files);
  }

  private processSelectedReportFiles(files: File[]) {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const maxFileSize = PrescriptionsService.REPORT_UPLOAD_MAX_BYTES;
    const validFiles: File[] = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        this.toast.add({
          severity: 'error',
          summary: 'Invalid File Type',
          detail: `File '${file.name}' was rejected. Only PDF, JPG, and PNG are allowed.`
        });
        continue;
      }

      if (file.size > maxFileSize) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        this.toast.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: `File '${file.name}' (${fileSizeMB} MB) exceeds the ${PrescriptionsService.REPORT_UPLOAD_MAX_MB} MB limit.`
        });
        continue;
      }

      validFiles.push(file);
    }

    if (!validFiles.length) {
      return;
    }

    this.pendingFiles = validFiles;

    this.toast.add({
      severity: 'info',
      summary: 'Files Ready',
      detail: `${this.pendingFiles.length} file(s) are ready to be uploaded.`
    });
  }

  onUploadSelectedFiles() {
    if (!this.pendingFiles?.length) {
      this.toast.add({
        severity: 'warn',
        summary: 'No Files',
        detail: 'Please select valid files before uploading.'
      });
      return;
    }

    const patientId = Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
    if (!patientId) {
      this.toast.add({
        severity: 'warn',
        summary: 'Patient Missing',
        detail: 'Select a patient before uploading reports.'
      });
      return;
    }

    const prescriptionId = Number(this.prescription?.id ?? 0);
    const formData = this.prescriptionsService.buildReportUploadFormData({
      patient_id: patientId,
      prescription_id: prescriptionId > 0 ? prescriptionId : undefined,
      report_type: this.pendingReportType,
      source_type: 'lab',
      files: this.pendingFiles,
    });

    this.isUploadingReports = true;
    this.prescriptionsService.uploadReports(formData).subscribe({
      next: (res: any) => {
        this.isUploadingReports = false;
        if (res?.success) {
          const uploaded = Array.isArray(res?.data) ? res.data : [];
          this.pendingFiles = [];
          this.refreshPatientReports();
          this.autoAnalyzeUploadedReports(uploaded);
          this.toast.add({
            severity: 'success',
            summary: 'Upload Complete',
            detail: this.aiEnabled
              ? `${uploaded.length} file(s) uploaded. AI analysis started.`
              : `${uploaded.length} file(s) uploaded.`,
          });
          return;
        }

        this.toast.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: res?.message || 'Unable to upload reports.'
        });
      },
      error: (err) => {
        this.isUploadingReports = false;
        this.toast.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: err?.error?.message || 'Unable to upload reports.'
        });
      }
    });
  }

  saveTextReport() {
    this.applyTextReportCapitalization();

    const patientId = Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
    const prescriptionId = Number(this.prescription?.id ?? 0);
    const text = String(this.reportTextContent || '').trim();
    const title = String(this.reportTextTitle || '').trim();

    if (!patientId) {
      this.toast.add({
        severity: 'warn',
        summary: 'Patient Missing',
        detail: 'Select a patient before saving a text report.'
      });
      return;
    }

    if (!text) {
      this.toast.add({
        severity: 'warn',
        summary: 'No Text',
        detail: 'Enter report text before saving.'
      });
      return;
    }

    if (isTextReportOverLimit(text, this.textReportMaxLength)) {
      this.toast.add({
        severity: 'error',
        summary: 'Text Too Long',
        detail: `Text reports are limited to ${this.textReportMaxLength.toLocaleString()} characters.`,
      });
      return;
    }

    this.isSavingTextReport = true;
    this.prescriptionsService.uploadTextReport({
      patient_id: patientId,
      prescription_id: prescriptionId > 0 ? prescriptionId : undefined,
      report_title: title || undefined,
      report_text: text,
      report_type: this.pendingReportType || undefined,
    }).subscribe({
      next: (res: any) => {
        this.isSavingTextReport = false;
        if (res?.success) {
          const uploaded = Array.isArray(res?.data) ? res.data : [];
          this.refreshPatientReports();
          this.autoAnalyzeUploadedReports(uploaded);
          this.reportTextTitle = '';
          this.reportTextContent = '';
          this.toast.add({
            severity: 'success',
            summary: 'Saved',
            detail: this.aiEnabled
              ? 'Text report saved. AI analysis started.'
              : 'Text report saved.',
          });
          return;
        }

        this.toast.add({
          severity: 'error',
          summary: 'Save Failed',
          detail: res?.message || 'Unable to save text report.'
        });
      },
      error: (err) => {
        this.isSavingTextReport = false;
        this.toast.add({
          severity: 'error',
          summary: 'Save Failed',
          detail: err?.error?.message || 'Unable to save text report.'
        });
      }
    });
  }

  canOpenReportDocument(file: any): boolean {
    if (!file) return false;

    if (isTextReportFile(file)) {
      return canOpenTextReport(file);
    }

    return !!file?.url;
  }

  isTextReport(file: any): boolean {
    return isTextReportFile(file);
  }

  isImageReport(file: any): boolean {
    return !this.isTextReport(file) && String(file?.type || '').startsWith('image/');
  }

  getTextReportBody(file: any = this.viewingTextReport): string {
    return getTextReportBody(file);
  }

  getTextReportExcerpt(file: any, maxLength = 140): string {
    return getTextReportExcerpt(file, maxLength);
  }

  getTextReportLineCount(file: any): number {
    return getTextReportLineCount(file);
  }

  getTextReportDraftCount(): number {
    return getTextReportCharCount(this.reportTextContent);
  }

  isTextReportContentOverLimit(): boolean {
    return isTextReportOverLimit(this.reportTextContent, this.textReportMaxLength);
  }

  getTextReportCounterLabel(): string {
    return formatTextReportCounter(this.getTextReportDraftCount(), this.textReportMaxLength);
  }

  openTextReportViewer(file: any): void {
    if (!isTextReportFile(file)) {
      return;
    }

    const localText = getTextReportBody(file);
    if (localText) {
      this.viewingTextReport = file;
      this.textReportViewerLoading = false;
      this.textReportViewerVisible = true;
      return;
    }

    const reportId = Number(file?.id ?? 0);
    if (!reportId) {
      return;
    }

    this.viewingTextReport = file;
    this.textReportViewerLoading = true;
    this.textReportViewerVisible = true;

    this.prescriptionsService.getReportById(reportId).subscribe({
      next: (res: any) => {
        this.textReportViewerLoading = false;
        if (!res?.success || !res?.data) {
          this.toast.add({
            severity: 'error',
            summary: 'Load Failed',
            detail: res?.message || 'Unable to load text report.',
          });
          return;
        }

        this.viewingTextReport = res.data;
        this.upsertReportFileInGallery(res.data);
      },
      error: (err) => {
        this.textReportViewerLoading = false;
        this.toast.add({
          severity: 'error',
          summary: 'Load Failed',
          detail: err?.error?.message || 'Unable to load text report.',
        });
      },
    });
  }

  closeTextReportViewer(): void {
    this.textReportViewerVisible = false;
    this.textReportViewerLoading = false;
    this.viewingTextReport = null;
  }

  private upsertReportFileInGallery(updated: any): void {
    const reportId = Number(updated?.id ?? 0);
    if (!reportId) {
      return;
    }

    for (const group of this.uploadsByDate || []) {
      for (let index = 0; index < (group.files || []).length; index++) {
        if (Number(group.files[index]?.id) === reportId) {
          group.files[index] = { ...group.files[index], ...updated };
          this.syncReportsViewModel();
          return;
        }
      }
    }
  }

  copyTextReportContent(): void {
    const content = getTextReportBody(this.viewingTextReport);
    if (!content) {
      return;
    }

    navigator.clipboard?.writeText(content).then(() => {
      this.toast.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Report text copied to clipboard.',
      });
    }).catch(() => {
      this.toast.add({
        severity: 'warn',
        summary: 'Copy failed',
        detail: 'Unable to copy report text.',
      });
    });
  }

  openTextReportAnalysisFromViewer(): void {
    const file = this.viewingTextReport;
    if (!file?.id) {
      return;
    }

    this.closeTextReportViewer();
    this.openReportAnalysis(file);
  }

  openReportDocument(file: any = this.selectedReportFile) {
    if (!this.canOpenReportDocument(file)) return;

    if (isTextReportFile(file)) {
      this.openTextReportViewer(file);
      return;
    }

    this.openFile(file);
  }

  openFile(file: any) {
    if (!file) return;

    if (isTextReportFile(file)) {
      this.openTextReportViewer(file);
      return;
    }

    if (!file.url) return;

    const contentType = String(file.type || '').toLowerCase();

    // 🔹 If it's an image or PDF, open it directly
    if (contentType.startsWith('image/') || contentType === 'application/pdf') {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
        <html>
          <head>
            <title>${file.name}</title>
          </head>
          <body style="margin:0;display:flex;justify-content:center;align-items:center;background:#111;">
            <embed src="${file.url}" 
                   type="${file.type}" 
                   style="width:100%;height:100vh;object-fit:contain;" />
          </body>
        </html>
      `);
        newWindow.document.close();
      }
    }
    // 🔹 For other file types (docx, txt, etc.), trigger download instead
    else {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.click();
    }
  }

  deleteUploadedReport(file: any, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    const target = (event?.currentTarget || event?.target) as HTMLElement | undefined;
    const reportId = Number(file?.id ?? file?.report_id);
    const isSavedReport = Number.isFinite(reportId) && reportId > 0;

    this.confirmationService.confirm({
      key: 'reportDeleteConfirm',
      target,
      message: isSavedReport
        ? 'Delete this report permanently?'
        : 'Remove this unsaved report from the current screen?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'report-confirm-accept',
      rejectButtonStyleClass: 'report-confirm-reject',
      accept: () => {
        if (!isSavedReport) {
          this.removeLocalReportFromGallery(file);
          this.toast.add({
            severity: 'success',
            summary: 'Removed',
            detail: 'Unsaved report removed from the current screen.'
          });
          return;
        }

        this.prescriptionsService.deleteReport(reportId).subscribe({
          next: (res: any) => {
            if (res?.success) {
              this.removeReportFromGallery(reportId);
              this.toast.add({
                severity: 'success',
                summary: 'Deleted',
                detail: 'Report deleted successfully.'
              });
              return;
            }

            this.toast.add({
              severity: 'error',
              summary: 'Delete Failed',
              detail: res?.message || 'Unable to delete report.'
            });
          },
          error: (err) => {
            this.toast.add({
              severity: 'error',
              summary: 'Delete Failed',
              detail: err?.error?.message || 'Unable to delete report.'
            });
          }
        });
      }
    });
  }

  private removeLocalReportFromGallery(fileToRemove: any) {
    this.uploadsByDate = (this.uploadsByDate || [])
      .map((group: any) => ({
        ...group,
        files: (group?.files || []).filter((f: any) => f !== fileToRemove)
      }))
      .filter((group: any) => (group?.files || []).length > 0);
    this.syncReportsViewModel();
  }

  private removeReportFromGallery(reportId: number) {
    this.uploadsByDate = (this.uploadsByDate || [])
      .map((group: any) => ({
        ...group,
        files: (group?.files || []).filter((f: any) => Number(f?.id) !== reportId)
      }))
      .filter((group: any) => (group?.files || []).length > 0);
    this.syncReportsViewModel();
  }

  isFormValid(): boolean {
    return this.getValidationIssues().length === 0;
  }

  get validationIssues(): string[] {
    return this.getValidationIssues(this.showSchedulePicker ? 'schedule' : undefined);
  }

  private getValidationIssues(mode?: 'pharmacy' | 'case' | 'schedule'): string[] {
    const issues: string[] = [];
    const prescriptions = Array.isArray(this.prescription?.prescriptions) ? this.prescription.prescriptions : [];
    const hasPatientSelection = this.patientSelectionConfirmed && Number(this.patient?.patient_id || 0) > 0;

    if (!hasPatientSelection) {
      issues.push('Select a patient from queue or search before saving.');
    }

    if (!String(this.prescription?.chief_complaints || '').trim()) {
      issues.push('Add at least one chief complaint.');
    }

    if (!this.diagnosisTags || this.diagnosisTags.length === 0) {
      issues.push('Add at least one diagnosis.');
    }

    if (this.prescription?.visit_fees === null || this.prescription?.visit_fees === undefined || Number(this.prescription.visit_fees) < 0) {
      issues.push('Visit fees must be zero or more.');
    }

    if (mode === 'case' && this.isPrescriptionEmpty()) {
      // Do not require prescriptions if it's completely empty and we're just saving the case
    } else {
      if (!prescriptions.length) {
        issues.push('Add at least one prescription.');
      }

      prescriptions.forEach((p: any, pIndex: number) => {
        const rxNum = pIndex + 1;
        if (!(p?.title || '').toString().trim()) {
          issues.push(`Rx ${rxNum}: title is required.`);
        }
        if (!(p?.duration || '').toString().trim()) {
          issues.push(`Rx ${rxNum}: duration is required.`);
        }

        const medicines = Array.isArray(p?.medicines) ? p.medicines : [];
        const hasValidMedicine = medicines.some((m: any) => {
          const hasTags = Array.isArray(m?.medicineTags) && m.medicineTags.length > 0;
          const hasDose = (m?.drams ?? '').toString().trim() !== '';
          const hasSchedule = (m?.ballers ?? '').toString().trim() !== '';
          return hasTags && hasDose && hasSchedule;
        });

        if (!hasValidMedicine) {
          issues.push(`Rx ${rxNum}: add at least one medicine with dose and schedule.`);
        }

        // Unresolved medicines should be shown in UI, but not block save actions.
      });
    }

    if (mode === 'schedule' && !this.scheduledDate) {
      issues.push('Select schedule date before confirming.');
    }

    return Array.from(new Set(issues));
  }

  private ensureActionCanProceed(mode: 'pharmacy' | 'case' | 'schedule'): boolean {
    if (this.getValidationIssues(mode).length === 0) return true;

    this.focusFirstInvalidField(mode);
    return false;
  }

  private focusFirstInvalidField(mode: 'pharmacy' | 'case' | 'schedule') {
    const target = this.getFirstInvalidTarget(mode);
    if (!target) return;

    if (target.id === 'patient-selection') {
      this.openQueueSidebar();
      this.sidebarTab = 'queue';
      this.flashValidationTargetById('patient-selection');
      return;
    }

    this.flashValidationTargetById(target.id);
  }

  private getFirstInvalidTarget(mode: 'pharmacy' | 'case' | 'schedule'): { id: string } | null {
    const prescriptions = Array.isArray(this.prescription?.prescriptions) ? this.prescription.prescriptions : [];
    const hasPatientSelection = this.patientSelectionConfirmed && Number(this.patient?.patient_id || 0) > 0;

    if (!hasPatientSelection) return { id: 'patient-selection' };
    if (!String(this.prescription?.chief_complaints || '').trim()) return { id: 'chief-complaints' };
    if (!this.diagnosisTags || this.diagnosisTags.length === 0) return { id: 'diagnosis' };
    if (this.prescription?.visit_fees === null || this.prescription?.visit_fees === undefined || Number(this.prescription.visit_fees) < 0) {
      return { id: 'visit-fees' };
    }
    if (!(mode === 'case' && this.isPrescriptionEmpty())) {
      if (!prescriptions.length) return { id: 'add-prescription-btn' };

      for (let pIndex = 0; pIndex < prescriptions.length; pIndex += 1) {
        const p = prescriptions[pIndex];
        if (!(p?.title || '').toString().trim()) return { id: `rx-${pIndex}-title` };
        if (!(p?.duration || '').toString().trim()) return { id: `rx-${pIndex}-duration` };

        const medicines = Array.isArray(p?.medicines) ? p.medicines : [];
        if (!medicines.length) return { id: `rx-${pIndex}-medicine-0` };

        for (let mIndex = 0; mIndex < medicines.length; mIndex += 1) {
          const m = medicines[mIndex];
          const hasTags = Array.isArray(m?.medicineTags) && m.medicineTags.length > 0;
          const hasDose = (m?.drams ?? '').toString().trim() !== '';
          const hasSchedule = (m?.ballers ?? '').toString().trim() !== '';
          if (!hasTags) return { id: `rx-${pIndex}-medicine-${mIndex}` };
          if (!hasDose) return { id: `rx-${pIndex}-dose-${mIndex}` };
          if (!hasSchedule) return { id: `rx-${pIndex}-schedule-${mIndex}` };
        }
      }
    }

    if (mode === 'schedule' && !this.scheduledDate) return { id: 'schedule-date' };
    return null;
  }

  private flashValidationTargetById(id: string) {
    const focusNow = () => {
      const el = document.querySelector(`[data-validation-id="${id}"]`) as HTMLElement | null;
      if (!el) return;

      document.querySelectorAll('.rx-focus-highlight').forEach((node) => node.classList.remove('rx-focus-highlight'));

      el.classList.add('rx-focus-highlight');
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

      const focusable = (
        el.matches('input,textarea,select,[tabindex]') ? el : el.querySelector('input, textarea, select, .p-inputtext')
      ) as HTMLElement | null;
      focusable?.focus({ preventScroll: true });

      if (this.validationHighlightTimer) clearTimeout(this.validationHighlightTimer);
      this.validationHighlightTimer = setTimeout(() => {
        el.classList.remove('rx-focus-highlight');
      }, 2200);
    };

    if (id === 'patient-selection') {
      setTimeout(focusNow, 180);
      return;
    }
    focusNow();
  }

  private hasAnyDraftContent(): boolean {
    const prescriptions = Array.isArray(this.prescription?.prescriptions) ? this.prescription.prescriptions : [];
    const hasTaggedMedicine = prescriptions.some((p: any) =>
      (Array.isArray(p?.medicines) ? p.medicines : []).some((m: any) =>
        Array.isArray(m?.medicineTags) && m.medicineTags.length > 0
      )
    );

    return hasTaggedMedicine ||
      !!(this.prescription?.notes || '').toString().trim() ||
      !!(this.prescription?.special_instructions || '').toString().trim() ||
      !!(this.prescription?.chief_complaints || '').toString().trim() ||
      (Array.isArray(this.diagnosisTags) && this.diagnosisTags.length > 0) ||
      (Array.isArray(this.reportTags) && this.reportTags.length > 0) ||
      (Array.isArray(this.selectedIcdCodes) && this.selectedIcdCodes.length > 0);
  }

  private getDraftQuery() {
    const patientId = Number(this.patient?.patient_id || this.patient?.id || 0);
    const appointmentId = this.patientSelectedFromSearch
      ? 0
      : Number(this.prescription?.appointment_id || 0);

    if (appointmentId <= 0 && patientId <= 0) {
      return null;
    }

    return {
      context_type: 'prescription' as const,
      appointment_id: appointmentId > 0 ? appointmentId : null,
      patient_id: patientId > 0 ? patientId : null,
      patient_selected_from_search: this.patientSelectedFromSearch
    };
  }

  private getDraftClearQuery() {
    const patientId = Number(this.patient?.patient_id || this.patient?.id || 0);
    const appointmentId = this.patientSelectedFromSearch
      ? 0
      : Number(this.prescription?.appointment_id || 0);

    if (appointmentId <= 0 && patientId <= 0) {
      return null;
    }

    return {
      context_type: 'prescription' as const,
      appointment_id: appointmentId > 0 ? appointmentId : null,
      patient_id: patientId > 0 ? patientId : null
    };
  }

  private buildDraftPayload() {
    const draftPrescription = {
      ...this.prescription,
      appointment_id: this.patientSelectedFromSearch
        ? 0
        : Number(this.prescription?.appointment_id || 0)
    };

    return {
      saved_at: new Date().toISOString(),
      prescription: draftPrescription,
      diagnosisTags: this.diagnosisTags,
      reportTags: this.reportTags,
      selectedIcdCodes: this.selectedIcdCodes,
      scheduledDate: this.scheduledDate,
      showSchedulePicker: this.showSchedulePicker
    };
  }

  private persistDraftSnapshot(
    query: {
      context_type: 'prescription';
      appointment_id?: number | null;
      patient_id?: number | null;
      patient_selected_from_search?: boolean;
    },
    force = false
  ) {
    if (!force && this.isDraftAutosavePausedUntilChange) return;
    if (!query.patient_id && !query.appointment_id) return;
    if (!force && !this.hasAnyDraftContent()) return;

    const draft = this.buildDraftPayload();

    this.draftSaveState = 'saving';
    this.draftSaveLabel = 'Saving draft...';

    this.consultationDraftService.saveDraft(query, draft).subscribe({
      next: () => {
        const savedAt = new Date().toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });
        this.draftSaveState = 'saved';
        this.draftSaveLabel = `Draft saved at ${savedAt}`;
        this.canClearDraftFlag = true;
      },
      error: () => {
        this.draftSaveState = 'error';
        this.draftSaveLabel = 'Draft save failed';
      }
    });
  }

  private safeStringify(obj: any): string {
    const cache = new Set();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.has(value)) {
          return undefined;
        }
        cache.add(value);
      }
      return value;
    });
  }

  private saveDraftSnapshot(force = false) {
    const query = this.getDraftQuery();
    if (!query) return;
    this.persistDraftSnapshot(query, force);
  }

  private draftMatchesCurrentContext(data: any): boolean {
    if (!data) return false;

    const currentPatientId = Number(this.patient?.patient_id || this.patient?.id || 0);
    const currentAppointmentId = this.patientSelectedFromSearch
      ? 0
      : Number(this.prescription?.appointment_id || 0);
    const scopeType = String(data.scope_type || '');
    const scopeId = Number(data.scope_id || 0);

    if (this.patientSelectedFromSearch) {
      return scopeType === 'patient' && scopeId === currentPatientId;
    }

    if (currentAppointmentId > 0) {
      return scopeType === 'appointment' && scopeId === currentAppointmentId;
    }

    return scopeType === 'patient' && scopeId === currentPatientId;
  }

  private restoreDraftForCurrentContext(
    showToast = false,
    resumeAutosaveAfter = false,
    expectedPatientId: number | null = null
  ) {
    const query = this.getDraftQuery();
    if (!query) {
      this.canClearDraftFlag = false;
      if (resumeAutosaveAfter) {
        this.isDraftAutosaveSuspended = false;
      }
      return;
    }

    this.consultationDraftService.getDraft(query).subscribe({
      next: (res: any) => {
        if (expectedPatientId !== null && Number(this.patient?.patient_id || 0) !== expectedPatientId) {
          return;
        }

        if (!this.draftMatchesCurrentContext(res?.data)) {
          this.canClearDraftFlag = false;
          return;
        }

        const parsed = res?.data?.payload;
        if (!parsed?.prescription) {
          this.canClearDraftFlag = false;
          return;
        }
        if (!this.isSavedToday(parsed?.saved_at)) {
          this.canClearDraftFlag = false;
          return;
        }

        const draftPrescription = parsed.prescription || {};
        this.prescription = {
          ...this.prescription,
          ...draftPrescription,
          prescriptions: this.normalizePrescriptionRows(draftPrescription.prescriptions ?? this.prescription?.prescriptions),
          chief_complaints: this.normalizeComplaintTextarea(draftPrescription.chief_complaints ?? this.prescription?.chief_complaints)
        };
        this.applyClinicalTextCapitalization();
        this.diagnosisTags = this.normalizeDiagnosisTags(
          Array.isArray(parsed.diagnosisTags) ? parsed.diagnosisTags : this.diagnosisTags
        );
        this.reportTags = this.normalizeReportTags(
          Array.isArray(parsed.reportTags) ? parsed.reportTags : this.reportTags
        );
        this.selectedIcdCodes = this.normalizeSelectedIcdCodes(parsed.selectedIcdCodes);
        this.scheduledDate = parsed.scheduledDate || this.scheduledDate;
        this.showSchedulePicker = !!parsed.showSchedulePicker;

        if (showToast) {
          this.toast.add({
            severity: 'info',
            summary: 'Draft Restored',
            detail: 'Recovered unsaved prescription changes for this patient.'
          });
        }

        this.draftSaveState = 'saved';
        this.draftSaveLabel = 'Draft restored';
        this.canClearDraftFlag = true;
      },
      error: () => {
        this.canClearDraftFlag = false;
      },
      complete: () => {
        if (resumeAutosaveAfter) {
          this.isDraftAutosaveSuspended = false;
        }
      }
    });
  }

  private isSavedToday(savedAt: any): boolean {
    if (!savedAt) return false;
    const savedDate = new Date(savedAt);
    if (Number.isNaN(savedDate.getTime())) return false;

    const now = new Date();
    return savedDate.getFullYear() === now.getFullYear() &&
      savedDate.getMonth() === now.getMonth() &&
      savedDate.getDate() === now.getDate();
  }

  private clearDraftForCurrentContext() {
    const query = this.getDraftClearQuery();
    if (!query) return;

    this.consultationDraftService.clearDraft(query).subscribe({
      next: () => {
        this.isDraftAutosavePausedUntilChange = true;
        this.draftSaveState = 'cleared';
        this.draftSaveLabel = 'Draft cleared';
        this.canClearDraftFlag = false;
      },
      error: () => {
        // ignore
      }
    });
  }

  private refreshDraftAvailability() {
    const query = this.getDraftQuery();
    if (!query) {
      this.canClearDraftFlag = false;
      return;
    }

    this.consultationDraftService.getDraft(query).subscribe({
      next: (res: any) => {
        this.canClearDraftFlag = !!res?.data?.payload;
      },
      error: () => {
        this.canClearDraftFlag = false;
      }
    });
  }

  get draftStatusIcon(): string {
    if (this.draftSaveState === 'saving') return 'pi pi-spin pi-spinner';
    if (this.draftSaveState === 'saved') return 'pi pi-check-circle';
    if (this.draftSaveState === 'error') return 'pi pi-exclamation-circle';
    if (this.draftSaveState === 'cleared') return 'pi pi-trash';
    return 'pi pi-save';
  }

  //! =========================================== template code =======================================================

  templates: any[] = [];
  templatesLoading = false;
  private searchDebounceTimer: any = null;
  showSaveTemplateDialog = false;
  showLoadTemplateDialog = false;
  templateName = '';
  saveTemplateAsNew = false;
  templateSaving = false;
  renamingTemplateId: number | null = null;
  renamingTemplateName = '';
  templateRenameSavingId: number | null = null;
  searchQuery = '';
  selectedAlphabet = 'A';
  templateSortMode: 'alpha' | 'recent' | 'most_used' = 'alpha';
  filteredTemplates: any[] = [];
  activePrescriptionIndex: number | null = null;
  pinnedTemplateIds = new Set<number>();
  templateUsageCount: Record<number, number> = {};
  templateLastUsedAt: Record<number, string> = {};

  private readonly pinnedTemplatesStorageKey = 'rx_template_pins';
  private readonly templateUsageStorageKey = 'rx_template_usage';
  private readonly templateLastUsedStorageKey = 'rx_template_last_used';

  // openSaveTemplateDialog() {
  //   this.templateName = '';
  //   this.showSaveTemplateDialog = true;
  // }

  /**
   * Fetches prescription templates from the backend server.
   * Performs dynamic server-side filtering using the current selectedAlphabet and searchQuery.
   * If both are empty, defaults to fetching templates starting with the letter 'A'.
   */
  getTemplates() {
    let alphabetVal = this.selectedAlphabet;
    let searchVal = this.searchQuery;

    // Default to 'A' if no specific alphabet or search query is set
    if (!alphabetVal && !searchVal) {
      alphabetVal = 'A';
    }

    // Enable template loading state
    this.templatesLoading = true;

    this.templateService.getTemplates(alphabetVal, searchVal).subscribe({
      next: (res: any) => {
        // Safe assignment of templates list
        this.templates = res.data || [];
        // Apply sorting and local arrangements
        this.applyTemplateFilters();
        // Turn off loading state
        this.templatesLoading = false;
      },
      error: (err) => {
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load templates.',
        });
        console.error(err);
        // Turn off loading state in case of error
        this.templatesLoading = false;
      },
    });
  }

  openSaveTemplateDialog(event: Event, index: number) {
    this.activePrescriptionIndex = index;
    this.saveTemplateAsNew = false;
    const linked = this.getLinkedTemplate(index);
    this.templateName = linked?.name || '';
    this.showSaveTemplateDialog = true;
  }

  isRxLinkedToTemplate(index: number): boolean {
    return !!this.getLinkedTemplate(index);
  }

  getRxLinkedTemplateName(index: number): string {
    return this.getLinkedTemplate(index)?.name || '';
  }

  unlinkTemplateFromRx(index: number, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    const rx = this.prescription?.prescriptions?.[index];
    if (!rx) return;
    delete rx.sourceTemplateId;
    delete rx.sourceTemplateName;
  }

  isUpdatingExistingTemplate(): boolean {
    if (this.saveTemplateAsNew) return false;
    if (this.activePrescriptionIndex === null) return false;
    return this.isRxLinkedToTemplate(this.activePrescriptionIndex);
  }

  private getLinkedTemplate(index: number): { id: number; name: string } | null {
    const rx = this.prescription?.prescriptions?.[index];
    const id = Number(rx?.sourceTemplateId);
    if (!id) return null;
    return {
      id,
      name: String(rx?.sourceTemplateName || ''),
    };
  }

  private linkTemplateToRx(rx: any, template: any) {
    if (!rx || !template) return;
    const id = Number(template.id);
    if (!id) return;
    rx.sourceTemplateId = id;
    rx.sourceTemplateName = String(template.name || '');
  }

  private syncLinkedTemplateName(templateId: number, name: string) {
    const prescriptions = Array.isArray(this.prescription?.prescriptions)
      ? this.prescription.prescriptions
      : [];
    prescriptions.forEach((rx: any) => {
      if (Number(rx?.sourceTemplateId) === Number(templateId)) {
        rx.sourceTemplateName = name;
      }
    });
  }

  loadTemplate(template: any) {
    // this.prescription.prescriptions = JSON.parse(
    //   JSON.stringify(template.prescriptions)
    // );
    if (this.activePrescriptionIndex === null) return;

    const sourcePrescription = JSON.parse(JSON.stringify(template.prescriptions?.[0] || null));
    if (!sourcePrescription) return;
    const normalizedSourcePrescription = this.normalizePrescriptionRows([sourcePrescription])[0];

    this.linkTemplateToRx(normalizedSourcePrescription, template);

    if (this.templateApplyMode === 'append') {
      const nextId =
        (Array.isArray(this.prescription?.prescriptions) && this.prescription.prescriptions.length
          ? Math.max(
            0,
            ...this.prescription.prescriptions
              .map((p: any) => Number(p?.id))
              .filter((n: number) => Number.isFinite(n))
          ) + 1
          : 1);

      normalizedSourcePrescription.id = nextId;
      this.prescription.prescriptions.push(normalizedSourcePrescription);
      this.activePrescriptionIndex = this.prescription.prescriptions.length - 1;
    } else {
      this.prescription.prescriptions[this.activePrescriptionIndex] = normalizedSourcePrescription;
    }

    this.trackTemplateUsage(template);
    this.showLoadTemplateDialog = false;

    this.toast.add({
      severity: 'success',
      summary: 'Loaded',
      detail: this.templateApplyMode === 'append'
        ? 'Template appended as a new Rx. You can update the original template when you are done.'
        : 'Template loaded. Edit the Rx, then click Update Template to save changes.',
      life: 4000,
    });
  }

  saveTemplate() {
    if (this.templateSaving) return;

    // Validate template name
    if (!this.templateName?.trim()) {
      this.toast.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Please enter a template name',
        life: 3000
      });
      return;
    }

    // Ensure index is defined
    if (this.activePrescriptionIndex === null || this.activePrescriptionIndex === undefined) {
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No prescription selected to save.',
        life: 3000
      });
      return;
    }

    const selectedPrescription = this.prescription.prescriptions[this.activePrescriptionIndex];

    // Validate that this prescription has at least one medicine
    const hasValidMedicine = selectedPrescription.medicines.some(
      (med: any) => med.medicineTags && med.medicineTags.length > 0
    );

    if (!hasValidMedicine) {
      this.toast.add({
        severity: 'warn',
        summary: 'Invalid',
        detail: 'Please add at least one medicine before saving template',
        life: 3000
      });
      return;
    }

    const linked = this.getLinkedTemplate(this.activePrescriptionIndex);
    const shouldUpdate = !!linked && !this.saveTemplateAsNew;
    const payload: any = {
      name: this.templateName.trim(),
      prescriptions: [this.clonePrescriptionForTemplate(selectedPrescription)],
    };

    this.templateSaving = true;

    const request$ = shouldUpdate && linked
      ? this.templateService.updateTemplate({ ...payload, id: linked.id })
      : this.templateService.addTemplate(payload);

    request$.subscribe({
      next: (res: any) => {
        const saved = res.data;
        if (shouldUpdate) {
          this.replaceTemplateInLists(saved);
        } else if (saved) {
          this.templates.unshift(saved);
        }
        this.applyTemplateFilters();
        this.linkTemplateToRx(selectedPrescription, saved || { id: linked?.id, name: payload.name });
        this.templateName = '';
        this.saveTemplateAsNew = false;
        this.templateSaving = false;
        this.showSaveTemplateDialog = false;

        this.toast.add({
          severity: 'success',
          summary: shouldUpdate ? 'Updated' : 'Success',
          detail: shouldUpdate
            ? 'Template updated successfully!'
            : 'Template saved successfully!',
          life: 3000
        });
      },
      error: (err) => {
        console.error(err);
        this.templateSaving = false;
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: shouldUpdate ? 'Failed to update template.' : 'Failed to save template.',
          life: 3000
        });
      }
    });
  }

  editTemplate(template: any) {
    if (this.activePrescriptionIndex === null) return;
    this.loadTemplate(template);
  }

  isRenamingTemplate(template: any): boolean {
    return this.renamingTemplateId === Number(template?.id);
  }

  isTemplateRenameSaving(template: any): boolean {
    return this.templateRenameSavingId === Number(template?.id);
  }

  startRenameTemplate(template: any, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    this.renamingTemplateId = Number(template?.id) || null;
    this.renamingTemplateName = String(template?.name || '');
    setTimeout(() => {
      const el = document.querySelector('.template-rename-input') as HTMLInputElement | null;
      el?.focus();
      el?.select();
    });
  }

  cancelRenameTemplate(event?: Event) {
    event?.stopPropagation();
    this.renamingTemplateId = null;
    this.renamingTemplateName = '';
    this.templateRenameSavingId = null;
  }

  commitRenameTemplate(template: any, event?: Event) {
    event?.stopPropagation();

    const id = Number(template?.id);
    if (this.templateRenameSavingId === id) return;

    const inputValue = event?.target instanceof HTMLInputElement
      ? event.target.value
      : this.renamingTemplateName;
    const nextName = String(inputValue || '').trim();
    if (!id) {
      this.cancelRenameTemplate();
      return;
    }

    if (!nextName) {
      this.toast.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Template name cannot be empty',
        life: 3000,
      });
      return;
    }

    if (nextName === String(template?.name || '').trim()) {
      if (this.renamingTemplateId === id) {
        this.cancelRenameTemplate();
      }
      return;
    }

    this.templateRenameSavingId = id;
    this.templateService.updateTemplate({ id, name: nextName }).subscribe({
      next: (res: any) => {
        const saved = res.data || { ...template, name: nextName };
        this.replaceTemplateInLists(saved);
        this.syncLinkedTemplateName(id, nextName);
        const firstLetter = nextName.charAt(0).toUpperCase();
        if (this.selectedAlphabet && firstLetter !== this.selectedAlphabet && /[A-Z]/.test(firstLetter)) {
          this.selectedAlphabet = firstLetter;
          this.getTemplates();
        } else {
          this.applyTemplateFilters();
        }
        this.templateRenameSavingId = this.templateRenameSavingId === id ? null : this.templateRenameSavingId;
        if (this.renamingTemplateId === id) {
          this.renamingTemplateId = null;
          this.renamingTemplateName = '';
        }
        this.toast.add({
          severity: 'success',
          summary: 'Renamed',
          detail: 'Template name updated.',
          life: 2500,
        });
      },
      error: (err) => {
        console.error(err);
        this.templateRenameSavingId = this.templateRenameSavingId === id ? null : this.templateRenameSavingId;
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to rename template.',
          life: 3000,
        });
      },
    });
  }

  onTemplateRenameKeydown(event: KeyboardEvent, template: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitRenameTemplate(template, event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelRenameTemplate(event);
    }
  }

  private replaceTemplateInLists(saved: any) {
    if (!saved?.id) return;
    const merge = (list: any[]) => {
      const idx = list.findIndex((t) => Number(t?.id) === Number(saved.id));
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...saved };
      }
    };
    merge(this.templates);
    if (Array.isArray(this.filteredTemplates)) {
      merge(this.filteredTemplates);
    }
  }

  private clonePrescriptionForTemplate(selectedPrescription: any) {
    const omitKeys = new Set([
      'displayNameHtml',
      'displayTitleHtml',
      'displayDramsHtml',
      'displayBallersHtml',
      'displayPotencyHtml',
      'displayCodeHtml',
      'filteredMedicines',
      'filteredDoses',
      'sourceTemplateId',
      'sourceTemplateName',
      'is_from_history',
    ]);

    return JSON.parse(JSON.stringify(selectedPrescription, (key, value) => {
      return omitKeys.has(key) ? undefined : value;
    }));
  }

  deleteTemplate(template: any) {
    this.templateService.deleteTemplate(template.id).subscribe({
      next: () => {
        // 🔥 Instantly remove the deleted template from the displayed list
        this.templates = this.templates.filter((t) => t.id !== template.id);

        // Optional: also handle filteredTemplates if you’re using a search or alphabet filter
        if (this.filteredTemplates && this.filteredTemplates.length) {
          this.filteredTemplates = this.filteredTemplates.filter((t) => t.id !== template.id);
        }

        this.pinnedTemplateIds.delete(Number(template.id));
        delete this.templateUsageCount[Number(template.id)];
        delete this.templateLastUsedAt[Number(template.id)];
        const deletedId = Number(template.id);
        const prescriptions = Array.isArray(this.prescription?.prescriptions)
          ? this.prescription.prescriptions
          : [];
        prescriptions.forEach((rx: any, index: number) => {
          if (Number(rx?.sourceTemplateId) === deletedId) {
            this.unlinkTemplateFromRx(index);
          }
        });
        this.saveTemplateBrowserPrefs();
        this.applyTemplateFilters();

        this.toast.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Template deleted successfully!',
          life: 3000,
        });
      },
      error: (err) => {
        console.error(err);
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete template.',
          life: 3000,
        });
      },
    });
  }

  /**
   * Opens the Load Template Dialog.
   * Resets the search query, defaults selectedAlphabet to 'A', and fetches initial templates.
   * 
   * @param index The index of the active prescription row where the template will be loaded
   */
  openLoadTemplateDialog(index: number) {
    this.loadTemplateBrowserPrefs();
    this.searchQuery = '';
    this.selectedAlphabet = 'A';
    this.activePrescriptionIndex = index;
    this.cancelRenameTemplate();
    this.getTemplates();
    this.showLoadTemplateDialog = true;
  }

  /**
   * Filters templates by clicking on an alphabet button.
   * Triggers a new backend API request to retrieve templates starting with the selected letter.
   * 
   * @param letter The letter clicked (e.g. 'A', 'B', 'C')
   */
  filterByAlphabet(letter: string) {
    this.selectedAlphabet = letter;
    this.getTemplates();
  }

  /**
   * Searches templates by matching a keyword typed in the searchbox.
   * Uses debouncing to prevent flooding the backend API with requests on every keystroke.
   */
  searchTemplates() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.getTemplates();
    }, 300); // 300ms delay
  }

  setTemplateSortMode(mode: 'alpha' | 'recent' | 'most_used') {
    this.templateSortMode = mode;
    this.applyTemplateFilters();
  }

  toggleTemplatePin(template: any) {
    const id = Number(template?.id);
    if (!id) return;

    if (this.pinnedTemplateIds.has(id)) {
      this.pinnedTemplateIds.delete(id);
    } else {
      this.pinnedTemplateIds.add(id);
    }

    this.saveTemplateBrowserPrefs();
    this.applyTemplateFilters();
  }

  isTemplatePinned(template: any): boolean {
    return this.pinnedTemplateIds.has(Number(template?.id));
  }

  getTemplateUseCount(template: any): number {
    return this.templateUsageCount[Number(template?.id)] || 0;
  }

  getTemplateLastUsed(template: any): string | null {
    return this.templateLastUsedAt[Number(template?.id)] || null;
  }

  getTemplateMedicineCount(template: any): number {
    if (!Array.isArray(template?.prescriptions)) return 0;

    return template.prescriptions.reduce((acc: number, pres: any) => {
      if (!Array.isArray(pres?.medicines)) return acc;
      return acc + pres.medicines.filter((m: any) =>
        Array.isArray(m?.medicineTags) && m.medicineTags.length > 0
      ).length;
    }, 0);
  }

  /**
   * Clears the current alphabet filter and re-fetches templates.
   * Defaults back to loading templates starting with 'A' if search query is also empty.
   */
  clearTemplateLetterFilter() {
    this.selectedAlphabet = '';
    this.getTemplates();
  }

  /**
   * Clears the current searchbox input and re-fetches templates immediately.
   * Cancels any pending debounced search request.
   */
  clearTemplateSearch() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchQuery = '';
    this.getTemplates();
  }

  private applyTemplateFilters() {
    const query = this.searchQuery.trim().toLowerCase();
    const alphabet = this.selectedAlphabet.trim().toLowerCase();

    let list = [...this.templates];

    if (alphabet) {
      list = list.filter((template: any) =>
        String(template?.name || '').toLowerCase().startsWith(alphabet)
      );
    }

    if (query) {
      list = list.filter((template: any) =>
        this.buildTemplateSearchText(template).includes(query)
      );
    }

    list.sort((a: any, b: any) => this.compareTemplates(a, b));
    this.precomputeTemplateHighlights(list, this.searchQuery);
    this.filteredTemplates = list;
  }

  /**
   * Escape unsafe HTML strings to prevent breaking layout layout tags.
   */
  private escapeHtmlString(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Safe HTML highlighting precomputation.
   * Matches query case-insensitively and wraps matches in search-highlight span tags.
   *
   * @param text The input text to format
   * @param query The active search query key
   */
  private generateHighlightedSafeHtml(text: string, query: string): any {
    if (!text) return this.sanitizer.bypassSecurityTrustHtml('');
    if (!query) {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtmlString(text));
    }

    // Escape special HTML characters to prevent breaking structure
    const escapedText = this.escapeHtmlString(text);

    // Escape regex characters
    const escapedQuery = this.escapeHtmlString(query).replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    // Wrap matches in highlighted span tags
    const highlighted = escapedText.replace(regex, '<span class="search-highlight">$1</span>');
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  /**
   * Traverses template structures and pre-computes safe, highlighted HTML strings
   * exactly once when the templates are loaded or filtered, fully preventing change detection cycles.
   *
   * @param templates The list of template objects
   * @param query The active search query text
   */
  private precomputeTemplateHighlights(templates: any[], query: string) {
    if (!templates || !templates.length) return;

    const trimmedQuery = query ? query.trim() : '';

    templates.forEach(template => {
      // 1. Highlight template name
      template.displayNameHtml = this.generateHighlightedSafeHtml(template.name || '', trimmedQuery);

      if (Array.isArray(template.prescriptions)) {
        template.prescriptions.forEach((pres: any) => {
          // 2. Highlight prescription title
          pres.displayTitleHtml = this.generateHighlightedSafeHtml(pres.title || '', trimmedQuery);

          if (Array.isArray(pres.medicines)) {
            pres.medicines.forEach((med: any) => {
              // 3. Highlight dose (drams) and schedule (ballers)
              med.displayDramsHtml = this.generateHighlightedSafeHtml(med.drams || '-', trimmedQuery);
              med.displayBallersHtml = this.generateHighlightedSafeHtml(med.ballers || '-', trimmedQuery);

              // 4. Highlight medicine tags
              if (Array.isArray(med.medicineTags)) {
                med.medicineTags.forEach((tag: any) => {
                  tag.displayNameHtml = this.generateHighlightedSafeHtml(tag.name || '', trimmedQuery);
                  tag.displayPotencyHtml = this.generateHighlightedSafeHtml(tag.potency || '', trimmedQuery);
                  tag.displayCodeHtml = this.generateHighlightedSafeHtml(tag.code || '', trimmedQuery);
                });
              }
            });
          }
        });
      }
    });
  }

  private compareTemplates(a: any, b: any): number {
    const aId = Number(a?.id) || 0;
    const bId = Number(b?.id) || 0;

    const aPinned = this.pinnedTemplateIds.has(aId);
    const bPinned = this.pinnedTemplateIds.has(bId);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;

    if (this.templateSortMode === 'most_used') {
      const countDiff = (this.templateUsageCount[bId] || 0) - (this.templateUsageCount[aId] || 0);
      if (countDiff !== 0) return countDiff;
    }

    if (this.templateSortMode === 'recent' || this.templateSortMode === 'most_used') {
      const timeA = this.getTemplateRecentTime(a);
      const timeB = this.getTemplateRecentTime(b);
      if (timeA !== timeB) return timeB - timeA;
    }

    return String(a?.name || '').localeCompare(String(b?.name || ''));
  }

  private getTemplateRecentTime(template: any): number {
    const id = Number(template?.id) || 0;
    const lastUsed = this.templateLastUsedAt[id];
    if (lastUsed) {
      const parsed = new Date(lastUsed).getTime();
      if (Number.isFinite(parsed)) return parsed;
    }

    const updatedAt = new Date(template?.updated_at || template?.created_at || 0).getTime();
    return Number.isFinite(updatedAt) ? updatedAt : 0;
  }

  /**
   * Builds a search text index of a template, including name, prescription titles, medicines,
   * codes, and potency. Looks up tags in both 'tags' and 'medicineTags' arrays to be extremely robust.
   */
  private buildTemplateSearchText(template: any): string {
    const chunks: string[] = [];

    chunks.push(String(template?.name || ''));
    chunks.push(String(template?.created_at || ''));

    if (Array.isArray(template?.prescriptions)) {
      for (const pres of template.prescriptions) {
        chunks.push(String(pres?.title || ''));
        chunks.push(String(pres?.duration || ''));

        if (Array.isArray(pres?.medicines)) {
          for (const med of pres.medicines) {
            chunks.push(String(med?.drams || ''));
            chunks.push(String(med?.ballers || ''));

            // Check both tags and medicineTags arrays for robust search matching
            const tags = med?.tags || med?.medicineTags;
            if (Array.isArray(tags)) {
              for (const tag of tags) {
                chunks.push(String(tag?.name || ''));
                chunks.push(String(tag?.code || ''));
                chunks.push(String(tag?.potency || ''));
              }
            }
          }
        }
      }
    }

    return chunks.join(' ').toLowerCase();
  }


  private trackTemplateUsage(template: any) {
    const id = Number(template?.id);
    if (!id) return;

    this.templateUsageCount[id] = (this.templateUsageCount[id] || 0) + 1;
    this.templateLastUsedAt[id] = new Date().toISOString();
    this.saveTemplateBrowserPrefs();
    this.applyTemplateFilters();
  }

  private loadTemplateBrowserPrefs() {
    try {
      const pinned = localStorage.getItem(this.pinnedTemplatesStorageKey);
      if (pinned) {
        const ids = JSON.parse(pinned);
        this.pinnedTemplateIds = new Set(
          Array.isArray(ids) ? ids.map((x: any) => Number(x)).filter((x: number) => Number.isFinite(x)) : []
        );
      }
    } catch {
      this.pinnedTemplateIds = new Set<number>();
    }

    try {
      const usage = localStorage.getItem(this.templateUsageStorageKey);
      this.templateUsageCount = usage ? JSON.parse(usage) : {};
    } catch {
      this.templateUsageCount = {};
    }

    try {
      const lastUsed = localStorage.getItem(this.templateLastUsedStorageKey);
      this.templateLastUsedAt = lastUsed ? JSON.parse(lastUsed) : {};
    } catch {
      this.templateLastUsedAt = {};
    }
  }

  private saveTemplateBrowserPrefs() {
    localStorage.setItem(
      this.pinnedTemplatesStorageKey,
      JSON.stringify(Array.from(this.pinnedTemplateIds))
    );
    localStorage.setItem(
      this.templateUsageStorageKey,
      JSON.stringify(this.templateUsageCount)
    );
    localStorage.setItem(
      this.templateLastUsedStorageKey,
      JSON.stringify(this.templateLastUsedAt)
    );
  }

  confirmLoadTemplate(event: Event, template: any) {
    this.pendingTemplateConfirm = template;
    this.confirmationService.confirm({
      key: 'loadTemplatePopup',
      target: event.target as EventTarget,
      acceptLabel: this.templateApplyMode === 'append' ? 'Add as new Rx' : 'Replace Rx',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'tpl-confirm-accept',
      rejectButtonStyleClass: 'tpl-confirm-reject',
      defaultFocus: 'reject',
      accept: () => this.loadTemplate(template),
      reject: () => { this.pendingTemplateConfirm = null; }
    });
  }

  confirmDeleteTemplate(event: Event, template: any) {
    this.pendingTemplateConfirm = template;
    this.confirmationService.confirm({
      key: 'deleteTemplatePopup',
      target: event.target as EventTarget,
      acceptLabel: 'Delete template',
      rejectLabel: 'Keep',
      acceptButtonStyleClass: 'tpl-confirm-accept tpl-confirm-accept--danger',
      rejectButtonStyleClass: 'tpl-confirm-reject',
      defaultFocus: 'reject',
      accept: () => this.deleteTemplate(template),
      reject: () => { this.pendingTemplateConfirm = null; }
    });
  }

  saveTemplatesToStorage() {
    localStorage.setItem('prescriptionTemplates', JSON.stringify(this.templates));
  }

  loadTemplatesFromStorage() {
    const stored = localStorage.getItem('prescriptionTemplates');
    if (stored) {
      this.templates = JSON.parse(stored);
    }
  }

  get alphabets() {
    return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  }

  //! Save Case
  saveCase() {
    const now = new Date();
    const appointmentDate = now.toISOString().slice(0, 10);
    const hour = now.getHours().toString().padStart(2, '0');
    const startTime = `${hour}:00`;
    const endTime = `${(now.getHours() + 1).toString().padStart(2, '0')}:00`;

    const clinicId = sessionStorage.getItem('clinic_id');
    const doctorId = sessionStorage.getItem('doctor_id');

    // Appointment payload
    const appointmentPayload = {
      clinic_id: Number(clinicId) || 1,
      doctor_id: Number(doctorId) || 1,
      // clinic_id: this.clinicId,
      // doctor_id: this.doctorId,
      patient_id: this.patient.patient_id,
      appointment_date: appointmentDate,
      appointment_type: 'case',
      start_time: startTime,
      end_time: endTime,
      status: 'completed',
      type: 'on_flow',
    };

    this.appointmentService.addAppointment(appointmentPayload).subscribe({
      next: (res: any) => {
        if (res.success) {
          const appt = res.data;
          const prescription = this.prescription;

          // 🔹 Transform prescriptions before sending
          const transformedPrescriptions = this.transformPrescriptions();

          // Now create prescription linked to this appointment
          const prescriptionPayload = {
            appointment_id: appt.id,
            allergies: prescription.allergies || '',
            current_medications: prescription.current_medications || '',
            personal_family_history: prescription.personal_family_history || '',
            surgical_history: prescription.surgical_history || '',
            blood_pressure: prescription.blood_pressure || '',
            pulse_rate: prescription.pulse_rate || '',
            temperature: prescription.temperature || '',
            weight: prescription.weight || '',
            height: prescription.height || '',
            fasting_blood_sugar: prescription.fasting_blood_sugar || '',
            blood_oxygen: prescription.blood_oxygen || '',
            runny_nose: prescription.runny_nose || '',
            cough: prescription.cough || '',

            chief_complaints: this.prescription.chief_complaints || '',
            diagnosis: this.diagnosisTags.map((tag: any) => tag.diagnosis || tag),
            suggested_reports: this.reportTags.map((tag: any) => tag.report),

            // prescriptions: prescription.prescriptions || [],
            prescriptions: transformedPrescriptions || [],

            personal_dues: prescription.personal_dues || 0,
            visit_fees: prescription.visit_fees || 0,
            received_cash: prescription.received_cash || 0,
            received_online: prescription.received_online || 0,

            notes: prescription.notes || '',
            is_courier: this.normalizePrescriptionMode(prescription.is_courier),
            special_instructions: prescription.special_instructions || '',
          };

          this.prescriptionsService.addPrescriptions(prescriptionPayload).subscribe((presRes: any) => {
            if (presRes.success) {
              const prescription = presRes.data;

              // ✅ Upload reports if there are any files waiting
              if (this.pendingFiles && this.pendingFiles.length > 0) {
                const formData = new FormData();
                formData.append('patient_id', this.patient.patient_id.toString());
                formData.append('prescription_id', prescription.id.toString());
                this.pendingFiles.forEach((file: File) => {
                  formData.append('files[]', file, file.name);
                });

                this.prescriptionsService.uploadReports(formData).subscribe({
                  next: (uploadRes: any) => {
                    if (uploadRes.success) {
                      this.pendingFiles = [];
                      this.refreshPatientReports();
                      this.autoAnalyzeUploadedReports(uploadRes.data || []);
                    }
                  },
                  error: (err) => console.error('Report upload failed:', err)
                });
              }

              this.toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Prescription saved as Case successfully!',
              });

              this.clearForm();
            }
          });

          // this.clearForm();
        }
      },
      error: (err) => {
        console.error('Save error:', err);
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save appointment and prescription',
        });
      }
    });
  }

  getAppointmentClass(type: string) {
    switch (this.normalizeAppointmentType(type)) {
      case 'new':
        return 'apt-new';
      case 'follow_up':
        return 'apt-followup';
      case 'case':
        return 'apt-case';
      default:
        return 'apt-default';
    }
  }

  //! Schedule meds for later code
  toggleSchedulePicker() {
    this.showSchedulePicker = !this.showSchedulePicker;
    if (this.showSchedulePicker) {
      this.scheduledDate = null; // Reset date when opening
    }
  }

  cancelSchedule() {
    this.showSchedulePicker = false;
    this.scheduledDate = null;
  }

  clearRx(index: number, event: Event) {
    this.pendingClearRxNumber = index + 1;
    this.confirmationService.confirm({
      key: 'clearRxPopup',
      target: event.target as EventTarget,
      acceptLabel: 'Clear Rx',
      rejectLabel: 'Keep it',
      acceptButtonStyleClass: 'tpl-confirm-accept tpl-confirm-accept--danger',
      rejectButtonStyleClass: 'tpl-confirm-reject',
      defaultFocus: 'reject',
      reject: () => {
        this.pendingClearRxNumber = 0;
      },
      accept: () => {
        if (!Array.isArray(this.prescription?.prescriptions) || !this.prescription.prescriptions[index]) {
          return;
        }

        const current = this.prescription.prescriptions[index];
        const resetRx = {
          ...current,
          title: '',
          duration: '',
          medicines: [this.createEmptyMedicineRow()]
        };
        delete resetRx.sourceTemplateId;
        delete resetRx.sourceTemplateName;
        this.prescription.prescriptions[index] = resetRx;

        this.toast.add({
          severity: 'success',
          summary: 'Cleared',
          detail: `Rx ${index + 1} cleared.`
        });
      }
    });
  }

  clearCurrentDraft(event: Event) {
    const query = this.getDraftQuery();
    if (!query) {
      this.toast.add({
        severity: 'info',
        summary: 'No Draft',
        detail: 'No draft found for current patient.'
      });
      return;
    }

    this.consultationDraftService.getDraft(query).subscribe({
      next: (res: any) => {
        if (!res?.data?.payload) {
          this.canClearDraftFlag = false;
          this.toast.add({
            severity: 'info',
            summary: 'No Draft',
            detail: 'No draft found for current patient.'
          });
          return;
        }

        this.confirmationService.confirm({
          key: 'templatePopup',
          target: event.target as EventTarget,
          message: 'Clear saved draft for this patient?',
          icon: 'pi pi-trash',
          acceptButtonStyleClass: 'custom-accept-btn',
          rejectButtonStyleClass: 'custom-reject-btn',
          accept: () => {
            this.clearDraftForCurrentContext();
            this.toast.add({
              severity: 'success',
              summary: 'Draft Cleared',
              detail: 'Saved draft removed for current patient.'
            });
          }
        });
      },
      error: () => {
        this.toast.add({
          severity: 'info',
          summary: 'No Draft',
          detail: 'No draft found for current patient.'
        });
      }
    });
  }

  confirmSchedule() {
    // if (this.scheduledDate) {
    //   // Handle the scheduled date here
    //   console.log('Scheduled for:', this.scheduledDate);

    //   // Your API call or logic here
    //   // this.scheduleAppointment(this.scheduledDate);

    //   this.showSchedulePicker = false;
    //   this.scheduledDate = null;
    // }

    if (!this.ensureActionCanProceed('schedule')) return;

    if (this.scheduledDate) {
      // this.scheduleForLater();
      this.addPrescriptions('schedule');
    }
  }

  canRecallFromPharmacy(): boolean {
    const appointmentId = Number(this.prescription?.appointment_id || 0);
    const status = String(this.currentAppointmentStatus || '').toLowerCase();
    const isToday = this.isCurrentAppointmentToday();
    const isAllowedStatus = (status === '' || status === 'medicine' || status === 'reception' || status === 'medicine_scheduled');
    return appointmentId > 0
      && isToday
      && isAllowedStatus;
  }

  recallFromPharmacy(event: Event) {
    const appointmentId = Number(this.prescription?.appointment_id || 0);
    if (!appointmentId) {
      this.toast.add({
        severity: 'warn',
        summary: 'No Appointment',
        detail: 'No appointment found to recall.'
      });
      return;
    }

    if (!this.isCurrentAppointmentToday()) {
      this.toast.add({
        severity: 'warn',
        summary: 'Recall Not Allowed',
        detail: 'Recall from Pharmacy is allowed only for today\'s appointment.'
      });
      return;
    }

    this.confirmationService.confirm({
      key: 'recallPharmacyPopup',
      target: event.target as EventTarget,
      acceptLabel: 'Recall Rx',
      rejectLabel: 'Leave in Pharmacy',
      acceptButtonStyleClass: 'recall-confirm-accept',
      rejectButtonStyleClass: 'recall-confirm-reject',
      defaultFocus: 'reject',
      accept: () => {
        this.appointmentService.changeStatus(appointmentId, { status: 'doctor' }).subscribe({
          next: (res: any) => {
            if (!res?.success) {
              this.toast.add({
                severity: 'error',
                summary: 'Recall Failed',
                detail: 'Unable to move the case back to doctor queue.'
              });
              return;
            }

            this.currentAppointmentStatus = 'doctor';
            this.patient.status = 'doctor';
            this.loadQueue();
            this.clearDraftForCurrentContext();
            this.selectPatient({
              appointment_id: appointmentId,
              status: 'doctor',
              is_courier: this.prescription?.is_courier,
              fromRecall: true
            });
            this.recalledFromPharmacy = true;
            this.toast.add({
              severity: 'success',
              summary: 'Recalled',
              detail: 'Case moved back to doctor queue for editing.'
            });
          },
          error: () => {
            this.toast.add({
              severity: 'error',
              summary: 'Recall Failed',
              detail: 'Unable to move the case back to doctor queue.'
            });
          }
        });
      }
    });
  }

  private getAppointmentDateIstKey(raw: any): string | null {
    const text = (raw || '').toString().trim();
    if (!text) return null;

    const IST_OFFSET_MS = 330 * 60 * 1000;
    const appointmentDate = new Date(text);
    if (Number.isNaN(appointmentDate.getTime())) return null;

    return new Date(appointmentDate.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
  }

  private isAppointmentDateToday(raw: any): boolean {
    const appointmentDateIst = this.getAppointmentDateIstKey(raw);
    if (!appointmentDateIst) return false;

    const IST_OFFSET_MS = 330 * 60 * 1000;
    const todayIst = new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
    return appointmentDateIst === todayIst;
  }

  private findTodaysRecallableAppointment(records: any[]): any | null {
    if (!Array.isArray(records) || records.length === 0) return null;

    const recallableStatuses = new Set(['medicine', 'reception', 'medicine_scheduled']);
    const matches = records.filter((record) => {
      const appointmentId = Number(record?.appointment_id || 0);
      if (appointmentId <= 0) return false;

      const status = String(record?.status || record?.appointment_status || '').toLowerCase();
      if (!recallableStatuses.has(status)) return false;

      return this.isAppointmentDateToday(record?.appointment_date || record?.visit_date);
    });

    if (!matches.length) return null;

    return [...matches].sort((a, b) => {
      const aTime = new Date(a?.appointment_date || a?.visit_date || a?.created_at || 0).getTime();
      const bTime = new Date(b?.appointment_date || b?.visit_date || b?.created_at || 0).getTime();
      if (aTime !== bTime) return bTime - aTime;
      return (Number(b?.appointment_id) || 0) - (Number(a?.appointment_id) || 0);
    })[0] || null;
  }

  private isCurrentAppointmentToday(): boolean {
    return this.isAppointmentDateToday(this.currentAppointmentDate);
  }

  //! Old Schedule meds for later
  scheduleForLater() {
    // Keep this method if you need it for backward compatibility
    // this.toggleSchedulePicker();

    const now = new Date();
    const appointmentDate = now.toISOString().slice(0, 10);
    const hour = now.getHours().toString().padStart(2, '0');
    const startTime = `${hour}:00`;
    const endTime = `${(now.getHours() + 1).toString().padStart(2, '0')}:00`;

    const clinicId = sessionStorage.getItem('clinic_id');
    const doctorId = sessionStorage.getItem('doctor_id');

    // Appointment payload
    const appointmentPayload = {
      clinic_id: Number(clinicId) || 1,
      doctor_id: Number(doctorId) || 1,
      // clinic_id: this.clinicId,
      // doctor_id: this.doctorId,
      patient_id: this.patient.patient_id,
      appointment_date: appointmentDate,
      appointment_type: 'case',
      start_time: startTime,
      end_time: endTime,
      status: 'medicine_scheduled',
      type: 'on_flow',
    };

    this.appointmentService.addAppointment(appointmentPayload).subscribe({
      next: (res: any) => {
        if (res.success) {
          const appt = res.data;
          const prescription = this.prescription;

          // 🔹 Transform prescriptions before sending
          const transformedPrescriptions = this.transformPrescriptions();

          // Now create prescription linked to this appointment
          const prescriptionPayload = {
            appointment_id: appt.id,
            allergies: prescription.allergies || '',
            current_medications: prescription.current_medications || '',
            personal_family_history: prescription.personal_family_history || '',
            surgical_history: prescription.surgical_history || '',
            blood_pressure: prescription.blood_pressure || '',
            pulse_rate: prescription.pulse_rate || '',
            temperature: prescription.temperature || '',
            weight: prescription.weight || '',
            height: prescription.height || '',
            fasting_blood_sugar: prescription.fasting_blood_sugar || '',
            blood_oxygen: prescription.blood_oxygen || '',
            runny_nose: prescription.runny_nose || '',
            cough: prescription.cough || '',

            chief_complaints: this.prescription.chief_complaints || '',
            diagnosis: this.diagnosisTags.map((tag: any) => tag.diagnosis || tag),
            suggested_reports: this.reportTags.map((tag: any) => tag.report),

            // prescriptions: prescription.prescriptions || [],
            prescriptions: transformedPrescriptions || [],

            personal_dues: prescription.personal_dues || 0,
            visit_fees: prescription.visit_fees || 0,
            received_cash: prescription.received_cash || 0,
            received_online: prescription.received_online || 0,

            notes: prescription.notes || '',
            is_courier: this.normalizePrescriptionMode(prescription.is_courier),
            special_instructions: prescription.special_instructions || '',
            collection_date: this.scheduledDate
          };

          this.prescriptionsService.addPrescriptions(prescriptionPayload).subscribe((presRes: any) => {
            if (presRes.success) {
              const prescription = presRes.data;

              // ✅ Upload reports if there are any files waiting
              if (this.pendingFiles && this.pendingFiles.length > 0) {
                const formData = new FormData();
                formData.append('patient_id', this.patient.patient_id.toString());
                formData.append('prescription_id', prescription.id.toString());
                this.pendingFiles.forEach((file: File) => {
                  formData.append('files[]', file, file.name);
                });

                this.prescriptionsService.uploadReports(formData).subscribe({
                  next: (uploadRes: any) => {
                    if (uploadRes.success) {
                      this.pendingFiles = [];
                      this.refreshPatientReports();
                      this.autoAnalyzeUploadedReports(uploadRes.data || []);
                    }
                  },
                  error: (err) => console.error('Report upload failed:', err)
                });
              }

              this.toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Prescription saved as Case successfully!',
              });

              this.clearForm();
              this.patientFromSearch = false;
            }
          });

          // this.clearForm();
        }
      },
      error: (err) => {
        console.error('Save error:', err);
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save appointment and prescription',
        });
      }
    });
  }

  formatTime(time: any): string {
    if (!time && time !== 0) return '';

    try {
      // If already HH:MM or HH:MM:SS
      if (typeof time === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(time)) {
        return time.slice(0, 5).padStart(4, '0');
      }

      // If numeric timestamp
      if (typeof time === 'number') {
        const d = new Date(time);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      }

      // Try parsing ISO or full datetime string
      const parsed = new Date(time);
      if (!isNaN(parsed.getTime())) {
        const hh = String(parsed.getHours()).padStart(2, '0');
        const mm = String(parsed.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      }

      // Fallback to original
      return String(time).slice(0, 5);
    } catch {
      return String(time);
    }
  }

  openQueueSidebar() {
    this.loadQueue();
    this.isPatientVisible = true;
    this.sidebarTab = 'queue';
  }

  openPatientSearch() {
    const sidebarWasOpen = this.isPatientVisible;
    this.isPatientVisible = true;
    this.sidebarTab = 'search';
    this.focusPatientSearchInput(sidebarWasOpen);
  }

  private focusPatientSearchInput(sidebarWasOpen = false) {
    const delayMs = sidebarWasOpen ? 120 : 300;

    setTimeout(() => {
      const input = document.getElementById('patient-search-input') as HTMLInputElement | null;
      input?.focus();
    }, delayMs);
  }

  closeSidebar() {
    this.isPatientVisible = false;
  }



  downloadPrescriptionPDF(): void {
    // PDF download removed
  }

  printPrescription(): void {
    const element = document.getElementById('prescription-pdf');

    if (!element) {
      console.error('Prescription container not found');
      return;
    }

    const originalTitle = document.title;

    document.title = `Prescription_${this.patient.first_name}_${this.patient.last_name}_${this.selectedDate}`;

    setTimeout(() => {
      window.print();

      // Restore title after print dialog opens
      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    }, 0);
  }

  generatePrescriptionPDF(): void {

    if (!this.patient) {
      console.error('Patient data missing');
      return;
    }

    if (!this.prescription) {
      console.error('Prescription data missing');
      return;
    }

    const doc = new jsPDF({
      unit: 'pt',
      format: 'a4',
    });

    const img = new Image();
    // img.src = '/logo.png'; // local
    img.src = '/clinic/logo.png'; // server

    const build = () =>
      this.buildPrescriptionPDF(
        doc,
        this.patient,
        this.prescription,
        img.complete ? img : undefined
      );

    img.onload = build;
    img.onerror = build;
  }

  private buildPrescriptionPDF(
    doc: jsPDF,
    patient: any,
    prescription: any,
    img?: HTMLImageElement
  ) {

    /* ================= BASE CONFIG ================= */

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginX = 32;
    let y = 20;

    const brandPink: [number, number, number] = [227, 28, 121];
    const brandOrange: [number, number, number] = [246, 137, 32];
    const darkGray: [number, number, number] = [51, 51, 51];
    const black: [number, number, number] = [0, 0, 0];

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);

    /* ================= HEADER (UNCHANGED) ================= */

    if (img) {
      const imgWidth = (pageWidth - marginX * 2) * 0.3;
      const imgHeight = imgWidth * 0.4;
      doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
    } else {
      doc.setFillColor(...brandPink);
      doc.roundedRect(marginX, y, 40, 40, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text('CLINIC', marginX + 20, y + 17, { align: 'center' });
      doc.text('LOGO', marginX + 20, y + 26, { align: 'center' });
    }

    const headerTextX =
      marginX + (img ? (pageWidth - marginX * 2) * 0.3 + 16 : 56);

    doc.setFontSize(9);
    doc.setTextColor(...darkGray);
    doc.text(
      'Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,',
      headerTextX,
      y + 28
    );
    doc.text(
      'Maharshi Nagar, Pune, Maharashtra – 411037',
      headerTextX,
      y + 39
    );
    doc.text(
      'Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com',
      headerTextX,
      y + 49
    );

    y += 70;

    doc.setLineWidth(3);
    doc.setDrawColor(...brandPink);
    doc.line(marginX, y, pageWidth / 2, y);
    doc.setDrawColor(...brandOrange);
    doc.line(pageWidth / 2, y, pageWidth - marginX, y);

    y += 3;

    /* ================= SUB HEADER (UNCHANGED) ================= */

    const halfWidth = (pageWidth - marginX * 2) / 2;
    const subHeaderHeight = 15;

    doc.setFillColor(...brandPink);
    doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');

    doc.setFillColor(...brandOrange);
    doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);

    doc.text(
      'SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES',
      marginX + 12,
      y + 10
    );

    doc.text(
      'Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm',
      pageWidth - marginX - 12,
      y + 10,
      { align: 'right' }
    );

    y += subHeaderHeight + 20;

    /* ================= PATIENT INFO ================= */

    const fullName = [
      patient.first_name,
      patient.middle_name,
      patient.last_name,
    ].filter(Boolean).join(' ');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkGray);

    doc.text('Patient Name:', marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(fullName, marginX + 90, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', pageWidth / 2, y);
    doc.setFont('helvetica', 'normal');
    // doc.text(this.selectedDate || '', pageWidth / 2 + 40, y);
    doc.text(
      this.selectedDate
        ? new Date(this.selectedDate).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
        : '',
      pageWidth / 2 + 40, y
    );

    y += 14;

    doc.setFont('helvetica', 'bold');
    doc.text('Age:', marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(patient.age || ''), marginX + 30, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Sex:', pageWidth / 2, y);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.gender || '', pageWidth / 2 + 30, y);

    y += 25;

    /* ================= SECTION HELPER ================= */

    const section = (title: string) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      // doc.setTextColor(...brandPink);
      doc.setTextColor(...black);
      doc.text(title, marginX, y);
      y += 6;
      doc.setDrawColor(...brandOrange);
      doc.line(marginX, y, pageWidth - marginX, y);
      y += 10;
    };

    /* ================= VITALS ================= */

    section('Vitals');

    const cols = 4;
    const gap = 6;
    const cardHeight = 36;
    const cardWidth =
      (pageWidth - marginX * 2 - gap * (cols - 1)) / cols;


    const drawVitalCard = (
      label: string,
      value: string,
      index: number
    ) => {
      const col = index % cols;
      const row = Math.floor(index / cols);

      const x = marginX + col * (cardWidth + gap);
      const yy = y + row * (cardHeight + gap);

      // Card border
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.rect(x, yy, cardWidth, cardHeight);

      // Label
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(label, x + 6, yy + 12);

      // Value
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(51, 51, 51);
      doc.text(value || '-', x + 6, yy + 26);
    };

    let i = 0;

    drawVitalCard(
      'Blood Pressure',
      prescription.blood_pressure
        ? `${prescription.blood_pressure} mmHg`
        : '-',
      i++
    );

    drawVitalCard(
      'Pulse Rate',
      prescription.pulse_rate
        ? `${prescription.pulse_rate} bpm`
        : '-',
      i++
    );

    drawVitalCard(
      'Temperature',
      prescription.temperature
        ? `${prescription.temperature} °C`
        : '-',
      i++
    );

    drawVitalCard(
      'Weight',
      prescription.weight
        ? `${prescription.weight} kg`
        : '-',
      i++
    );

    drawVitalCard(
      'Height',
      prescription.height
        ? `${prescription.height} cm`
        : '-',
      i++
    );

    drawVitalCard(
      'Fasting Blood Sugar',
      prescription.fasting_blood_sugar
        ? `${prescription.fasting_blood_sugar} mg/dL`
        : '-',
      i++
    );

    drawVitalCard(
      'Blood Oxygen',
      prescription.blood_oxygen
        ? `${prescription.blood_oxygen} %`
        : '-',
      i++
    );

    // Move cursor below vitals
    y += Math.ceil(i / cols) * (cardHeight + gap) + 15;


    /* ================= COMPLAINTS ================= */

    // section('Chief Complaints');

    // prescription.complaints?.forEach((c: string, i: number) => {
    //   doc.text(`${i + 1}. ${c}`, marginX, y);
    //   y += 12;
    // });

    // y += 5;

    /* ================= CHIEF COMPLAINTS ================= */

    section('Chief Complaints');

    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(51, 51, 51);

    const complaintsText = this.prescription.chief_complaints || '';

    if (complaintsText) {
      const lines = doc.splitTextToSize(
        complaintsText,
        pageWidth - marginX * 2
      );
      doc.text(lines, marginX, y);
      y += lines.length * 12 + 10;
    }

    /* ================= DIAGNOSIS ================= */

    const diagnosisText = this.diagnosisTags
      ?.map((raw: any) => {
        const text =
          typeof raw === 'string'
            ? raw
            : typeof raw === 'object'
              ? String(raw.diagnosis || raw.name || '')
              : String(raw || '');

        return text
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
      })
      .filter(Boolean)
      .join(', ');

    if (diagnosisText) {
      section('Diagnosis');
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(51, 51, 51);

      const lines = doc.splitTextToSize(
        diagnosisText,
        pageWidth - marginX * 2
      );
      doc.text(lines, marginX, y);
      y += lines.length * 12 + 10;
    }

    // y = yy + tagHeight + 12;


    /* ================= PRESCRIPTIONS ================= */

    section('Prescriptions');

    y += 5;

    prescription.prescriptions?.forEach((p: any) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`For ${p.title} – ${p.duration}`, marginX, y);
      y += 12;

      doc.setFont('helvetica', 'normal');
      p.medicines.forEach((m: any, i: number) => {
        doc.text(`${i + 1}. ${m.ballers}`, marginX + 10, y);
        y += 11;
      });

      y += 10;
    });

    /* ================= REPORTS ================= */

    if (this.reportTags?.length > 0) {
      section('Suggested Laboratory Reports');

      y += 5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(51, 51, 51);

      const reportsText = this.reportTags
        ?.map((raw: any) => {
          const text =
            typeof raw === 'string'
              ? raw
              : typeof raw === 'object'
                ? String(raw.label || raw.name || raw.value || raw.report || '')
                : String(raw || '');

          return text
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
        })
        .join(', ');

      if (reportsText) {
        const lines = doc.splitTextToSize(
          reportsText,
          pageWidth - marginX * 2
        );
        doc.text(lines, marginX, y);
        y += lines.length * 12 + 6;
      }
    }

    /* ================= FOOTER ================= */

    const footerY = Math.max(y + 30, pageHeight - 60);

    doc.setDrawColor(...darkGray);
    doc.line(pageWidth - marginX - 160, footerY, pageWidth - marginX, footerY);

    doc.setFont('helvetica', 'bold');
    doc.text('Dr. Shailesh Sancheti', pageWidth - marginX - 160, footerY + 14);

    doc.setFontSize(10);
    doc.text('MD (Homeopathy)', pageWidth - marginX - 160, footerY + 24);

    doc.save(`prescription_${fullName || 'patient'}.pdf`);
  }

  onDoseComplete(i: number, j: number, event: any) {
    const med = this.prescription.prescriptions[i].medicines[j];
    const query = (event.query || '').trim().toLowerCase();

    if (!query) {
      med.filteredDoses = [...this.allDoses];
    } else {
      med.filteredDoses = this.allDoses.filter((dose: any) => {
        const doseText = (typeof dose === 'string' ? dose : dose.dose || dose.value || '').toLowerCase();
        return doseText.includes(query);
      }).slice(0, 15);
    }
  }

  onDoseDropdownOpen(i: number, j: number) {
    const med = this.prescription.prescriptions[i].medicines[j];
    // Populate all doses when dropdown opens
    med.filteredDoses = [...this.allDoses];
  }

  // ensurePrescriptionExists() {
  //   if (!this.prescription.prescriptions || this.prescription.prescriptions.length === 0) {
  //     this.addPrescription();
  //   }
  // }

  // private isPrescriptionEmpty(prescription: any): boolean {
  //   return (
  //     !prescription.prescriptions ||
  //     !Array.isArray(prescription.prescriptions.medicines) ||
  //     prescription.prescriptions.medicines.length === 0
  //   );
  // }

  // private ensureWritablePrescription(): number {
  //   // If no prescriptions exist at all
  //   if (!this.prescription.prescriptions.length) {
  //     this.addPrescription();
  //     return 0;
  //   }

  //   const lastIndex = this.prescription.prescriptions.length - 1;
  //   const last = this.prescription.prescriptions[lastIndex];

  //   // If last prescription is empty → reuse it
  //   if (this.isPrescriptionEmpty(last)) {
  //     return lastIndex;
  //   }

  //   // Otherwise → create a new one
  //   this.addPrescription();
  //   return this.prescription.prescriptions.length - 1;
  // }

  private ensureWritablePrescription(forceNew = false): number {
    const prescriptions = this.prescription.prescriptions;

    if (
      forceNew ||
      prescriptions.length === 0 ||
      prescriptions[prescriptions.length - 1].medicines.length > 0
    ) {
      this.addPrescription();
    }

    return prescriptions.length - 1;
  }

  private hasOnlyOneEmptyPrescription(): boolean {
    const list = this.prescription.prescriptions;
    return (
      list.length === 1 &&
      (!list[0].medicines || list[0].medicines.length === 0)
    );
  }



  // handleInput(value: string): void {
  //   if (!value) return;

  //   this.ensurePrescriptionExists();

  //   // Support multiple rows & prescriptions in one input
  //   const lines = value
  //     .split('\n')
  //     .map(l => l.trim())
  //     .filter(Boolean);

  //   for (let line of lines) {
  //     const endsWithSemicolon = line.endsWith(';');
  //     const cleanLine = endsWithSemicolon ? line.slice(0, -1) : line;

  //     const parsedRow = this.parser.parseRow(cleanLine);

  //     this.addParsedRowToPrescription(parsedRow);

  //     if (endsWithSemicolon) {
  //       this.addPrescription();
  //     }
  //   }
  // }

  private normalizeHintCode(code: string): string {
    const match = code.toUpperCase().match(/^([A-Z]+)0*(\d+)$/);
    if (!match) return code.toUpperCase();
    return `${match[1]}${match[2]}`;
  }

  // handleInput(text: string) {
  //   if (!text) return;

  //   const lines = text
  //     .split('\n')
  //     .map(l => l.trim())
  //     .filter(Boolean);

  //   for (const line of lines) {
  //     const parsedRow = this.parser.parseRow(line);

  //     const presIndex = this.prescription.prescriptions.length - 1;

  //     // 1️⃣ create empty row using YOUR existing function
  //     this.addMedicineRow(presIndex);

  //     const pres = this.prescription.prescriptions[presIndex];
  //     const row = pres.medicines[pres.medicines.length - 1];

  //     // 2️⃣ resolve medicines from DB
  //     // Version 1
  //     // row.tags = parsedRow.tags
  //     // // @ts-ignore
  //     //   .map(hint => this.resolver.resolve(hint, this.allMedicines))
  //     //   .filter(Boolean);

  //     // Version 2
  //     // row.tags = parsedRow.tags
  //     //   .map(hint =>
  //     //     this.resolver.resolveWithIndex(
  //     //       hint,
  //     //       this.medicineByCode,
  //     //       // this.medicinesByName
  //     //     )
  //     //   )
  //     //   .filter(Boolean);

  //     // Version 3
  //     row.tags = [];
  //     row.unresolvedMedicines = [];

  //     // @ts-ignore
  //     // for (const hint of parsedRow.tags) {
  //     //   const resolved = this.resolver.resolveWithIndex(
  //     //     hint,
  //     //     this.medicineByCode,
  //     //     // @ts-ignore
  //     //     this.allMedicines
  //     //   );

  //     //   if (resolved) {
  //     //     row.tags.push(resolved);
  //     //   } else {
  //     //     row.unresolvedMedicines.push(hint.raw);
  //     //   }
  //     // }

  //     for (const hint of parsedRow.tags) {
  //       const normalizedHint = {
  //         ...hint,
  //         codeHint: hint.codeHint
  //           ? this.normalizeHintCode(hint.codeHint)
  //           : ''
  //       };

  //       const resolved = this.resolver.resolveWithIndex(
  //         normalizedHint,
  //         this.medicineByCode,
  //         // @ts-ignore
  //         this.allMedicines
  //       );

  //       if (resolved) {
  //         row.tags.push(resolved);
  //         // row.medicineTags.push(resolved);
  //       } else {
  //         row.unresolvedMedicines.push(hint.raw);
  //       }
  //     }


  //     // 3️⃣ hydrate remaining fields
  //     row.dose = parsedRow.dose || '';
  //     row.drams = parsedRow.drams || '';
  //     row.ballers = parsedRow.ballers || '';
  //   }
  // }

  handleInput(text: string) {
    if (!text) return;

    // 0️⃣ Ensure at least one prescription exists
    // if (this.prescription.prescriptions.length === 0) {
    //   this.addPrescription();
    // }

    // 🔥 REMOVE default empty prescription if present
    // 1️⃣ If only one empty prescription exists, reset its medicines (DON'T delete it)
    if (this.hasOnlyOneEmptyPrescription()) {
      // this.prescription.prescriptions = [];
      this.prescription.prescriptions[0].medicines = [];
    }

    // 1️⃣ Split into prescription blocks using ';'
    const prescriptionBlocks = text
      .split(';')
      .map(b => b.trim())
      .filter(Boolean);

    // for (const block of prescriptionBlocks) {
    for (let i = 0; i < prescriptionBlocks.length; i++) {
      const block = prescriptionBlocks[i];

      // // 2️⃣ Create a NEW prescription for each block
      // this.addPrescription();

      // const presIndex = this.prescription.prescriptions.length - 1;
      // const presIndex = this.ensureWritablePrescription();
      // const presIndex = this.ensureWritablePrescription(block !== prescriptionBlocks[0]);
      const presIndex = this.ensureWritablePrescription(i !== 0);

      // 3️⃣ Decide which prescription to write into
      // let presIndex: number;
      // if (i === 0) {
      //   presIndex = 0;
      // } else {
      //   this.addPrescription();
      //   presIndex = this.prescription.prescriptions.length - 1;
      // }

      const pres = this.prescription.prescriptions[presIndex];

      // 3️⃣ Split block into lines
      const lines = block
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);

      for (const line of lines) {

        const parsedRow = this.parser.parseRow(line);

        // 4️⃣ Create empty medicine row (your existing function)
        this.addMedicineRow(presIndex);
        const row = pres.medicines[pres.medicines.length - 1];

        // 5️⃣ Resolve medicines
        row.medicineTags = [];
        row.tags = [];
        row.unresolvedMedicines = [];
        row.resolvedMedicines = [];
        row.unresolvedHints = [];

        for (const hint of parsedRow.tags) {
          const normalizedHint = {
            ...hint,
            codeHint: hint.codeHint
              ? this.normalizeHintCode(hint.codeHint)
              : ''
          };

          const resolved = this.resolver.resolveWithConfidence(
            normalizedHint,
            this.medicineByCode,
            // @ts-ignore
            this.allMedicines
          );

          if (resolved) {
            row.medicineTags.push(resolved);
            row.tags.push(resolved);
          } else {
            row.unresolvedMedicines.push(hint.raw);
            row.unresolvedHints.push(normalizedHint);
          }
        }

        // 6️⃣ Hydrate dosing fields
        row.dose = parsedRow.dose || '';
        row.drams = parsedRow.drams || '';
        row.ballers = parsedRow.ballers || '';
      }
    }
  }

  handleInputNew(text: string) {
    if (!text) return;

    // 0️⃣ Ensure at least one prescription exists
    if (this.prescription.prescriptions.length === 0) {
      this.addPrescription();
    }

    // 1️⃣ If only one empty prescription exists, reset its medicines (DON'T delete it)
    if (this.hasOnlyOneEmptyPrescription()) {
      this.prescription.prescriptions[0].medicines = [];
    }

    // 2️⃣ Split into prescription blocks using ';'
    const prescriptionBlocks = text
      .split(';')
      .map(b => b.trim())
      .filter(Boolean);

    for (let i = 0; i < prescriptionBlocks.length; i++) {
      const block = prescriptionBlocks[i];

      // 3️⃣ Decide which prescription to write into
      let presIndex: number;
      if (i === 0) {
        presIndex = 0;
      } else {
        this.addPrescription();
        presIndex = this.prescription.prescriptions.length - 1;
      }

      const pres = this.prescription.prescriptions[presIndex];

      // 4️⃣ Split block into lines
      const lines = block
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);

      for (const line of lines) {
        const parsedRow = this.parser.parseRow(line);

        // 5️⃣ Skip creating a row if there's literally no data
        if (
          !parsedRow.tags?.length &&
          !parsedRow.dose &&
          !parsedRow.drams &&
          !parsedRow.ballers
        ) {
          continue;
        }

        // 6️⃣ Create medicine row ONLY when needed
        this.addMedicineRow(presIndex);
        const row = pres.medicines[pres.medicines.length - 1];

        // 7️⃣ Reset row state
        row.medicineTags = [];
        row.tags = [];
        row.unresolvedMedicines = [];
        row.resolvedMedicines = [];
        row.unresolvedHints = [];

        // 8️⃣ Resolve medicines
        for (const hint of parsedRow.tags || []) {
          const normalizedHint = {
            ...hint,
            codeHint: hint.codeHint
              ? this.normalizeHintCode(hint.codeHint)
              : ''
          };

          const resolved = this.resolver.resolveWithConfidence(
            normalizedHint,
            this.medicineByCode,
            // @ts-ignore
            this.allMedicines
          );

          if (resolved) {
            row.medicineTags.push(resolved);
          } else {
            row.unresolvedMedicines.push(hint.raw);
            row.unresolvedHints.push(normalizedHint);
          }
        }

        // 9️⃣ Hydrate dosing fields
        row.dose = parsedRow.dose || '';
        row.drams = parsedRow.drams || '';
        row.ballers = parsedRow.ballers || '';
      }
    }
  }


  addParsedRowToPrescription(parsedRow: ParsedMedicineRow) {
    const presIndex = this.prescription.prescriptions.length - 1;

    // Step 1: create empty row using EXISTING function
    this.addMedicineRow(presIndex);

    const pres = this.prescription.prescriptions[presIndex];
    const lastRow = pres.medicines[pres.medicines.length - 1];

    // Step 2: hydrate it
    lastRow.dose = parsedRow.dose || '';
    lastRow.drams = parsedRow.drams || '';
    lastRow.ballers = parsedRow.ballers || '';

    // temporary: unresolved medicine codes
    lastRow.tags = parsedRow.tags.map(code => ({
      id: null,
      name: code,      // placeholder until resolved
      code: code,
      potency: ''
    }));
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeQuickSidebar() {
    this.sidebarOpen = false;
  }

  clearQuickText() {
    this.quickText = '';
  }

  get quickTextLineCount(): number {
    const text = this.quickText.trim();
    if (!text) return 0;
    return text.split('\n').filter((line) => line.trim()).length;
  }

  @HostListener('document:keydown.escape')
  onQuickSidebarEscape(): void {
    if (this.sidebarOpen) {
      this.closeQuickSidebar();
    }
  }

  submitQuickText() {
    if (!this.quickText.trim()) return;

    this.handleInput(this.quickText);

    this.quickText = '';

    this.closeQuickSidebar();
  }

  onQuickTextKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submitQuickText();
    }
  }

  // --- Report AI Analysis ---

  private getCurrentAppointmentId(): number | undefined {
    const appointmentId = Number(this.prescription?.appointment_id ?? 0);
    return appointmentId > 0 ? appointmentId : undefined;
  }

  refreshPatientReports(selectionToken?: number) {
    const patientId = Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
    if (!patientId) return;

    this.reportsLoadingMore = false;

    this.prescriptionsService.getReportsByPatientId(patientId, { limit: 40, offset: 0 }).subscribe({
      next: (rRes: any) => {
        if (selectionToken != null && selectionToken !== this.activeSearchSelectionToken) return;
        this.uploadsByDate = rRes?.success ? (rRes.data || []) : [];
        this.reportsMeta = this.parseReportsMeta(rRes?.meta, this.uploadsByDate);
        this.syncReportsViewModel();
        this.refreshBaselineReportCandidates();
        this.loadPhotoProgressHistory();
      },
      error: () => {
        this.uploadsByDate = [];
        this.reportsMeta = { total: 0, limit: 40, offset: 0, hasMore: false };
        this.baselineReportPool = [];
        this.buildBaselineReportOptions();
        this.syncReportsViewModel();
        this.photoProgressHistory = [];
        this.photoProgressHistoryOpen = false;
      }
    });
  }

  loadMorePatientReports() {
    const patientId = Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
    if (!patientId || this.reportsLoadingMore || !this.reportsMeta.hasMore) return;

    const nextOffset = this.reportsMeta.offset + this.reportsMeta.limit;
    this.reportsLoadingMore = true;

    this.prescriptionsService.getReportsByPatientId(patientId, {
      limit: this.reportsMeta.limit,
      offset: nextOffset,
    }).subscribe({
      next: (rRes: any) => {
        this.reportsLoadingMore = false;
        if (!rRes?.success) return;

        this.uploadsByDate = this.mergeReportGroups(this.uploadsByDate, rRes.data || []);
        this.reportsMeta = this.parseReportsMeta(rRes?.meta, this.uploadsByDate);
        this.syncReportsViewModel();
      },
      error: () => {
        this.reportsLoadingMore = false;
      }
    });
  }

  getPatientReportsLoadedCount(): number {
    return this.patientReportsLoadedCount;
  }

  toggleReportsUploadPanel(): void {
    this.reportsUploadPanelOpen = !this.reportsUploadPanelOpen;
  }

  setReportsDocFilter(filter: 'all' | 'image' | 'pdf' | 'text' | 'analyzed' | 'pending'): void {
    this.reportsDocFilter = filter;
    this.syncReportsViewModel();
  }

  onReportsDocSearchChange(value: string): void {
    this.reportsDocSearch = value;
    this.syncReportsViewModel();
  }

  onReportTileClick(file: any): void {
    if (this.imageCompareMode) {
      this.toggleImageCompareSelection(file);
      return;
    }

    this.openFile(file);
  }

  getComparableImageCount(): number {
    return this.flattenReportGroups(this.uploadsByDate).filter((file) => this.isImageReport(file)).length;
  }

  canEnterImageCompareMode(): boolean {
    return this.getComparableImageCount() >= 2;
  }

  toggleImageCompareMode(): void {
    if (this.imageCompareMode) {
      this.exitImageCompareMode();
      return;
    }

    if (!this.canEnterImageCompareMode()) {
      this.toast.info('Need two images', 'Upload at least two photos to compare before and after.');
      return;
    }

    this.imageCompareMode = true;
    this.imageCompareTarget = 'before';
    this.imageCompareBefore = [];
    this.imageCompareAfter = [];
    this.photoProgressInitialAnalysis = null;
    this.loadPhotoProgressHistory();
    if (this.reportsDocFilter !== 'image') {
      this.reportsDocFilter = 'image';
      this.syncReportsViewModel();
    }
  }

  exitImageCompareMode(): void {
    this.imageCompareMode = false;
    this.imageCompareBefore = [];
    this.imageCompareAfter = [];
    this.photoProgressInitialAnalysis = null;
    this.closeImageCompareDialog();
  }

  private resetImageCompareState(): void {
    this.imageCompareMode = false;
    this.imageCompareTarget = 'before';
    this.imageCompareBefore = [];
    this.imageCompareAfter = [];
    this.imageCompareDialogVisible = false;
    this.photoProgressInitialAnalysis = null;
    this.photoProgressHistory = [];
    this.photoProgressHistoryOpen = false;
  }

  isImageCompareSelected(file: any): boolean {
    return this.getImageCompareRole(file) !== null;
  }

  getImageCompareRole(file: any): 'before' | 'after' | null {
    if (this.imageCompareBefore.some((item) => this.isSameReportFile(item, file))) {
      return 'before';
    }
    if (this.imageCompareAfter.some((item) => this.isSameReportFile(item, file))) {
      return 'after';
    }
    return null;
  }

  canOpenImageCompare(): boolean {
    return this.imageCompareBefore.length > 0 && this.imageCompareAfter.length > 0;
  }

  setImageCompareTarget(target: 'before' | 'after'): void {
    this.imageCompareTarget = target;
  }

  toggleImageCompareSelection(file: any): void {
    if (!this.isImageReport(file)) {
      this.toast.info('Images only', 'Comparison works with photos. Switch to the Images filter to pick a pair.');
      return;
    }

    const currentRole = this.getImageCompareRole(file);
    if (currentRole) {
      this.removeFromImageCompareSide(currentRole, file);
      return;
    }

    const target = this.imageCompareTarget;
    const side = target === 'before' ? this.imageCompareBefore : this.imageCompareAfter;
    if (side.length >= this.imageCompareMaxPerSide) {
      this.toast.info(
        `Maximum ${this.imageCompareMaxPerSide} ${target} photos`,
        'Deselect one photo on this side before adding another.'
      );
      return;
    }

    if (target === 'before') {
      this.imageCompareBefore = [...this.imageCompareBefore, file];
    } else {
      this.imageCompareAfter = [...this.imageCompareAfter, file];
    }
  }

  openImageCompareDialog(): void {
    if (!this.canOpenImageCompare()) return;

    this.imageCompareBefore = this.orderImageCompareSelection(this.imageCompareBefore);
    this.imageCompareAfter = this.orderImageCompareSelection(this.imageCompareAfter);
    this.photoProgressInitialAnalysis = null;
    this.imageCompareDialogVisible = true;
  }

  closeImageCompareDialog(): void {
    this.imageCompareDialogVisible = false;
    this.photoProgressInitialAnalysis = null;
  }

  loadPhotoProgressHistory(): void {
    const patientId = Number(this.patient?.patient_id ?? this.patient?.id ?? this.selectedPatient?.id ?? 0);
    if (!patientId || !this.aiEnabled) {
      this.photoProgressHistory = [];
      this.photoProgressHistoryOpen = false;
      return;
    }

    this.photoProgressHistoryLoading = true;
    this.reportAnalysisService.listPhotoProgressByPatient(patientId).subscribe({
      next: (res: any) => {
        this.photoProgressHistoryLoading = false;
        const rows = Array.isArray(res?.data) ? res.data : [];
        this.photoProgressHistory = rows.map((item: any) => this.decoratePhotoProgressHistoryItem(item));
      },
      error: () => {
        this.photoProgressHistoryLoading = false;
        this.photoProgressHistory = [];
      },
    });
  }

  togglePhotoProgressHistory(): void {
    this.photoProgressHistoryOpen = !this.photoProgressHistoryOpen;
    if (this.photoProgressHistoryOpen && !this.photoProgressHistory.length && !this.photoProgressHistoryLoading) {
      this.loadPhotoProgressHistory();
    }
  }

  /** Stable UI fields so templates never allocate new arrays each change-detection cycle. */
  private decoratePhotoProgressHistoryItem(analysis: any): any {
    if (!analysis || typeof analysis !== 'object') return analysis;
    return {
      ...analysis,
      _ui: {
        tone: this.getPhotoProgressHistoryTone(analysis),
        toneIcon: this.getPhotoProgressHistoryToneIcon(analysis),
        levelLabel: this.getPhotoProgressHistoryLevelLabel(analysis),
        savedOn: this.getPhotoProgressHistorySavedOn(analysis),
        title: this.getPhotoProgressHistoryShortTitle(analysis),
        dateRange: this.getPhotoProgressHistoryDateRange(analysis),
        counts: this.getPhotoProgressHistoryCounts(analysis),
        missing: this.getPhotoProgressHistoryMissing(analysis),
        pairs: this.buildPhotoProgressHistoryPairs(analysis),
      },
    };
  }

  private buildPhotoProgressHistoryPairs(
    analysis: any,
  ): Array<{ before: any | null; after: any | null; index: number }> {
    const before = Array.isArray(analysis?.before_photos) ? analysis.before_photos : [];
    const after = Array.isArray(analysis?.after_photos) ? analysis.after_photos : [];
    const count = Math.max(before.length, after.length, 0);
    if (!count) {
      return [{ before: null, after: null, index: 0 }];
    }
    return Array.from({ length: count }, (_, index) => ({
      before: before[index] || null,
      after: after[index] || null,
      index,
    }));
  }

  openSavedPhotoProgress(analysis: any): void {
    if (!analysis) return;

    const diff = analysis?.comparison_diff || {};
    const gallery = this.flattenReportGroups(this.uploadsByDate);
    const resolvePhoto = (photo: any, fallbackId?: number) => {
      const id = Number(photo?.id || fallbackId || 0);
      const fromGallery = gallery.find((file) => Number(file?.id) === id);
      if (fromGallery) {
        return { ...fromGallery };
      }
      if (photo?.url) {
        return {
          id,
          url: photo.url,
          name: photo.name || 'Photo',
          report_date: photo.date || null,
          uploaded_at: photo.date || null,
          missing: !!photo.missing,
        };
      }
      return {
        id,
        url: null,
        name: photo?.name || `Missing photo #${id || '?'}`,
        report_date: photo?.date || null,
        missing: true,
      };
    };

    const beforeFromApi = Array.isArray(analysis.before_photos) ? analysis.before_photos : [];
    const afterFromApi = Array.isArray(analysis.after_photos) ? analysis.after_photos : [];
    const beforeIds = (diff.before_report_ids || []).map((id: any) => Number(id)).filter((id: number) => id > 0);
    const afterIds = (diff.after_report_ids || []).map((id: any) => Number(id)).filter((id: number) => id > 0);

    this.imageCompareBefore = (beforeFromApi.length ? beforeFromApi : beforeIds.map((id: number) => ({ id }))).map(
      (photo: any, index: number) => resolvePhoto(photo, beforeIds[index]),
    );
    this.imageCompareAfter = (afterFromApi.length ? afterFromApi : afterIds.map((id: number) => ({ id }))).map(
      (photo: any, index: number) => resolvePhoto(photo, afterIds[index]),
    );

    const availableBefore = this.imageCompareBefore.filter((p) => !!p?.url).length;
    const availableAfter = this.imageCompareAfter.filter((p) => !!p?.url).length;
    if (!availableBefore || !availableAfter) {
      this.toast.warn(
        'Photos missing',
        'Some photos from this comparison are no longer available. Restore what remains or pick new photos.',
      );
    }

    this.imageCompareMode = true;
    this.photoProgressInitialAnalysis = analysis;
    this.imageCompareDialogVisible = true;
  }

  onPhotoProgressAnalysisSaved(_analysis: any): void {
    this.loadPhotoProgressHistory();
  }

  getPhotoProgressHistoryTitle(analysis: any): string {
    return (
      analysis?.comparison_diff?.title ||
      analysis?.raw_response?.title ||
      analysis?.summary_text ||
      'Clinical photo progress'
    );
  }

  getPhotoProgressHistoryShortTitle(analysis: any): string {
    const site =
      analysis?.comparison_diff?.anatomical_site ||
      analysis?.raw_response?.anatomical_site ||
      '';
    if (site) {
      return String(site)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());
    }

    const raw = this.getPhotoProgressHistoryTitle(analysis);
    // Drop noisy "BEFORE (…) vs AFTER (…)" suffixes from model titles
    return raw
      .replace(/\s*[—\-]\s*BEFORE\s*\([^)]*\)\s*vs\s*AFTER\s*\([^)]*\)/i, '')
      .replace(/\s+/g, ' ')
      .trim() || 'Clinical photo progress';
  }

  getPhotoProgressHistoryLevel(analysis: any): string {
    return String(
      analysis?.comparison_diff?.overall_level ||
        analysis?.raw_response?.overall_level ||
        analysis?.likely_response ||
        '',
    ).toLowerCase();
  }

  getPhotoProgressHistoryLevelLabel(analysis: any): string {
    const level = this.getPhotoProgressHistoryLevel(analysis);
    if (!level) return 'Reviewed';
    return level.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getPhotoProgressHistoryTone(analysis: any): 'positive' | 'caution' | 'warning' | 'negative' | 'neutral' {
    switch (this.getPhotoProgressHistoryLevel(analysis)) {
      case 'improved':
        return 'positive';
      case 'partial':
        return 'warning';
      case 'worsened':
        return 'negative';
      case 'unchanged':
      case 'stable':
        return 'neutral';
      case 'inconclusive':
        return 'caution';
      default:
        return 'neutral';
    }
  }

  getPhotoProgressHistoryToneIcon(analysis: any): string {
    switch (this.getPhotoProgressHistoryTone(analysis)) {
      case 'positive':
        return 'pi-check-circle';
      case 'warning':
        return 'pi-exclamation-circle';
      case 'caution':
        return 'pi-exclamation-triangle';
      case 'negative':
        return 'pi-times-circle';
      default:
        return 'pi-minus-circle';
    }
  }

  getPhotoProgressHistorySavedOn(analysis: any): string {
    if (!analysis?.created_at) return '';
    return this.formatBaselineReportDate(String(analysis.created_at).slice(0, 10));
  }

  getPhotoProgressHistoryDateRange(analysis: any): string {
    const before = Array.isArray(analysis?.before_photos) ? analysis.before_photos : [];
    const after = Array.isArray(analysis?.after_photos) ? analysis.after_photos : [];
    const beforeDate = before[0]?.date || '';
    const afterDate = after[0]?.date || '';
    if (beforeDate && afterDate) return `${beforeDate} → ${afterDate}`;
    if (beforeDate) return `From ${beforeDate}`;
    if (afterDate) return `To ${afterDate}`;
    return '';
  }

  getPhotoProgressHistoryCounts(analysis: any): string {
    const beforeCount =
      analysis?.before_photos?.length ||
      analysis?.comparison_diff?.before_report_ids?.length ||
      0;
    const afterCount =
      analysis?.after_photos?.length ||
      analysis?.comparison_diff?.after_report_ids?.length ||
      0;
    return `${beforeCount} before · ${afterCount} after`;
  }

  getPhotoProgressHistoryMissing(analysis: any): number {
    return Number(analysis?.missing_photo_count || 0);
  }

  getPhotoProgressHistoryMeta(analysis: any): string {
    const bits = [this.getPhotoProgressHistoryCounts(analysis)];
    const when = this.getPhotoProgressHistorySavedOn(analysis);
    if (when) bits.push(when);
    const missing = this.getPhotoProgressHistoryMissing(analysis);
    if (missing) bits.push(`${missing} missing`);
    return bits.join(' · ');
  }

  getPhotoProgressHistoryThumbs(analysis: any): any[] {
    const before = Array.isArray(analysis?.before_photos) ? analysis.before_photos : [];
    const after = Array.isArray(analysis?.after_photos) ? analysis.after_photos : [];
    return [...before.slice(0, 2), ...after.slice(0, 2)];
  }

  getPhotoProgressHistoryLeadThumbs(analysis: any): { before: any | null; after: any | null } {
    const before = Array.isArray(analysis?.before_photos) ? analysis.before_photos : [];
    const after = Array.isArray(analysis?.after_photos) ? analysis.after_photos : [];
    return {
      before: before[0] || null,
      after: after[0] || null,
    };
  }

  getPhotoProgressHistoryPairs(
    analysis: any,
  ): Array<{ before: any | null; after: any | null; index: number }> {
    return analysis?._ui?.pairs || this.buildPhotoProgressHistoryPairs(analysis);
  }

  trackPhotoProgressHistory = (_: number, item: any): string | number =>
    item?.id ?? item?.analysis_id ?? item?._ui?.title ?? _;

  trackPhotoProgressPair = (_: number, pair: { index: number }): number =>
    pair?.index ?? _;

  getPhotoProgressChiefComplaints(): string {
    return this.complaintTags.map((tag) => this.formatTagDisplay(tag)).join(', ');
  }

  getPhotoProgressDiagnoses(): string {
    return this.diagnosisTags.map((tag) => this.formatTagDisplay(tag)).join(', ');
  }

  getPhotoProgressTreatmentContext(): string {
    const blocks = Array.isArray(this.prescription?.prescriptions) ? this.prescription.prescriptions : [];
    const meds = blocks.flatMap((block: any) =>
      (block?.medicines || []).map((med: any) => med?.medicineInput || med?.name).filter(Boolean)
    );
    return meds.slice(0, 12).join(', ');
  }

  getPatientExportLabel(): string {
    const name = [this.patient?.first_name, this.patient?.last_name].filter(Boolean).join(' ');
    const id = this.patient?.patient_id || this.patient?.id;
    return [name || 'Patient', id ? `ID ${id}` : ''].filter(Boolean).join(' · ');
  }

  private removeFromImageCompareSide(side: 'before' | 'after', file: any): void {
    if (side === 'before') {
      this.imageCompareBefore = this.imageCompareBefore.filter((item) => !this.isSameReportFile(item, file));
      return;
    }
    this.imageCompareAfter = this.imageCompareAfter.filter((item) => !this.isSameReportFile(item, file));
  }

  private orderImageCompareSelection(files: any[]): any[] {
    return [...(files || [])].sort((a, b) => {
      const aTime = this.parseBaselineReportDate(a?.report_date || a?.uploaded_at || '');
      const bTime = this.parseBaselineReportDate(b?.report_date || b?.uploaded_at || '');
      if (aTime !== bTime) return aTime - bTime;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }

  private isSameReportFile(left: any, right: any): boolean {
    const leftId = Number(left?.id ?? 0);
    const rightId = Number(right?.id ?? 0);
    if (leftId && rightId) {
      return leftId === rightId;
    }

    return String(left?.url || left?.name || '') === String(right?.url || right?.name || '');
  }

  isReportsTabActive(): boolean {
    return this.activeIndex === this.reportsTabIndex;
  }

  private syncReportsViewModel(): void {
    this.patientReportsLoadedCount = this.flattenReportGroups(this.uploadsByDate).length;

    const query = (this.reportsDocSearch || '').trim().toLowerCase();
    this.filteredUploadsByDate = (this.uploadsByDate || [])
      .map(group => ({
        date: group.date,
        files: (group.files || []).filter(file => this.matchesReportsDocFilter(file, query)),
      }))
      .filter(group => group.files.length > 0);
    this.filteredDocumentsCount = this.flattenReportGroups(this.filteredUploadsByDate).length;
  }

  private clearReportAnalysisPolling(): void {
    for (const timer of this.reportAnalysisPollTimers.values()) {
      clearTimeout(timer);
    }
    this.reportAnalysisPollTimers.clear();
    this.analyzingReportIds.clear();
  }

  getReportDocKindLabel(file: any): string {
    if (file?.kind === 'text') return 'Text';
    if (file?.type?.startsWith('image/')) return 'Image';
    return 'PDF';
  }

  getReportDocKindClass(file: any): string {
    if (file?.kind === 'text') return 'rd-kind--text';
    if (file?.type?.startsWith('image/')) return 'rd-kind--image';
    return 'rd-kind--pdf';
  }

  getReportDocIcon(file: any): string {
    if (file?.kind === 'text') return 'pi-align-left';
    if (file?.type?.startsWith('image/')) return 'pi-image';
    return 'pi-file-pdf';
  }

  private matchesReportsDocFilter(file: any, query: string): boolean {
    if (query && !String(file?.name || '').toLowerCase().includes(query)) {
      return false;
    }

    const status = String(file?.status || file?.analysis_status || 'uploaded').toLowerCase();
    switch (this.reportsDocFilter) {
      case 'image':
        return file?.kind !== 'text' && file?.type?.startsWith('image/');
      case 'pdf':
        return file?.kind !== 'text' && !file?.type?.startsWith('image/');
      case 'text':
        return file?.kind === 'text';
      case 'analyzed':
        return status === 'analyzed';
      case 'pending':
        return status !== 'analyzed' && status !== 'analyzing';
      default:
        return true;
    }
  }

  private refreshBaselineReportCandidates() {
    const patientId = Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
    if (!patientId) {
      this.baselineReportPool = [];
      this.buildBaselineReportOptions();
      return;
    }

    this.prescriptionsService.getReportsByPatientId(patientId, {
      limit: 50,
      offset: 0,
      analyzedOnly: true,
    }).subscribe({
      next: (rRes: any) => {
        this.baselineReportPool = this.flattenReportGroups(rRes?.success ? (rRes.data || []) : []);
        this.buildBaselineReportOptions();
      },
      error: () => {
        this.baselineReportPool = this.flattenReportGroups(this.uploadsByDate);
        this.buildBaselineReportOptions();
      }
    });
  }

  private flattenReportGroups(groups: any[] | null | undefined): any[] {
    const files: any[] = [];
    for (const group of groups || []) {
      for (const file of group?.files || []) {
        files.push(file);
      }
    }
    return files;
  }

  private mergeReportGroups(existing: any[], incoming: any[]): any[] {
    const merged = new Map<string, any>();

    for (const group of existing || []) {
      const key = String(group?.date || '');
      merged.set(key, {
        date: group?.date,
        files: [...(group?.files || [])],
      });
    }

    for (const group of incoming || []) {
      const key = String(group?.date || '');
      const current = merged.get(key);
      if (current) {
        current.files.push(...(group?.files || []));
      } else {
        merged.set(key, {
          date: group?.date,
          files: [...(group?.files || [])],
        });
      }
    }

    return Array.from(merged.values());
  }

  private parseReportsMeta(meta: any, groups: any[]) {
    const loaded = this.flattenReportGroups(groups).length;
    const total = Number(meta?.total ?? loaded);
    const limit = Number(meta?.limit ?? 40);
    const offset = Number(meta?.offset ?? 0);

    return {
      total,
      limit,
      offset,
      hasMore: Boolean(meta?.has_more ?? (offset + loaded < total)),
    };
  }

  private buildBaselineReportOptions() {
    const currentReportId = Number(this.selectedReportFile?.id ?? 0);
    const candidates: BaselineReportOption[] = [];
    const source = this.baselineReportPool.length
      ? this.baselineReportPool
      : this.flattenReportGroups(this.uploadsByDate);

    for (const file of source) {
      const id = Number(file?.id);
      if (!id || id === currentReportId) continue;

      const status = String(file?.status || file?.analysis_status || 'uploaded').toLowerCase();
      const dateLabel = this.formatBaselineReportDate(file?.report_date || file?.uploaded_at || '');

      candidates.push({
        value: id,
        label: `${file.name} · ${dateLabel}`,
        name: String(file?.name || `Report #${id}`),
        date: dateLabel,
        dateSort: this.parseBaselineReportDate(file?.report_date || file?.uploaded_at || ''),
        kindLabel: this.getReportKindLabel(file),
        kindIcon: this.getReportKindIcon(file),
        kindClass: this.getReportKindClass(file),
        status,
        statusLabel: this.getReportStatusLabel(file),
        reportTypeLabel: this.getReportTypeLabel(file?.report_type),
        insight: this.getReportInsightTooltip(file),
        isAnalyzed: status === 'analyzed',
      });
    }

    candidates.sort((a, b) => b.dateSort - a.dateSort);
    this.baselineReportCandidates = candidates;
    this.applyBaselineFilter();
    this.refreshAnalysisDialogCaches();
  }

  applyBaselineFilter() {
    const query = this.baselineSearchQuery.trim().toLowerCase();
    if (!query) {
      this.filteredBaselineCandidates = [...this.baselineReportCandidates];
      return;
    }

    this.filteredBaselineCandidates = this.baselineReportCandidates.filter((item) => {
      const haystack = [
        item.name,
        item.date,
        item.kindLabel,
        item.reportTypeLabel,
        item.statusLabel,
        item.insight || '',
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }

  selectBaselineReport(option: BaselineReportOption) {
    if (!option?.isAnalyzed) return;
    this.baselineReportId = option.value;
    this.baselineSelectionMode = 'manual';
    this.refreshAnalysisDialogCaches();
  }

  clearBaselineSelection() {
    this.baselineReportId = null;
    this.baselineSelectionMode = 'auto';
    this.refreshAnalysisDialogCaches();
  }

  isBaselineAutoMode(): boolean {
    return this.baselineSelectionMode === 'auto' || !this.baselineReportId;
  }

  isBaselineSelected(option: BaselineReportOption): boolean {
    return this.baselineSelectionMode === 'manual' && this.baselineReportId === option.value;
  }

  getSelectedBaselineOption(): BaselineReportOption | null {
    if (!this.baselineReportId) return null;
    return this.baselineReportCandidates.find((item) => item.value === this.baselineReportId) || null;
  }

  getBaselineSelectableCount(): number {
    return this.baselineReportCandidates.filter((item) => item.isAnalyzed).length;
  }

  getReadyBaselineCandidates(): BaselineReportOption[] {
    return this.filteredBaselineCandidates.filter((item) => item.isAnalyzed);
  }

  getPendingBaselineCount(): number {
    return this.filteredBaselineCandidates.filter((item) => !item.isAnalyzed).length;
  }

  getBaselineOptionTooltip(item: BaselineReportOption): string | undefined {
    if (!item?.insight) return item?.name;
    return `${item.name}\n${item.insight}`;
  }

  private countPriorAnalyzedReports(): number {
    return this.getBaselineSelectableCount();
  }

  private formatBaselineReportDate(value: string): string {
    if (!value) return 'Date unknown';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private parseBaselineReportDate(value: string): number {
    const parsed = new Date(value || 0);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  getReportTypeLabel(value: string | undefined): string {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return 'Report';

    for (const group of this.reportTypeOptions) {
      const match = group.items.find((item) => item.value === normalized);
      if (match) return match.label;
    }

    return normalized.replace(/_/g, ' ');
  }

  getCurrentReportDateLabel(): string {
    const file = this.selectedReportFile;
    if (!file) return '';

    for (const group of this.uploadsByDate || []) {
      if ((group?.files || []).some((item: any) => Number(item?.id) === Number(file.id))) {
        return this.formatBaselineReportDate(file?.report_date || group?.date || '');
      }
    }

    return this.formatBaselineReportDate(file?.report_date || '');
  }

  autoAnalyzeUploadedReports(reports: any[]) {
    if (!this.aiEnabled) {
      return;
    }

    for (const report of reports || []) {
      const reportId = Number(report?.id);
      if (!reportId) continue;
      this.analyzeReportById(reportId, false, false);
      this.pollReportAnalysisStatus(reportId);
    }
  }

  private isAnalysisInProgressMessage(message: string | undefined | null): boolean {
    return (message || '').toLowerCase().includes('already in progress');
  }

  private pollReportAnalysisStatus(reportId: number, attempt = 0, patientIdAtStart?: number) {
    const maxAttempts = 40;
    if (attempt >= maxAttempts) return;

    const existingTimer = this.reportAnalysisPollTimers.get(reportId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const trackedPatientId = patientIdAtStart ?? Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
    const delayMs = attempt === 0 ? 1500 : 3000;
    const timer = setTimeout(() => {
      this.reportAnalysisPollTimers.delete(reportId);

      const currentPatientId = Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
      if (!currentPatientId || currentPatientId !== trackedPatientId) return;

      this.prescriptionsService.getReportsByPatientId(currentPatientId, { limit: 40, offset: 0 }).subscribe({
        next: (rRes: any) => {
          if (Number(this.patient?.patient_id ?? this.patient?.id ?? 0) !== trackedPatientId) return;
          if (!rRes?.success) {
            this.pollReportAnalysisStatus(reportId, attempt + 1, trackedPatientId);
            return;
          }

          this.uploadsByDate = rRes.data || [];
          this.reportsMeta = this.parseReportsMeta(rRes?.meta, this.uploadsByDate);
          this.syncReportsViewModel();

          const file = this.findReportFileById(reportId);
          const status = String(file?.status || '').toLowerCase();
          if (status === 'analyzing' || status === 'uploaded') {
            this.pollReportAnalysisStatus(reportId, attempt + 1, trackedPatientId);
            return;
          }

          this.analyzingReportIds.delete(reportId);
          this.notifyReportAnalysisResultOnce(reportId, file?.latest_analysis, file);
          this.refreshBaselineReportCandidates();
        },
        error: () => {
          if (Number(this.patient?.patient_id ?? this.patient?.id ?? 0) !== trackedPatientId) return;
          this.pollReportAnalysisStatus(reportId, attempt + 1, trackedPatientId);
        },
      });
    }, delayMs);

    this.reportAnalysisPollTimers.set(reportId, timer);
  }

  analyzeReportById(reportId: number, openDialog = true, notifyOnError = openDialog) {
    if (!this.aiEnabled) {
      if (notifyOnError) {
        this.toast.add({
          severity: 'warn',
          summary: 'AI Disabled',
          detail: 'AI features are currently turned off.',
        });
      }
      return;
    }

    if (this.analyzingReportIds.has(reportId)) return;

    this.reportAnalysisResultNotifiedIds.delete(reportId);
    this.analyzingReportIds.add(reportId);
    this.updateReportStatusInGallery(reportId, 'analyzing');

    this.reportAnalysisService.analyzeReport(reportId).subscribe({
      next: (res: any) => {
        this.analyzingReportIds.delete(reportId);
        if (res?.success) {
          const analysis = res.data;
          this.updateReportStatusInGallery(reportId, analysis?.quality_status === 'needs_reupload' ? 'needs_reupload' : 'analyzed', analysis);
          this.notifyReportAnalysisResultOnce(reportId, analysis);
          if (openDialog) {
            const file = this.findReportFileById(reportId);
            if (file) {
              this.openReportAnalysis(file, analysis);
            }
          } else {
            this.refreshExtractionInOpenDialog(reportId, analysis);
          }
          return;
        }

        if (this.isAnalysisInProgressMessage(res?.message)) {
          this.updateReportStatusInGallery(reportId, 'analyzing');
          this.pollReportAnalysisStatus(reportId);
          return;
        }

        this.updateReportStatusInGallery(reportId, 'uploaded');
        if (notifyOnError) {
          this.toast.add({
            severity: 'warn',
            summary: 'Analysis unavailable',
            detail: res?.message || 'The document is saved. You can retry analysis later.',
          });
        }
      },
      error: (err) => {
        this.analyzingReportIds.delete(reportId);

        if (this.isAnalysisInProgressMessage(err?.error?.message)) {
          this.updateReportStatusInGallery(reportId, 'analyzing');
          this.pollReportAnalysisStatus(reportId);
          return;
        }

        this.updateReportStatusInGallery(reportId, 'uploaded');
        if (notifyOnError) {
          this.toast.add({
            severity: 'warn',
            summary: 'Analysis unavailable',
            detail: err?.error?.message || 'The document is saved. You can retry analysis later.',
          });
        }
      }
    });
  }

  private notifyReportAnalysisResultOnce(reportId: number, analysis?: any, file?: any | null): void {
    if (this.reportAnalysisResultNotifiedIds.has(reportId)) return;
    this.reportAnalysisResultNotifiedIds.add(reportId);
    this.showReportAnalysisResultMessage(reportId, analysis, file);
  }

  private showReportAnalysisResultMessage(reportId: number, analysis?: any, file?: any | null): void {
    const reportFile = file ?? this.findReportFileById(reportId);
    const reportName = reportFile?.name || reportFile?.file_name || `Report #${reportId}`;
    const qualityStatus = String(
      analysis?.quality_status || reportFile?.status || reportFile?.analysis_status || '',
    ).toLowerCase();
    const markerCount = Array.isArray(analysis?.markers) ? analysis.markers.length : 0;
    const summary = analysis?.summary_text || reportFile?.analysis_summary;

    if (qualityStatus === 'needs_reupload') {
      this.toast.add({
        severity: 'warn',
        summary: 'Report needs re-upload',
        detail: `${reportName}: the scan quality was too low for reliable extraction. Please upload a clearer copy.`,
        life: 8000,
      });
      return;
    }

    if (qualityStatus === 'poor') {
      this.toast.add({
        severity: 'warn',
        summary: 'Analysis completed with limitations',
        detail: summary
          ? `${reportName}: ${this.truncateReportInsight(summary, 120)}`
          : `${reportName}: extraction confidence was low. Review the results carefully.`,
        life: 8000,
      });
      return;
    }

    const markerPart = markerCount > 0
      ? ` ${markerCount} lab marker${markerCount === 1 ? '' : 's'} extracted.`
      : '';
    const detail = summary
      ? `${reportName}: ${this.truncateReportInsight(summary, 120)}${markerPart}`
      : `${reportName} was analyzed successfully.${markerPart}`;

    this.toast.add({
      severity: 'success',
      summary: 'Report analyzed',
      detail,
      life: 7000,
    });
  }

  retryReportAnalysis(file: any, event?: Event) {
    event?.stopPropagation();

    const reportId = Number(file?.id);
    if (!reportId) return;

    this.analyzeReportById(reportId, false, true);
  }

  canRetryReportAnalysis(file: any): boolean {
    if (!this.aiEnabled || !file?.id) return false;

    const status = String(file?.status || file?.analysis_status || 'uploaded').toLowerCase();
    if (status === 'analyzing' || status === 'analyzed') return false;

    return status === 'uploaded' || status === 'failed' || status === 'needs_reupload';
  }

  openReportAnalysis(file: any, latestAnalysis?: any) {
    if (!this.aiEnabled) {
      this.toast.add({
        severity: 'warn',
        summary: 'AI Disabled',
        detail: 'Report AI analysis is currently turned off.',
      });
      return;
    }

    const reportId = Number(file?.id);
    if (!reportId) {
      this.toast.add({
        severity: 'warn',
        summary: 'Unsaved Report',
        detail: 'Save the report before running AI analysis.'
      });
      return;
    }

    this.selectedReportFile = file;
    this.reportAnalysisDialogVisible = true;
    this.reportAnalysisTabIndex = 0;
    this.showUnchangedDiffMarkers = false;
    this.analysisTabRenderReady = true;
    this.doctorReviewNotes = '';
    this.reportExtractionAnalysis = latestAnalysis || null;
    this.reportComparisonAnalysis = null;
    this.reportOutcomeAnalysis = null;
    this.baselineReportId = null;
    this.baselineSelectionMode = 'auto';
    this.baselineSearchQuery = '';
    this.buildBaselineReportOptions();
    this.refreshAnalysisDialogCaches();

    this.reportAnalysisLoading = true;
    this.reportAnalysisService.listAnalyses(reportId).subscribe({
      next: (res: any) => {
        this.reportAnalysisLoading = false;
        this.reportAnalyses = res?.success ? (res.data || []) : [];
        this.reportExtractionAnalysis = this.reportAnalyses.find((a: any) => a.analysis_type === 'extraction') || this.reportExtractionAnalysis;
        this.reportComparisonAnalysis = this.reportAnalyses.find((a: any) => a.analysis_type === 'comparison') || null;
        this.reportOutcomeAnalysis = this.reportAnalyses.find((a: any) => a.analysis_type === 'outcome') || null;
        this.doctorReviewNotes = this.reportOutcomeAnalysis?.doctor_override_notes
          || this.reportComparisonAnalysis?.doctor_override_notes
          || this.reportExtractionAnalysis?.doctor_override_notes
          || '';
        this.refreshAnalysisDialogCaches();
      },
      error: () => {
        this.reportAnalysisLoading = false;
        this.reportAnalyses = [];
        this.refreshAnalysisDialogCaches();
      }
    });
  }

  runReportExtraction() {
    const reportId = Number(this.selectedReportFile?.id);
    if (!reportId || this.isExtractionAnalyzing()) return;
    this.analyzeReportById(reportId, false, true);
  }

  runReportComparison() {
    if (!this.aiEnabled) {
      this.toast.add({
        severity: 'warn',
        summary: 'AI Disabled',
        detail: 'AI features are currently turned off.',
      });
      return;
    }

    const reportId = Number(this.selectedReportFile?.id);
    if (!reportId) return;

    this.reportCompareLoading = true;
    this.reportAnalysisService.compareReports(reportId, this.baselineReportId || undefined).subscribe({
      next: (res: any) => {
        this.reportCompareLoading = false;
        if (res?.success) {
          this.reportComparisonAnalysis = res.data;
          this.refreshAnalysisDialogCaches();
          this.setReportAnalysisTab(1);
          this.toast.add({
            severity: 'success',
            summary: 'Comparison Ready',
            detail: 'Report comparison completed.'
          });
          return;
        }

        this.toast.add({
          severity: 'warn',
          summary: 'Comparison unavailable',
          detail: res?.message || 'Run extraction on both reports before comparing.',
        });
      },
      error: (err) => {
        this.reportCompareLoading = false;
        this.toast.add({
          severity: 'warn',
          summary: 'Comparison unavailable',
          detail: err?.error?.message || 'Run extraction on both reports before comparing.',
        });
      }
    });
  }

  runTreatmentOutcomeAnalysis() {
    if (!this.aiEnabled) {
      this.toast.add({
        severity: 'warn',
        summary: 'AI Disabled',
        detail: 'AI features are currently turned off.',
      });
      return;
    }

    const reportId = Number(this.selectedReportFile?.id);
    const patientId = this.getConsultPatientId();

    if (!reportId || !this.isConsultationSelectedForOutcome()) {
      this.toast.add({
        severity: 'warn',
        summary: 'Missing Context',
        detail: 'Select a patient to consult before running treatment outcome analysis.'
      });
      return;
    }

    const prescriptionId = Number(this.prescription?.id ?? 0);

    this.reportOutcomeLoading = true;
    this.reportAnalysisService.treatmentOutcome(reportId, {
      prescriptionId: prescriptionId > 0 ? prescriptionId : undefined,
      baselineReportId: this.baselineReportId || undefined,
      consultContext: this.buildOutcomeConsultContext(patientId, prescriptionId),
    }).subscribe({
      next: (res: any) => {
        this.reportOutcomeLoading = false;
        if (res?.success) {
          this.reportOutcomeAnalysis = res.data;
          this.refreshAnalysisDialogCaches();
          this.setReportAnalysisTab(2);
          this.toast.add({
            severity: 'info',
            summary: 'Outcome Analysis Ready',
            detail: 'Findings are suggestive only and do not prove causation.'
          });
          return;
        }

        this.toast.add({
          severity: 'error',
          summary: 'Outcome Analysis Failed',
          detail: res?.message || 'Unable to analyze treatment outcome.'
        });
      },
      error: (err) => {
        this.reportOutcomeLoading = false;
        this.toast.add({
          severity: 'error',
          summary: 'Outcome Analysis Failed',
          detail: err?.error?.message || err?.error?.error || 'Unable to analyze treatment outcome.'
        });
      }
    });
  }

  canSaveAnalysisReview(): boolean {
    const analysis = this.reportOutcomeAnalysis || this.reportComparisonAnalysis || this.reportExtractionAnalysis;
    return !!Number(analysis?.id);
  }

  saveAnalysisReview() {
    const analysis = this.reportOutcomeAnalysis || this.reportComparisonAnalysis || this.reportExtractionAnalysis;
    const analysisId = Number(analysis?.id);
    if (!analysisId || this.reportReviewSaving) return;

    this.reportReviewSaving = true;
    this.reportAnalysisService.reviewAnalysis(analysisId, this.doctorReviewNotes).subscribe({
      next: (res: any) => {
        this.reportReviewSaving = false;
        if (res?.success) {
          this.toast.add({
            severity: 'success',
            summary: 'Review Saved',
            detail: 'Doctor review notes saved.'
          });
          return;
        }

        this.toast.add({
          severity: 'error',
          summary: 'Save Failed',
          detail: res?.message || 'Unable to save review.'
        });
      },
      error: (err) => {
        this.reportReviewSaving = false;
        this.toast.add({
          severity: 'error',
          summary: 'Save Failed',
          detail: err?.error?.message || 'Unable to save review.'
        });
      }
    });
  }

  isReportAnalyzing(reportId: number): boolean {
    return this.analyzingReportIds.has(Number(reportId));
  }

  getSelectedReportStatus(): string {
    return String(this.selectedReportFile?.status || this.selectedReportFile?.analysis_status || 'uploaded').toLowerCase();
  }

  isExtractionAnalyzing(): boolean {
    const reportId = Number(this.selectedReportFile?.id);
    if (!reportId) return false;
    return this.isReportAnalyzing(reportId) || this.getSelectedReportStatus() === 'analyzing';
  }

  needsExtractionRetry(): boolean {
    const status = this.getSelectedReportStatus();
    if (status === 'failed' || status === 'needs_reupload') {
      return true;
    }

    const quality = String(this.reportExtractionAnalysis?.quality_status || '').toLowerCase();
    return quality === 'needs_reupload' || quality === 'poor';
  }

  hasSuccessfulExtraction(): boolean {
    if (!this.reportExtractionAnalysis) return false;
    if (this.needsExtractionRetry()) return false;

    return this.getSelectedReportStatus() === 'analyzed';
  }

  shouldShowExtractionCta(): boolean {
    if (!this.aiEnabled) {
      return false;
    }

    if (this.isExtractionAnalyzing() || this.needsExtractionRetry()) {
      return true;
    }

    if (this.reportAnalysisLoading) {
      return false;
    }

    return !this.hasSuccessfulExtraction();
  }

  getExtractionCtaTitle(): string {
    if (this.isExtractionAnalyzing()) return 'Extracting markers…';
    if (this.needsExtractionRetry()) return 'Retry extraction';
    return 'Extract lab markers';
  }

  getExtractionCtaHint(): string {
    if (this.isExtractionAnalyzing()) {
      return 'This usually runs automatically after upload. Please wait a moment.';
    }

    if (this.needsExtractionRetry()) {
      return 'The document is saved. Upload a clearer copy if needed, or retry analysis when AI is available.';
    }

    return 'Analysis is optional. The document works without it — run this when you want extracted markers.';
  }

  getExtractionActionLabel(): string {
    if (this.isExtractionAnalyzing()) return 'Analyzing…';
    if (this.needsExtractionRetry()) return 'Re-analyze';
    return 'Analyze Report';
  }

  getReportStatusLabel(file: any): string {
    const status = String(file?.status || file?.analysis_status || 'uploaded').toLowerCase();
    const map: Record<string, string> = {
      uploaded: 'Not analyzed',
      analyzing: 'Analyzing…',
      analyzed: 'Analyzed',
      failed: 'Not analyzed',
      needs_reupload: 'Re-upload',
    };
    return map[status] || 'Not analyzed';
  }

  getReportStatusClass(file: any): string {
    const status = String(file?.status || file?.analysis_status || 'uploaded').toLowerCase();
    const normalized = status === 'failed' ? 'uploaded' : status;
    return `ra-status--${normalized}`;
  }

  getLikelyResponseLabel(value: string): string {
    return String(value || 'inconclusive').replace(/_/g, ' ');
  }

  getDiffEntries(diff: any, key: 'changed' | 'added' | 'removed' | 'unchanged'): any[] {
    return Array.isArray(diff?.[key]) ? diff[key] : [];
  }

  readonly reportAnalysisTabs = [
    { index: 0, label: 'Extraction', icon: 'pi pi-sparkles' },
    { index: 1, label: 'Comparison', icon: 'pi pi-chart-line' },
    { index: 2, label: 'Outcome', icon: 'pi pi-heart' },
  ];

  setReportAnalysisTab(index: number) {
    this.reportAnalysisTabIndex = index;
    this.refreshAnalysisDialogCaches();

    if (index === 1 || index === 2) {
      this.analysisTabRenderReady = false;
      setTimeout(() => {
        if (this.reportAnalysisTabIndex === index) {
          this.analysisTabRenderReady = true;
        }
      }, 0);
      return;
    }

    this.analysisTabRenderReady = true;
  }

  private refreshAnalysisDialogCaches(): void {
    this.treatmentContextSummary = this.buildTreatmentContextSummary();
    this.outcomePrerequisites = this.buildOutcomePrerequisites();
    this.comparisonDisplay = this.buildInterpretationDisplay(this.reportComparisonAnalysis);
    this.outcomeDisplay = this.buildInterpretationDisplay(this.reportOutcomeAnalysis);
  }

  getConsultPatientId(): number {
    return Number(this.patient?.patient_id ?? this.patient?.id ?? 0);
  }

  isConsultationSelectedForOutcome(): boolean {
    return this.patientSelectionConfirmed && this.getConsultPatientId() > 0;
  }

  getOutcomeContextLabel(): string {
    const prescriptionId = Number(this.prescription?.id ?? 0);
    if (prescriptionId > 0) {
      return `Rx #${prescriptionId}`;
    }

    return 'Current consultation';
  }

  private buildOutcomeConsultContext(patientId: number, prescriptionId: number) {
    const appointmentId = Number(this.prescription?.appointment_id ?? 0);

    return {
      patient_id: patientId,
      appointment_id: appointmentId > 0 ? appointmentId : undefined,
      prescription_id: prescriptionId > 0 ? prescriptionId : undefined,
      chief_complaints: this.stripHtml(String(this.prescription?.chief_complaints || '')),
      prescription_blocks: this.transformPrescriptions(),
      treatment_duration: this.getOutcomeTreatmentDuration(),
    };
  }

  private getOutcomeTreatmentDuration(): string | undefined {
    const durations = (this.prescription?.prescriptions || [])
      .map((block: any) => String(block?.duration || '').trim())
      .filter(Boolean);

    return durations.length ? durations.join(', ') : undefined;
  }

  private buildTreatmentContextSummary(): string {
    const complaints = this.stripHtml(String(this.prescription?.chief_complaints || '')).trim();

    if (!complaints) {
      return 'No chief complaint entered yet for the current consultation.';
    }

    return complaints.length > 180 ? `${complaints.slice(0, 179).trim()}…` : complaints;
  }

  private buildOutcomePrerequisites(): Array<{ ok: boolean; label: string }> {
    return [
      {
        ok: this.isConsultationSelectedForOutcome(),
        label: 'Patient selected for consultation',
      },
      {
        ok: !!this.reportExtractionAnalysis,
        label: 'Current report extraction completed',
      },
      {
        ok: this.countPriorAnalyzedReports() > 0,
        label: 'At least one prior analyzed report available as baseline',
      },
    ];
  }

  private buildInterpretationDisplay(analysis: any | null): ReportInterpretationDisplay | null {
    if (!analysis) {
      return null;
    }

    const likelyResponseRaw = analysis.raw_response?.likely_response || analysis.likely_response || 'inconclusive';
    const diff = analysis.comparison_diff;
    const diffDisplay = this.buildDiffDisplay(diff);

    return {
      likelyResponse: this.getLikelyResponseLabel(likelyResponseRaw),
      likelyResponseClass: this.getLikelyResponseClass(likelyResponseRaw),
      confidencePercent: this.getConfidencePercent(analysis.confidence),
      summaryText: String(analysis.summary_text || ''),
      supportingChanges: this.extractSupportingChanges(analysis),
      caveats: this.extractAnalysisCaveats(analysis),
      diff: diffDisplay,
      changedPreview: diffDisplay?.changed.slice(0, 4) || [],
    };
  }

  private buildDiffDisplay(diff: any | null): ReportAnalysisDiffDisplay | null {
    if (!diff) {
      return null;
    }

    const changed = this.getDiffEntries(diff, 'changed');
    const added = this.getDiffEntries(diff, 'added');
    const removed = this.getDiffEntries(diff, 'removed');
    const unchanged = this.getDiffEntries(diff, 'unchanged');

    return {
      stats: {
        changed: changed.length,
        added: added.length,
        removed: removed.length,
        unchanged: unchanged.length,
      },
      changed,
      added,
      removed,
      unchanged,
    };
  }

  private extractSupportingChanges(analysis: any): string[] {
    const items = analysis?.raw_response?.supporting_changes ?? analysis?.supporting_changes;
    return Array.isArray(items) ? items.filter((item) => !!String(item || '').trim()) : [];
  }

  private extractAnalysisCaveats(analysis: any): string[] {
    const items = analysis?.raw_response?.caveats ?? analysis?.caveats;
    return Array.isArray(items) ? items.filter((item) => !!String(item || '').trim()) : [];
  }

  private stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  getReportKindLabel(file: any): string {
    if (file?.kind === 'text') return 'Text';
    if (file?.type?.startsWith('image/')) return 'Image';
    return 'PDF';
  }

  getReportKindIcon(file: any): string {
    if (file?.kind === 'text') return 'pi pi-align-left';
    if (file?.type?.startsWith('image/')) return 'pi pi-image';
    return 'pi pi-file-pdf';
  }

  getReportKindClass(file: any): string {
    if (file?.kind === 'text') return 'ra-kind--text';
    if (file?.type?.startsWith('image/')) return 'ra-kind--image';
    return 'ra-kind--pdf';
  }

  getQualityLabel(status: string): string {
    const map: Record<string, string> = {
      good: 'Good',
      partial: 'Partial',
      poor: 'Poor',
      needs_reupload: 'Needs Re-upload',
      pending: 'Pending',
    };
    return map[String(status || '').toLowerCase()] || String(status || 'Pending');
  }

  getQualityClass(status: string): string {
    return `ra-quality--${String(status || 'pending').toLowerCase()}`;
  }

  getConfidencePercent(confidence: number | null | undefined): number {
    const value = Number(confidence ?? 0);
    if (value <= 1) {
      return Math.min(100, Math.max(0, Math.round(value * 100)));
    }
    return Math.min(100, Math.max(0, Math.round(value)));
  }

  getMarkerFlagLabel(flag: string | null | undefined): string {
    const normalized = String(flag || '').trim().toLowerCase();
    const labels: Record<string, string> = {
      low: 'Low',
      high: 'High',
      normal: 'Normal',
      ungraded: 'Not graded',
      unknown: 'Not graded',
    };

    return labels[normalized] || 'Not graded';
  }

  getMarkerFlagCssKey(flag: string | null | undefined): string {
    const normalized = String(flag || '').trim().toLowerCase();
    if (normalized === 'unknown') {
      return 'ungraded';
    }

    if (normalized === 'low' || normalized === 'high' || normalized === 'normal' || normalized === 'ungraded') {
      return normalized;
    }

    return 'ungraded';
  }

  getAbnormalMarkerCount(markers: any[] | null | undefined): number {
    return (markers || []).filter((marker) => {
      const flag = String(marker?.flag || '').toLowerCase();
      return flag && flag !== 'normal' && flag !== 'ungraded' && flag !== 'unknown';
    }).length;
  }

  calculateGaugePosition(marker: any): number | null {
    if (marker.reference_low == null || marker.reference_high == null || marker.value_numeric == null) return null;
    const low = Number(marker.reference_low);
    const high = Number(marker.reference_high);
    const val = Number(marker.value_numeric);
    if (isNaN(low) || isNaN(high) || isNaN(val) || low >= high) return null;

    // We map [low, high] to [20%, 80%] of the gauge so outliers can be shown
    const range = high - low;
    const normalized = (val - low) / range;
    const pos = 20 + (normalized * 60);
    // clamp to 0-100%
    return Math.max(0, Math.min(100, pos));
  }

  isMarkerValueInRange(marker: any): boolean {
    if (marker.reference_low == null || marker.reference_high == null || marker.value_numeric == null) return true;
    const low = Number(marker.reference_low);
    const high = Number(marker.reference_high);
    const val = Number(marker.value_numeric);
    if (isNaN(low) || isNaN(high) || isNaN(val)) return true;
    return val >= low && val <= high;
  }

  hasDiffEntries(diff: any, key: 'changed' | 'added' | 'removed' | 'unchanged'): boolean {
    return this.getDiffEntries(diff, key).length > 0;
  }

  getDiffStats(diff: any) {
    const changed = this.getDiffEntries(diff, 'changed').length;
    const added = this.getDiffEntries(diff, 'added').length;
    const removed = this.getDiffEntries(diff, 'removed').length;
    const unchanged = this.getDiffEntries(diff, 'unchanged').length;

    return {
      changed,
      added,
      removed,
      unchanged,
      total: changed + added + removed + unchanged,
    };
  }

  getBaselineReportLabel(reportId: number | null = this.baselineReportId): string {
    if (this.isBaselineAutoMode() || !reportId) {
      return 'Auto-select prior report';
    }

    const option = this.baselineReportCandidates.find((item) => item.value === reportId);
    return option?.name || `Report #${reportId}`;
  }

  getCurrentReportLabel(): string {
    return this.selectedReportFile?.name || 'Current report';
  }

  getDiffDirectionIcon(item: any): string {
    const direction = String(item?.direction || '').toLowerCase();
    if (direction === 'increased') return 'pi pi-arrow-up';
    if (direction === 'decreased') return 'pi pi-arrow-down';
    if (direction === 'unchanged') return 'pi pi-minus';
    return 'pi pi-arrows-h';
  }

  getDiffClinicalTrend(item: any): 'favorable' | 'unfavorable' | 'neutral' {
    const beforeFlag = this.normalizeDiffFlag(item?.before_flag);
    const afterFlag = this.normalizeDiffFlag(item?.after_flag);
    const direction = String(item?.direction || '').toLowerCase();

    if (beforeFlag !== 'normal' && afterFlag === 'normal') {
      return 'favorable';
    }

    if (beforeFlag === 'normal' && afterFlag !== 'normal') {
      return 'unfavorable';
    }

    if (beforeFlag === 'low' && afterFlag === 'high') {
      return 'unfavorable';
    }

    if (beforeFlag === 'high' && afterFlag === 'low') {
      return 'favorable';
    }

    if (afterFlag === 'high') {
      if (direction === 'increased') return 'unfavorable';
      if (direction === 'decreased') return 'favorable';
      return 'unfavorable';
    }

    if (afterFlag === 'low') {
      if (direction === 'decreased') return 'unfavorable';
      if (direction === 'increased') return 'favorable';
      return 'unfavorable';
    }

    if (beforeFlag === 'high' && direction === 'decreased') return 'favorable';
    if (beforeFlag === 'low' && direction === 'increased') return 'favorable';
    if (beforeFlag === 'high' && direction === 'increased') return 'unfavorable';
    if (beforeFlag === 'low' && direction === 'decreased') return 'unfavorable';

    return 'neutral';
  }

  getDiffTrendClass(item: any): string {
    return `ra-diff-trend--${this.getDiffClinicalTrend(item)}`;
  }

  getDiffDeltaClass(item: any): string {
    return `ra-diff-delta--${this.getDiffClinicalTrend(item)}`;
  }

  getDiffTrendLabel(item: any): string | null {
    const trend = this.getDiffClinicalTrend(item);
    const direction = String(item?.direction || '').toLowerCase();

    if (trend === 'favorable') {
      if (direction === 'increased') return 'Better ↑';
      if (direction === 'decreased') return 'Better ↓';
      return 'Improved';
    }

    if (trend === 'unfavorable') {
      if (direction === 'increased') return 'Worse ↑';
      if (direction === 'decreased') return 'Worse ↓';
      return 'Worsened';
    }

    return 'Changed';
  }

  getDiffTrendTooltip(item: any): string {
    const direction = String(item?.direction || 'changed').toLowerCase();
    const beforeFlag = this.normalizeDiffFlag(item?.before_flag);
    const afterFlag = this.normalizeDiffFlag(item?.after_flag);
    const trend = this.getDiffClinicalTrend(item);
    const trendText = trend === 'favorable'
      ? 'Clinically favorable'
      : trend === 'unfavorable'
        ? 'Clinically unfavorable'
        : 'Clinical significance unclear';

    const parts = [
      `${item?.marker_name || 'Marker'}: ${item?.before ?? '—'} → ${item?.after ?? '—'}`,
      `Direction: ${direction}`,
    ];

    if (item?.delta != null) {
      parts.push(`Delta: ${item.delta > 0 ? '+' : ''}${item.delta}${item?.unit ? ` ${item.unit}` : ''}`);
    }

    if (beforeFlag !== 'ungraded' || afterFlag !== 'ungraded') {
      parts.push(`Flags: ${this.getMarkerFlagLabel(beforeFlag)} → ${this.getMarkerFlagLabel(afterFlag)}`);
    }

    parts.push(trendText);
    return parts.join(' · ');
  }

  getAddedMarkerTrendClass(item: any): string {
    const flag = this.normalizeDiffFlag(item?.flag);
    if (flag === 'high' || flag === 'low') {
      return 'ra-diff-trend--unfavorable';
    }

    return 'ra-diff-trend--neutral';
  }

  private normalizeDiffFlag(flag: any): 'low' | 'high' | 'normal' | 'ungraded' {
    const normalized = String(flag || '').trim().toLowerCase();
    if (normalized === 'low' || normalized === 'high' || normalized === 'normal') {
      return normalized;
    }

    return 'ungraded';
  }

  toggleUnchangedDiffMarkers() {
    this.showUnchangedDiffMarkers = !this.showUnchangedDiffMarkers;
  }

  getLikelyResponseClass(value: string): string {
    const normalized = String(value || 'inconclusive').toLowerCase();
    return `ra-response--${normalized}`;
  }

  hasReportInsight(file: any): boolean {
    if (!this.aiEnabled) {
      return false;
    }
    return !!this.getReportInsightTooltip(file);
  }

  getReportInsightTooltip(file: any): string | undefined {
    if (!this.aiEnabled) {
      return undefined;
    }

    const status = String(file?.status || file?.analysis_status || '').toLowerCase();

    if (file?.analysis_insight) {
      return String(file.analysis_insight);
    }

    if (file?.latest_analysis) {
      return this.buildReportInsightFromAnalysis(file.latest_analysis) ?? undefined;
    }

    if (status === 'analyzed' && file?.analysis_summary) {
      return this.truncateReportInsight(file.analysis_summary) || undefined;
    }

    if (status === 'needs_reupload') {
      return 'Low quality — re-upload a clearer scan.';
    }

    if (status === 'uploaded' || status === 'failed') {
      return 'Document saved — AI analysis optional. Open or retry from the gallery.';
    }

    if (status === 'analyzed') {
      return 'Analysis complete.';
    }

    return undefined;
  }

  private buildReportInsightFromAnalysis(analysis: any): string | null {
    if (!analysis) return null;

    const quality = String(analysis.quality_status || '').toLowerCase();
    if (quality === 'needs_reupload' || quality === 'poor') {
      return analysis.summary_text
        ? this.truncateReportInsight(analysis.summary_text, 160)
        : 'Low quality — re-upload recommended.';
    }

    const markers = Array.isArray(analysis.markers) ? analysis.markers : [];
    const abnormal = markers.filter((marker: any) => {
      const flag = String(marker?.flag || '').toLowerCase();
      return flag === 'low' || flag === 'high';
    });

    const lines: string[] = [];
    if (analysis.summary_text) {
      lines.push(this.truncateReportInsight(analysis.summary_text, 110));
    }

    if (abnormal.length > 0) {
      lines.push(`Abnormal: ${this.formatAbnormalInsightPreview(abnormal)}`);
      return lines.filter(Boolean).join('\n');
    }

    if (markers.length > 0) {
      lines.push(this.formatMarkerInsightPreview(markers, 5));
      return lines.filter(Boolean).join('\n');
    }

    if (analysis.summary_text) {
      return this.truncateReportInsight(analysis.summary_text, 160);
    }

    return 'Analysis complete — open for full details.';
  }

  private formatMarkerInsightPreview(markers: any[], limit = 5): string {
    const preview = markers
      .slice(0, limit)
      .map((marker) => this.formatMarkerInsightCompact(marker))
      .filter(Boolean)
      .join(' · ');

    const remaining = Math.max(0, markers.length - limit);
    if (!preview) {
      return 'Markers extracted — open for details.';
    }

    return remaining > 0 ? `${preview} · +${remaining} more` : preview;
  }

  private formatAbnormalInsightPreview(markers: any[]): string {
    const preview = markers
      .slice(0, 3)
      .map((marker) => this.formatMarkerInsightCompact(marker, true))
      .filter(Boolean)
      .join(' · ');

    const remaining = Math.max(0, markers.length - 3);
    if (!preview) {
      return `${markers.length} flagged values`;
    }

    return remaining > 0 ? `${preview} · +${remaining} more` : preview;
  }

  private formatMarkerInsightCompact(marker: any, withFlag = false): string {
    const name = String(marker?.marker_name || marker?.marker_code || '').trim();
    if (!name) return '';

    const rawValue = marker?.value_numeric ?? marker?.value_text;
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return name;
    }

    const valueText = this.formatMarkerInsightValue(rawValue);
    const unit = String(marker?.unit || '').trim();
    let segment = unit ? `${name} ${valueText} ${unit}` : `${name} ${valueText}`;

    if (withFlag && marker?.flag) {
      segment += ` (${String(marker.flag).toLowerCase()})`;
    }

    return segment.trim();
  }

  private formatMarkerInsightValue(value: any): string {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || String(value).trim() === '') {
      return String(value).trim();
    }

    if (Math.abs(numeric - Math.round(numeric)) < 0.00001) {
      return String(Math.round(numeric));
    }

    return String(Number(numeric.toFixed(2)));
  }

  private truncateReportInsight(text: string, maxLength = 140): string {
    const normalized = String(text || '').trim();
    if (!normalized) return '';
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength - 1).trim()}…`;
  }

  private findReportFileById(reportId: number): any | null {
    for (const group of this.uploadsByDate || []) {
      const found = (group?.files || []).find((f: any) => Number(f?.id) === reportId);
      if (found) return found;
    }
    return null;
  }

  private refreshExtractionInOpenDialog(reportId: number, analysis: any) {
    if (!this.reportAnalysisDialogVisible || Number(this.selectedReportFile?.id) !== reportId) {
      return;
    }

    this.reportExtractionAnalysis = analysis;
    this.reportAnalyses = [
      analysis,
      ...(this.reportAnalyses || []).filter((item: any) => item?.analysis_type !== 'extraction'),
    ];
    this.refreshAnalysisDialogCaches();
  }

  private updateReportStatusInGallery(reportId: number, status: string, analysis?: any) {
    for (const group of this.uploadsByDate || []) {
      for (const file of group?.files || []) {
        if (Number(file?.id) === reportId) {
          file.status = status;
          file.analysis_status = status;
          if (analysis) {
            file.latest_analysis = analysis;
            file.analysis_summary = analysis.summary_text;
            file.analysis_insight = this.buildReportInsightFromAnalysis(analysis);
          }
        }
      }
    }
    this.syncReportsViewModel();
  }

  // --- Historical Rx Search Methods ---

  searchHistoricalRx(event: any) {
    const query = (event.query || '').toString().trim().toLowerCase();
    if (!query) {
      this.historicalRxResults = [];
      this.historicalRxSearchLoading = false;
      return;
    }

    this.historicalRxSearchLoading = true;
    this.prescriptionsService.searchPrescriptionTitles(query).subscribe({
      next: (res: any) => {
        if (res.success) {
          const rows: PrescriptionSearchResult[] = Array.isArray(res.data) ? res.data : [];
          this.historicalRxResults = rows.map((row, index) => {
            const rxKey = [
              row?.id ?? '',
              row?.patient_number ?? '',
              row?.date ?? '',
              row?.matched_title ?? '',
              index,
            ].join('|');
            const patientRef = row?.patient_number ? ` (#${row.patient_number})` : '';
            return {
              ...row,
              _rxKey: rxKey,
              _rxLabel: `${row?.matched_title ?? ''} — ${row?.patient_name ?? 'Unknown'}${patientRef}`,
            };
          });
        } else {
          this.historicalRxResults = [];
        }
      },
      error: (err) => {
        console.error('Error searching historical rx', err);
        this.historicalRxResults = [];
      },
      complete: () => {
        this.historicalRxSearchLoading = false;
      }
    });
  }

  getHistoricalRxDuration(rx: PrescriptionSearchResult): string {
    const block = rx?.raw_prescription?.[0];
    return (block?.duration || '').toString().trim();
  }

  getHistoricalRxMedicinePreview(rx: PrescriptionSearchResult): string {
    const block = rx?.raw_prescription?.[0];
    const medicines = Array.isArray(block?.medicines) ? block.medicines : [];
    const names: string[] = [];

    for (const medicine of medicines) {
      const tags = Array.isArray(medicine?.tags) ? medicine.tags : [];
      for (const tag of tags) {
        const name = (tag?.name || '').toString().trim();
        if (name) {
          names.push(name);
        }
      }
      if (names.length >= 3) {
        break;
      }
    }

    if (!names.length) {
      return '';
    }

    const preview = names.slice(0, 3).join(', ');
    const remaining = (rx.medicine_count || names.length) - Math.min(names.length, 3);

    return remaining > 0 ? `${preview} +${remaining} more` : preview;
  }

  onHistoricalRxItemClick(event: MouseEvent, rx: PrescriptionSearchResult): void {
    event.preventDefault();
    event.stopPropagation();
    this.openHistoricalRxPreview(rx);
  }

  onHistoricalRxSelect(event: any): void {
    const raw = event?.value ?? event;
    const selectedItem = this.resolveHistoricalRxSelection(raw);
    if (!selectedItem) {
      return;
    }
    this.openHistoricalRxPreview(selectedItem);
  }

  private resolveHistoricalRxSelection(raw: unknown): PrescriptionSearchResult | null {
    if (!raw) {
      return null;
    }

    if (typeof raw === 'object') {
      const candidate = raw as PrescriptionSearchResult;
      if (candidate._rxKey) {
        const exact = this.historicalRxResults.find((row) => row._rxKey === candidate._rxKey);
        if (exact) {
          return exact;
        }
      }
      if (candidate.id && candidate.patient_number) {
        const byPatient = this.historicalRxResults.find(
          (row) =>
            row.id === candidate.id &&
            row.patient_number === candidate.patient_number &&
            row.matched_title === candidate.matched_title,
        );
        if (byPatient) {
          return byPatient;
        }
      }
      return candidate;
    }

    if (typeof raw === 'string') {
      return (
        this.historicalRxResults.find((row) => row._rxLabel === raw) ??
        this.historicalRxResults.find((row) => row.matched_title === raw) ??
        null
      );
    }

    return null;
  }

  private openHistoricalRxPreview(rx: PrescriptionSearchResult): void {
    this.previewRx = JSON.parse(JSON.stringify(rx));

    if (this.previewRx && typeof this.previewRx.raw_prescription === 'string') {
      try {
        this.previewRx.raw_prescription = JSON.parse(this.previewRx.raw_prescription);
      } catch (e) {
        console.error('Failed to parse raw_prescription', e);
      }
    }

    if (this.previewRx?.raw_prescription && Array.isArray(this.previewRx.raw_prescription)) {
      this.selectedPreviewMedicines = new Array(this.previewRx.raw_prescription.length).fill(true);
    } else {
      this.selectedPreviewMedicines = [];
    }

    this.displayHistoricalRxPreview = true;
    this.selectedHistoricalRx = null;
    this.historicalRxResults = [];
    this.historicalRxAc?.hide(true);
  }

  importAllHistoricalMeds() {
    if (!this.previewRx || !this.previewRx.raw_prescription) return;

    // Check all medicines
    this.selectedPreviewMedicines = new Array(this.previewRx.raw_prescription.length).fill(true);

    this.importSelectedHistoricalMeds();
  }

  importSelectedHistoricalMeds() {
    if (!this.previewRx || !this.previewRx.raw_prescription) return;

    const selectedBlocks = this.previewRx.raw_prescription.filter((_, index) => this.selectedPreviewMedicines[index]);

    if (selectedBlocks.length === 0) {
      this.toast.add({ severity: 'warn', summary: 'No Medicines Selected', detail: 'Please select at least one medicine block to import.' });
      return;
    }

    // Append to existing prescriptions or initialize if empty
    if (!this.prescription.prescriptions || this.prescription.prescriptions.length === 0) {
      this.prescription.prescriptions = [];
    }

    // Add selected blocks
    for (const block of selectedBlocks) {
      // Ensure we deep copy to avoid reference issues
      const clonedBlock = JSON.parse(JSON.stringify(block));
      // Remove IDs from cloned blocks to treat them as new
      delete clonedBlock.id;
      if (clonedBlock.medicines) {
        clonedBlock.medicines.forEach((m: any) => delete m.id);
      }
      this.prescription.prescriptions.push(clonedBlock);
    }

    this.displayHistoricalRxPreview = false;
    this.previewRx = null;

    this.toast.add({ severity: 'success', summary: 'Rx Loaded', detail: 'Prescription added to the current visit.' });
  }

}


